/**
 * i18n augmentation bundle for the v2 persona UI (PersonaCollectionDisplayV2,
 * PersonaAttributeEditorV2): label keys that were never added to the locale
 * translation.json files, plus display translations for the journeyStage,
 * informationSources.category and Schwartz-value enum vocabularies (stored
 * canonically in English slugs, translated at display time).
 *
 * Registered in app/i18n.js via addResourceBundle(deep-merge) — kept out of
 * the locale JSON files, which contain duplicate keys and must never be
 * parsed + re-serialized. Generated 2026-07-16; machine-translated, native
 * review welcome.
 */

const uiLabelsAugment =  {
  "en": {
    "globalSettings": {
      "mcpConnectionsTitle": "Connected assistants",
      "mcpConnectionsEmpty": "No AI assistants are connected to your account yet.",
      "mcpConnected": "Connected",
      "mcpLastActive": "Last active",
      "mcpRevoke": "Revoke",
      "mcpPlanBadge": "Included in your plan (Growth & Enterprise feature).",
      "mcpPlanUpgrade": "The AI-assistant connector is available on Growth and Enterprise plans. Upgrade to enable it for your organization.",
      "mcpTitle": "Connect AI assistants (Claude & ChatGPT)",
      "mcpIntro": "Give Claude or ChatGPT secure, read-only access to your organization's research — Fast Research reports, personas, interviews, and knowledge bases. You sign in with your Yatabase account during setup; access can be revoked any time by removing the connector.",
      "mcpUrlLabel": "Connector URL",
      "mcpCopy": "Copy",
      "mcpCopied": "Copied",
      "mcpClaudeStep1": "In claude.ai, open Settings → Connectors and choose \"Add custom connector\".",
      "mcpClaudeStep2": "Paste the connector URL above and confirm.",
      "mcpClaudeStep3": "Click the connector's sign-in button, log in with your Yatabase account, and press Allow.",
      "mcpChatgptStep1": "In ChatGPT, open Settings → Connectors (requires developer mode or a Business/Enterprise plan).",
      "mcpChatgptStep2": "Add a new connector with the URL above.",
      "mcpChatgptStep3": "Log in with your Yatabase account when prompted and press Allow.",
      "mcpNote": "The connector reads exactly the research your account can see in Yatabase — nothing can be changed or deleted through it. Questions you ask are processed by the AI assistant you connect."
    },
    "autobase": {
      "schedule": {
        "once": "Run Once",
        "dailyAt": "Daily at {{time}}",
        "weeklyAt": "Weekly on {{day}} at {{time}}",
        "monthlyAt": "Monthly on day {{day}} at {{time}}"
      }
    },
    "dashboard": {
      "pageTitle": "Dashboard",
      "subtitle": "These are the problems you are solving"
    },
    "documentAnalyzer": {
      "errors": {
        "downloadFailed": "Failed to get download URL. Please try again.",
        "downloadError": "An error occurred while downloading the document."
      },
      "documents": "Documents",
      "downloadDocument": "Download document",
      "viewSummary": "View summary"
    },
    "documentLibrary": {
      "summary": {
        "knowledgeBases": "Knowledge Bases",
        "archived": "Archived"
      },
      "intelligence": {
        "insights": "Intelligence",
        "dataPoints": "data points analyzed"
      },
      "manageTeams": "Manage Teams"
    },
    "documentSummary": {
      "overview": "Overview",
      "section": "section",
      "sections": "sections",
      "collapseAll": "Collapse all",
      "expandAll": "Expand all"
    },
    "estatJapan": {
      "upgradeHeaderTitle": "Japan Integration"
    },
    "googleAdsLibrary": {
      "noAdvertisers": "No advertisers found",
      "title": "Google Ads Library",
      "upgradeMessage": "Search and analyze Google advertising campaigns. Available on Growth and Enterprise plans.",
      "subtitle": "Search and analyze ads from Google Ads Transparency Center",
      "searchByName": "By Advertiser Name",
      "searchByDomain": "By Domain",
      "domainPlaceholder": "Enter domain (e.g. nike.com)",
      "namePlaceholder": "Enter advertiser name (e.g. Nike)",
      "search": "Search",
      "creditsPerSearch": "credits per search",
      "creditsPerAdsFetch": "credits per ads fetch",
      "advertisersFound": "Advertisers Found",
      "ads": "ads",
      "backToResults": "Back",
      "pushToKB": "Push to KB",
      "allFormats": "All Formats",
      "allPlatforms": "All Platforms",
      "deselectAll": "Deselect All",
      "selectAll": "Select All",
      "loadMore": "Load More",
      "adDetails": "Ad Details",
      "regions": "Regions",
      "variations": "Variations",
      "pushTitle": "Push to Knowledge Base",
      "pushDescription": "Export {{count}} selected ads as a PDF document into your Knowledge Base.",
      "pushButton": "Push to Knowledge Base",
      "selectKB": "Select a Knowledge Base..."
    },
    "integrations": {
      "buttons": {
        "upgrade": "Upgrade Plan"
      },
      "upgrade": {
        "message": "Integration credits are available on Growth plan and above"
      },
      "credits": {
        "insufficient": "Insufficient credits"
      }
    },
    "interviews": {
      "viewer": {
        "marketOpportunities": "Market Opportunities",
        "riskAssessment": "Risk Assessment",
        "yatabaseAnalysis": "Yatabase Analysis",
        "analysisSubtitleWithLens": "Theory-grounded interview synthesis · {{lens}}",
        "analysisSubtitle": "Multi-pass synthesis of interview responses",
        "atAGlance": "At a Glance",
        "complexity": "Complexity",
        "marketSize": "Market Size",
        "competitiveAdvantage": "Competitive Advantage",
        "highRisks": "High Risks",
        "mediumRisks": "Medium Risks",
        "mitigationStrategies": "Mitigation Strategies",
        "questionnaire": "Questionnaire",
        "executiveSummaryTab": "Executive Summary",
        "noExecutiveSummary": "No Executive Summary Available",
        "executiveSummaryNotGenerated": "Executive summary has not been generated yet.",
        "yatabaseAnalysisTab": "Yatabase Analysis",
        "marketOpportunitiesCount": "Market Opportunities",
        "risksIdentifiedCount": "Risks Identified",
        "marketOpportunitiesTooltip": "Identified market opportunities with size estimates and competitive advantages",
        "riskAssessmentTooltip": "Identified business risks and recommended mitigation strategies",
        "theoreticalLens": "Theoretical lens",
        "constructs": "constructs",
        "inheritedFromRq": "Inherited from research question category",
        "viewRqSet": "view set",
        "whatIsFramework": "What is {{name}}?",
        "whyThisFramework": "Why this framework",
        "constructGlossary": "Construct glossary"
      },
      "library": {
        "errorLoadingInterview": "Error loading interview",
        "untitled": "Untitled Interview",
        "archiveInterview": "Archive Interview",
        "tooltip": {
          "keyMessage": "Key Finding"
        },
        "labels": {
          "theoreticalLens": "Theoretical lens"
        }
      },
      "generator": {
        "filterInterviewOnly": "Interview Only",
        "filterAll": "All",
        "noQuestionsMatch": "No questions match the selected filter.",
        "selectResearchQuestion": "Select a research question to use for your interviews"
      }
    },
    "surveyChat": {
      "error": "Sorry, something went wrong. Please try again."
    },
    "modelDash": {
      "archiveKnowledgeBaseQuestion": "Archive Knowledge Base?",
      "archiveKBConfirmText": "Are you sure you want to archive this knowledge base? It cannot be reversed.",
      "archiveKBNote": "It will still count towards the total number of knowledge bases you have created, but it will be removed from the active list.",
      "archiveKnowledgeBaseTooltip": "Archive Knowledge Base",
      "linkCopied": "Share link copied to clipboard"
    },
    "linkedInPage": {
      "messages": {
        "pushError": "Failed to push to Knowledge Base",
        "pushSuccess": "Successfully pushed {{count}} posts to Knowledge Base"
      }
    },
    "metaAdLibrary": {
      "noPages": "No pages found",
      "title": "Meta Ad Library",
      "upgradeMessage": "Search and analyze Facebook & Instagram advertising campaigns. Available on Growth and Enterprise plans.",
      "subtitle": "Search and analyze Facebook & Instagram ad campaigns",
      "searchByPage": "By Page/Brand",
      "searchByKeyword": "By Keyword",
      "keywordPlaceholder": "Search ads by keyword (e.g. running shoes)",
      "pagePlaceholder": "Enter page or brand name (e.g. Nike)",
      "search": "Search",
      "creditsPerSearch": "credits per search",
      "creditsPerAdsFetch": "credits per ads fetch",
      "pagesFound": "Pages Found",
      "ads": "ads",
      "backToResults": "Back",
      "pushToKB": "Push to KB",
      "allMedia": "All Media",
      "activeOnly": "Active Only",
      "inactiveOnly": "Inactive Only",
      "allStatus": "All Status",
      "deselectAll": "Deselect All",
      "selectAll": "Select All",
      "loadMore": "Load More",
      "pushTitle": "Push to Knowledge Base",
      "pushGeneratingDetail": "Formatting {{count}} ads with images. This may take a moment.",
      "pushDescription": "Export {{count}} selected ads as a PDF document into your Knowledge Base.",
      "pushButton": "Push to Knowledge Base",
      "pushGenerating": "Generating PDF document...",
      "selectKB": "Select a Knowledge Base..."
    },
    "noteComPage": {
      "search": {
        "errorEmpty": "Please enter a search keyword"
      },
      "creator": {
        "errorEmpty": "Please enter a creator username"
      },
      "hashtag": {
        "errorEmpty": "Please enter a hashtag"
      }
    },
    "organization": {
      "members": {
        "confirmRemoveTitle": "Remove Member?",
        "remove": "Remove"
      }
    },
    "personas": {
      "wizard": {
        "generating": {
          "segmentsStage1": "Reading your Knowledge Base...",
          "segmentsStage2": "Measuring the customer segments in your data..."
        },
        "framework": {
          "cardTitle": "Theoretical framework",
          "cardHint": "The lens your personas are profiled through — every persona is scored on its constructs.",
          "statusLocked": "Locked",
          "statusInherited": "From your research question set",
          "statusManual": "Your choice",
          "statusAuto": "Auto-picked for your question",
          "anchoredIn": "Anchored in",
          "changeShort": "Change",
          "constructs": "constructs",
          "crowdedWarning": "radar may feel crowded"
        },
        "segments": {
          "title": "Corpus segments",
          "hint": "Find the natural customer segments in your Knowledge Base — one persona per segment, weighted by its measured share of the corpus.",
          "discover": "Discover segments",
          "discovering": "Analyzing corpus…",
          "noCorpus": "Not enough document content in this Knowledge Base to segment reliably.",
          "discoverFailed": "Segment discovery failed. You can still generate personas normally.",
          "useSegments": "Set the count from my data — one persona per discovered segment (recommended)",
          "countLocked": "Set by segments",
          "basis": "Measured from {{docs}} documents ({{chunks}} excerpts)",
          "rediscover": "Re-discover",
          "autoHint": "Your data decides: one persona per customer segment found in the Knowledge Base, weighted by its measured share.",
          "autoChip": "Auto",
          "autoOption": "Auto — one persona per segment in my data (recommended)",
          "manualOption": "Manual — I choose the number",
          "postSaveNote": "After you save, Yatabase automatically calibrates this panel against your Knowledge Base, blind-tests it against real customer statements, and refines its voice until a judge model can no longer tell the difference. Watch it happen live on the collection page."
        },
        "step1": {
          "setAttribute": "Attribute",
          "filterPersonaOnly": "Persona Only",
          "filterAll": "All",
          "noQuestionsMatch": "No questions match the selected filter.",
          "hoverCategoryPrompt": "Hover over a category to see questions",
          "selectResearchQuestion": "Select a research question"
        },
        "messages": {
          "personaGeneratedSuccessfully": "New persona generated successfully!",
          "errorGeneratingPersona": "Error generating persona. Please try again.",
          "derivedEmpty": "Could not generate context & voice attributes. Open the editor and click Regenerate.",
          "waitForExtras": "Wait for AI-derived attributes to finish generating",
          "regenerateDerivedFailed": "Could not regenerate derived attributes",
          "regenerateExtrasFailed": "Could not regenerate goals & pain points"
        },
        "buttons": {
          "generating": "Generating...",
          "addPersona": "Add Persona",
          "addBlank": "Add Blank Persona",
          "aiGenerate": "AI Generate Persona"
        },
        "gen": {
          "substrate": "Substrate",
          "substrateDesc": "Demographics + framework constructs, calibrated",
          "images": "Portraits",
          "imagesDesc": "Headshots matched to each persona's gender, age, and location",
          "extras": "Goals & pain points",
          "extrasDesc": "What each persona wants — and what hurts — anchored to their construct profile",
          "derived": "Context & voice",
          "derivedDesc": "Day-in-the-life, JTBD job, objections, values, voice samples, and journey stage — generated in one coherent pass",
          "composing": "Composing your personas",
          "composingBlurb": "Each step is a separate AI call so every detail traces back to validated theory — not invented in isolation.",
          "complete": "complete",
          "now": "now",
          "footnote": "You can already edit any persona while the rest generates. Save is unlocked once all steps complete.",
          "cardDescPending": "Composing this persona's description from their construct profile…",
          "cardDescEmpty": "No description yet — click Regenerate in the editor to generate one."
        },
        "step3": {
          "composingDescription": "Composing description from construct profile…"
        },
        "navigation": {
          "waitingForFramework": "Waiting for framework…",
          "updatingCollection": "Updating...",
          "savingCollection": "Saving..."
        }
      },
      "library": {
        "badges": {
          "shared": "SHARED"
        },
        "labels": {
          "more": "more",
          "theoreticalLens": "Theoretical lens"
        }
      },
      "health": {
        "savedTitle": "Collection saved",
        "gapsTitle": "One thing worth a look — {{n}} topic(s) your customers discuss have no voice on this panel yet:",
        "allCovered": "All measured topics now have a voice — coverage {{cov}}%.",
        "progress": "{{added}} persona(s) added so far — coverage now {{cov}}%.",
        "done": "Done — view collection"
      },
      "realism": {
        "pending": "Realism check queued — a blind AI judge will test this panel against real customer statements from your Knowledge Base as soon as context & voice finish generating.",
        "tuning": "Verifying realism — a blind AI judge is comparing your personas’ statements with real customer statements from your Knowledge Base, and adjusting their voice if it can tell them apart…",
        "passTitle": "Blind judge couldn’t tell the difference.",
        "added": "Added {{n}} persona(s) to cover topics your customers discuss: {{topics}}.",
        "designNote": "Panel designed to cover all {{total}} topics measured in your data.",
        "designNotePartial": "Panel designed to cover {{assigned}} of {{total}} measured topics.",
        "coverageMet": "Measured topic attention: {{cov}}% — on par with the published benchmark (75%+).",
        "coverageBelow": "Measured topic attention: {{cov}}% — this simulates what the personas would actually emphasize in conversation, reads conservatively, and varies ±10 points between runs.",
        "missingLabel": "No voice yet:",
        "missingNext": "After you save, the collection page offers one-click fixes — add a persona per topic, update the Knowledge Base, or leave it as is.",
        "passBody": "This panel’s statements were statistically indistinguishable from your real customers’ ({{acc}}% judge accuracy, where 50% = pure chance).",
        "adjusted": "Voice auto-adjusted {{n}}× to get there.",
        "twoDraws": "Verified across 2 independent draws ({{n}} statements).",
        "partial": "A blind judge could still tell persona statements apart ({{acc}}% accuracy) after {{n}} voice adjustments. This usually means the Knowledge Base holds mixed content rather than real customer statements — the panel is still grounded in your data.",
        "unavailable": "Realism check unavailable right now — your personas are ready to save regardless.",
        "stale": "The panel changed after verification — the blind-test verdict no longer applies and won’t be saved.",
        "reverify": "Re-verify the panel",
        "chip": "Blind-judge verified",
        "chipTip": "A blind AI judge could not reliably tell this panel’s statements from real customer statements in your Knowledge Base ({{acc}}% judge accuracy over {{n}} statements, where 50% = pure chance)."
      },
      "calibration": {
        "bestFitDesigned": "This panel is a best fit to the customer voice in your Knowledge Base: segments and weights were measured from it, and every topic was assigned by design. The differences listed above reflect the corpus itself, not a build error.",
        "bestFitManual": "The differences listed above are measured against the customer voice in your linked Knowledge Base.",
        "bestFitKbCta": "Adding more real customer voice (reviews, interviews, tickets) to the Knowledge Base is the most reliable way to sharpen future panels.",
        "unchanged": "Nothing changed since the last calibration — showing the standing measurement. Edit personas or update the Knowledge Base to re-measure.",
        "tierGenerated": "Generated",
        "tierGrounded": "Grounded",
        "tierCalibrated": "Calibrated",
        "tierGeneratedTip": "Generated without customer data — link a Knowledge Base to ground it",
        "tierGroundedTip": "Generated using your customer data as context — run a calibration report to measure the match",
        "tierCalibratedTip": "Measured against the real customer-voice corpus in the linked Knowledge Base",
        "panelTitle": "Calibration",
        "coverage": "Topic coverage",
        "sentimentDelta": "Sentiment gap",
        "corpusMeta": "{{chunks}} excerpts from {{docs}} documents",
        "showDetails": "Show details",
        "hideDetails": "Hide details",
        "notYetRun": "Measure this panel against the real customer voice in its Knowledge Base.",
        "run": "Run calibration report",
        "rerun": "Re-run calibration",
        "running": "Measuring…",
        "realSentiment": "Real customers",
        "panelSentiment": "Persona panel",
        "missing": "Missing from panel",
        "underweighted": "Underweighted",
        "overweighted": "Overweighted",
        "noCorpus": "The linked Knowledge Base has too little indexed content to calibrate against.",
        "failed": "Calibration report failed. Please try again.",
        "addGapPersona": "+ Add persona",
        "gapRunning": "Adding…",
        "gapAdded": "Persona \"{{name}}\" added — re-measuring the panel…",
        "history": "Coverage over runs",
        "autoRunning": "Auto-calibrating — measuring this panel against your Knowledge Base…",
        "suggestTitle": "{{n}} topic(s) have no voice on this panel:",
        "suggestAdd": "Add {{n}} persona(s) to cover them",
        "suggestAdding": "Adding & re-measuring…",
        "suggestKb": "Or add documents about these topics to your",
        "suggestKbLink": "Knowledge Base",
        "suggestKbTail": "— the upstream fix that improves every future panel.",
        "suggestDismiss": "Leave as is — that’s fine too",
        "ofAttention": "of customer attention",
        "coverageTip": "Share of the topics real customers discuss (measured in the Knowledge Base) that at least one persona in this panel covers.",
        "sentimentDeltaTip": "Largest difference between how positive/negative real customers are and how positive/negative the persona panel is. Lower is better."
      },
      "validation": {
        "pill": "Blind test",
        "pillTip": "Blinded judge accuracy telling real from synthetic verbatims: {{acc}}% over {{n}} statements (p = {{p}} vs the 50% chance floor). Judge: {{model}} — {{caveat}}.",
        "atChance": "at chance ({{acc}}%)",
        "aboveChance": "distinguishable ({{acc}}%)",
        "holdout": "Holdout coverage",
        "run": "Run blind test",
        "rerun": "Re-run blind test",
        "running": "Testing…",
        "buttonTip": "Blinded test: can a judge model tell your personas’ statements from real customer verbatims?",
        "noCorpus": "Not enough real customer verbatims in the Knowledge Base to run the blind test.",
        "failed": "Validation test failed. Please try again.",
        "detailTitle": "Blind validation",
        "detailAtChance": "A blinded judge model could not reliably tell this panel’s statements from real customer verbatims — {{acc}}% accuracy over {{n}} statements is statistically indistinguishable from the 50% chance floor (p = {{p}}).",
        "detailAboveChance": "A blinded judge model told this panel’s statements apart from real customer verbatims at {{acc}}% accuracy over {{n}} statements (p = {{p}}) — the panel’s voice still differs measurably from real customers.",
        "detailHoldout": "Out-of-sample check: the panel covered {{cov}}% of topics in {{docs}} held-out documents it was never grounded on.",
        "detailCaveat": "Caveat: judge and generator share one model family ({{model}}).",
        "refine": "Refine voice",
        "refining": "Refining…",
        "refineTip": "Revise each persona’s voice toward the real customer register, re-test blind, repeat (max 3 rounds).",
        "refineDone": "Voice refined — the judge can no longer reliably tell the panel from real customers.",
        "refineStopped": "Refined for {{rounds}} rounds — the panel is closer, but still distinguishable. You can run it again.",
        "refineHistory": "Voice refinement:",
        "refineRounds": "{{n}} round(s), fresh verbatim draw each round",
        "autoRunning": "Blind test running…",
        "pooledPill": "Indistinguishability",
        "pooledTip": "Pooled over {{runs}} independent blind tests ({{n}} statements): judge accuracy {{acc}}% vs the 50% chance floor (p = {{p}}). 100 = exactly coin-flip.",
        "pooledDetailTitle": "Pooled score",
        "pooledDetail": "Across {{runs}} independent blind tests ({{n}} statements, each a fresh draw), the judge averaged {{acc}}% — indistinguishability {{score}}/100 (p = {{p}}).",
        "stepSegments": "Segments",
        "stepCalibration": "Calibration",
        "stepBlindTest": "Blind test",
        "stepRefine": "Voice refinement",
        "stepRefined": "Voice refined ({{n}}×)",
        "stepNotNeeded": "Not needed",
        "stepPooled": "Pool-verified",
        "holdoutTip": "Out-of-sample check: topic coverage measured only against held-out documents ({{docs}}) the panel was never grounded on — generalization, not parroting.",
        "pooledTipOne": "Based on a single blind test so far ({{n}} statements): judge accuracy {{acc}}% vs the 50% chance floor (p = {{p}}). 100 = exactly coin-flip. Re-run the blind test to add more independent draws and tighten this score.",
        "pooledDetailOne": "One blind test so far ({{n}} statements): judge accuracy {{acc}}% — indistinguishability {{score}}/100 (p = {{p}}). Re-run the blind test to add independent draws; the score firms up as tests accumulate."
      },
      "optimize": {
        "button": "Auto-improve",
        "buttonTip": "Runs everything for you: measures the panel, fills topic gaps, blind-tests, and refines the voice — up to 3 rounds — aiming for all-green metrics.",
        "running": "Improving… round {{n}}",
        "doneNote": "Auto-improve finished — all metrics reached their targets.",
        "partialNote": "Auto-improve went as far as this corpus allows: {{unmet}} did not reach target. Small or mixed-content Knowledge Bases limit how far these can go — richer real customer verbatims raise the ceiling.",
        "errorNote": "Auto-improve hit an error — you can try again.",
        "startFailed": "Could not start auto-improve. Please try again.",
        "stop": "Stop",
        "stopping": "Stopping…",
        "stoppedNote": "Auto-improve was stopped. Metrics reflect the progress made so far — run it again anytime.",
        "greenTitle": "All checks green — what that means",
        "greenCoverage": "Your personas talk about the same things your real customers talk about — no major topic in your data goes unrepresented.",
        "greenSentiment": "They feel the way your customers feel — the panel’s positive/negative balance matches the real data.",
        "greenBlind": "They sound like your customers — an AI judge shown real and persona statements mixed together could not reliably tell which was which.",
        "greenSummary": "Research you run on this panel reflects your actual customers — their topics, their mood, their voice — not a generic AI guess.",
        "partialTitle": "Auto-improve went as far as this data allows",
        "whyCoverage": "Topic coverage ({{value}}% of {{target}}%): your customers discuss more topics than this panel can speak for. Usually the Knowledge Base content is very varied, or the panel hit its 10-persona limit. Adding more documents on the missing topics (listed under details) — or splitting into two focused panels — raises this.",
        "whySentiment": "Sentiment gap ({{value}}% vs ≤{{target}}%): the panel’s overall mood still differs from your customers’. This usually closes as topic gaps close, since missing topics often carry the missing emotions.",
        "whyBlind": "Blind test ({{value}}/100): a judge can still spot persona statements. The most common cause is a Knowledge Base of mixed content (scraped posts, marketing copy, articles) rather than genuine customer statements — there is no single “customer voice” to imitate. With real reviews, feedback, or transcripts this check typically passes.",
        "partialSummary": "The metrics that ARE green are real, measured results — and running Auto-improve again after enriching the Knowledge Base can lift the rest."
      }
    },
    "common": {
      "unknown": "Unknown",
      "adding": "Adding..."
    },
    "personaChat": {
      "responseStyleNote": "Persona traits always take priority over these settings.",
      "focusGroup": {
        "textRegenFailed": "Attributes saved but text regeneration failed"
      }
    },
    "projects": {
      "teamAssigned": "Team successfully assigned to project",
      "addTeamToProject": "Add Team to Project",
      "totalProjects": "Total projects",
      "deleteProject": "Delete",
      "deleteProjectTitle": "Delete Project?"
    },
    "library": {
      "contextBasedResearch": "Context-based research",
      "researchQuestion": "Research Question",
      "suggestedQuestions": "Research Questions",
      "more": "more",
      "theoreticalLens": "Theoretical lens",
      "archiveFastResearch": "Archive Fast Research",
      "restoreQuickStart": "Restore",
      "status": {
        "processingInterview": "Generating interview responses",
        "extractingThemes": "Analysing interview themes"
      }
    },
    "quickstart": {
      "modal": {
        "step1": {
          "contextRequired": "Please provide at least one context source: a website URL, knowledge base, or additional context.",
          "projectRequired": "Please select a project",
          "researchFirst": "Please complete the web research first by clicking the search button next to the URL.",
          "project": "Project",
          "selectProject": "Select a project",
          "insufficientQuota": "Insufficient Quota",
          "noQuotaRemaining": "You have no synthetic responses remaining. Please upgrade your plan to use Fast Research.",
          "lowQuotaRemaining": "You only have {{count}} synthetic responses remaining. A minimum of 10 is needed for meaningful analysis.",
          "upgradePlan": "Upgrade Plan"
        },
        "step2": {
          "questionRequired": "Please select or enter a research question",
          "contextSummary": "Context Summary",
          "uxAnalysis": "UX & Viewport Analysis",
          "loadingRQSets": "Loading...",
          "noRQSets": "No saved research question sets",
          "questions": "questions"
        }
      },
      "results": {
        "fastResearch": "Fast Research",
        "contextBased": "Context-based research",
        "previewReportDesc": "Summary overview of your research",
        "theoreticalLens": "Theoretical lens — anchors every artifact in this Fast Research",
        "constructs": "constructs",
        "whatIsFramework": "What is {{name}}?",
        "constructGlossary": "Construct glossary"
      },
      "tabs": {
        "convergence": "Convergence"
      },
      "convergence": {
        "confirmed": "Confirmed",
        "nuanced": "Nuanced",
        "ruledOut": "Ruled out",
        "tensionFlagged": "Tension flagged",
        "singleSource": "Single-source",
        "loading": "Loading convergence report…",
        "unavailableTitle": "Convergence report not available for this run",
        "unavailableIntro": "This Fast Research was created before theory grounding was enabled. Its personas and interview were generated without a shared theoretical framework, so there is no construct-level evidence to triangulate.",
        "unavailableAction": "Start a new Fast Research to get the full convergence report — the framework is selected automatically and threads through every artifact it generates.",
        "startNew": "Start a new Fast Research",
        "title": "Methodological Convergence Report",
        "intro": "Methodological triangulation across the three independent generations in this Fast Research — persona profiles, qualitative interviews, and quantitative survey responses — all anchored to the same theoretical framework. Constructs where all methods converge form your highest-confidence findings. Constructs where they diverge are not errors — they're the most interesting findings, surfacing where simulated assumptions, narrative evidence, and measured signal pull in different directions.",
        "framework": "Anchored against",
        "generating": "Generating…",
        "generate": "Generate Convergence Report",
        "regenerating": "Regenerating…",
        "regenerate": "Regenerate",
        "headerConfirmed": "constructs confirmed across all available methods.",
        "synthesis": "Cross-Construct Synthesis",
        "personaSide": "Persona evidence",
        "corePersonas": "personas with core importance",
        "avgImportance": "Average importance",
        "personaNotScored": "These personas were not scored on this construct, so there is no persona-side comparison.",
        "interviewSide": "Interview evidence",
        "evidenceStrength": "Evidence strength",
        "convergentQuote": "Convergent quote",
        "divergentQuote": "Divergent quote",
        "noInterviewData": "Interview analysis has not been completed for this construct.",
        "surveySide": "Survey evidence",
        "meanAgreement": "Mean agreement",
        "highAgreement": "agreed strongly",
        "surveyItem": "item",
        "surveyItems": "items",
        "surveyResponses": "responses",
        "themeValidationTitle": "Emergent Theme Validation",
        "themeValidationIntro": "Interviews surfaced themes outside the framework. The survey then tested whether these themes hold up at scale — the sequential exploratory mixed-methods design in practice.",
        "themeValidated": "VALIDATED",
        "themePartial": "PARTIAL",
        "themeNotValidated": "NOT VALIDATED",
        "themeNoSurvey": "QUALITATIVE ONLY",
        "themeFromInterview": "From interviews",
        "themeFromSurvey": "From survey",
        "footer": "Generated by the Yatabase convergence pipeline using a sequential exploratory mixed-methods design (Creswell & Plano Clark): per-construct evidence aggregation across persona profiles, interviews, and survey responses, anchored to"
      },
      "status": {
        "retry": "Retry"
      }
    },
    "researchQuestions": {
      "errors": {
        "loadFailed": "Failed to load research question set.",
        "saveFailed": "Questions generated but failed to save to library.",
        "generateFailed": "An error occurred. Please try again."
      }
    },
    "rqLibrary": {
      "loadingMore": "Loading...",
      "moreCategories": "more categories"
    },
    "syntheticData": {
      "contextFromSurvey": "Context auto-filled from your YataForm settings",
      "webResearchPlaceholder": "URL or search topic...",
      "webResearchHint": "Optional: Add web context for more realistic responses",
      "additionalContext": "Additional Context (Optional)",
      "additionalContextPlaceholder": "Business context, target audience details, specific instructions for response generation...",
      "additionalContextHint": "Provide context or instructions to guide synthetic response generation"
    },
    "teams": {
      "memberCount_label": "members",
      "projectCount_label": "projects",
      "buttons": {
        "editName": "Edit Name",
        "addMember": "Add Member",
        "deleteTeam": "Delete",
        "removeMember": "Remove",
        "leaveTeam": "Leave"
      },
      "modals": {
        "editName": {
          "title": "Edit Team Name",
          "placeholder": "Team name"
        }
      },
      "confirmations": {
        "removeMemberTitle": "Remove Member?",
        "leaveTeamTitle": "Leave Team?",
        "deleteTeamTitle": "Delete Team?"
      }
    },
    "theoryBridge": {
      "library": {
        "details": {
          "narrative": "Narrative"
        },
        "tooltips": {
          "archiveAnalysis": "Archive",
          "totalAnalyses": "Total number of analyses",
          "totalMappings": "Total theories mapped across all analyses",
          "totalGaps": "Total gaps identified across all analyses"
        },
        "labels": {
          "analyses": "Analyses",
          "mappings": "Mappings",
          "gaps": "Gaps"
        },
        "searchPlaceholder": "Search analyses...",
        "loading": "Loading analyses...",
        "noResults": "No matching analyses",
        "noResultsDesc": "Try a different search term",
        "archiveAnalysis": "Archive Analysis?",
        "confirmArchive": "This will archive the Theory Bridge analysis and remove it from your active list. This cannot be undone.",
        "cancel": "Cancel",
        "yesArchive": "Yes, Archive"
      }
    },
    "urlInput": {
      "step0": {
        "filterYataformOnly": "YataForm Only",
        "filterAll": "All",
        "noQuestionsMatch": "No questions match the selected filters.",
        "hoverCategoryPrompt": "Hover over a category to see questions",
        "selectQuestionPrompt": "Select a research question"
      },
      "wizardTitle": "Create YataForm"
    },
    "contextSources": {
      "preFilledFromSet": "Context auto-filled from your research question set"
    },
    "yataFormLibrary": {
      "status": {
        "active": "Active",
        "closed": "Closed",
        "draft": "Draft"
      },
      "types": {
        "aiYataForm": "AI YataForm",
        "documentAnalysis": "Document Analysis",
        "excelAnalysis": "Excel Analysis",
        "webhookKB": "Webhook KB",
        "customForm": "Custom Form"
      },
      "loading": "Loading YataForms...",
      "title": "YataForm Library",
      "subtitle": "Create and manage AI-powered forms and surveys for data collection and analysis",
      "tooltips": {
        "totalActive": "Total number of active YataForms in your library",
        "unassigned": "YataForms not assigned to any project",
        "open": "YataForms currently accepting responses",
        "closed": "YataForms no longer accepting responses",
        "responses": "YataForms that have collected at least one response",
        "viewDashboard": "View Dashboard",
        "editName": "Edit Name"
      },
      "labels": {
        "active": "Active",
        "unassigned": "Unassigned",
        "inProject": "In Project",
        "open": "Open",
        "closed": "Closed",
        "responses": "Responses",
        "untitled": "Untitled",
        "shared": "Shared"
      },
      "searchPlaceholder": "Search YataForms...",
      "filters": {
        "allProjects": "All Projects",
        "unassigned": "Unassigned"
      },
      "buttons": {
        "newYataForm": "New YataForm",
        "createYataForm": "Create YataForm",
        "saveChanges": "Save Changes",
        "cancel": "Cancel",
        "assigning": "Assigning...",
        "assignToProject": "Assign to Project"
      },
      "menu": {
        "aiYataForm": "AI YataForm",
        "aiYataFormDesc": "Create intelligent forms with AI",
        "curatedTemplates": "Curated Templates (Enterprise)",
        "curatedTemplatesDesc": "Expert-designed templates for enterprise teams"
      },
      "emptyState": {
        "title": "No YataForms found",
        "adjustFilters": "Try adjusting your search or filter criteria",
        "createFirst": "Create your first YataForm to start collecting and analyzing data"
      },
      "modals": {
        "editName": {
          "title": "Edit YataForm Name"
        },
        "projectAssign": {
          "changeTitle": "Change YataForm Project Assignment",
          "assignTitle": "Assign YataForm to Project",
          "yataForm": "YataForm",
          "selectProject": "Select Project",
          "chooseProject": "Choose a project...",
          "currentProject": "Current Project"
        }
      },
      "details": {
        "responses": "responses",
        "created": "Created",
        "accessCode": "Access Code",
        "questions": "Questions"
      },
      "descriptions": {
        "aiGenerated": "AI-generated YataForm with intelligent analysis and insights",
        "documentAnalysis": "Document analysis survey for unstructured data processing",
        "excelAnalysis": "Excel data analysis with AI-powered insights and patterns",
        "customSurvey": "Custom survey awaiting data collection and analysis"
      }
    }
  },
  "ja": {
    "globalSettings": {
      "mcpConnectionsTitle": "接続中のアシスタント",
      "mcpConnectionsEmpty": "アカウントに接続されているAIアシスタントはまだありません。",
      "mcpConnected": "接続済み",
      "mcpLastActive": "最終利用",
      "mcpRevoke": "解除",
      "mcpPlanBadge": "ご利用中のプランに含まれています（Growth・Enterprise向け機能）。",
      "mcpPlanUpgrade": "AIアシスタント連携はGrowthおよびEnterpriseプランでご利用いただけます。組織で有効にするにはアップグレードしてください。",
      "mcpTitle": "AIアシスタント連携（Claude・ChatGPT）",
      "mcpIntro": "ClaudeやChatGPTに、組織のリサーチ（Fast Researchレポート、ペルソナ、インタビュー、ナレッジベース）への安全な読み取り専用アクセスを付与できます。設定時にYatabaseアカウントでログインし、コネクタを削除すればいつでもアクセスを取り消せます。",
      "mcpUrlLabel": "コネクタURL",
      "mcpCopy": "コピー",
      "mcpCopied": "コピーしました",
      "mcpClaudeStep1": "claude.aiで「設定 → コネクタ」を開き、「カスタムコネクタを追加」を選択します。",
      "mcpClaudeStep2": "上記のコネクタURLを貼り付けて確定します。",
      "mcpClaudeStep3": "コネクタのサインインボタンをクリックし、Yatabaseアカウントでログインして「許可」を押します。",
      "mcpChatgptStep1": "ChatGPTで「設定 → コネクタ」を開きます（開発者モードまたはBusiness/Enterpriseプランが必要です）。",
      "mcpChatgptStep2": "上記のURLで新しいコネクタを追加します。",
      "mcpChatgptStep3": "求められたらYatabaseアカウントでログインし、「許可」を押します。",
      "mcpNote": "コネクタが読み取れるのは、お使いのアカウントがYatabaseで閲覧できるリサーチのみです。変更や削除はできません。質問内容は接続先のAIアシスタントによって処理されます。"
    },
    "autobase": {
      "schedule": {
        "once": "一度実行",
        "dailyAt": "毎日 {{time}} に",
        "weeklyAt": "毎週 {{day}} の {{time}} に",
        "monthlyAt": "毎月 {{day}} 日の {{time}} に"
      }
    },
    "dashboard": {
      "pageTitle": "ダッシュボード",
      "subtitle": "あなたが解決している問題"
    },
    "documentAnalyzer": {
      "errors": {
        "downloadFailed": "ダウンロードURLの取得に失敗しました。再試行してください。",
        "downloadError": "ドキュメントのダウンロード中にエラーが発生しました。"
      },
      "documents": "ドキュメント",
      "downloadDocument": "ドキュメントをダウンロード",
      "viewSummary": "概要を表示"
    },
    "documentLibrary": {
      "summary": {
        "knowledgeBases": "ナレッジベース",
        "archived": "アーカイブ済み"
      },
      "intelligence": {
        "insights": "インテリジェンス",
        "dataPoints": "分析されたデータポイント"
      },
      "manageTeams": "チームを管理"
    },
    "documentSummary": {
      "overview": "概要",
      "section": "セクション",
      "sections": "セクション",
      "collapseAll": "すべて折りたたむ",
      "expandAll": "すべて展開する"
    },
    "estatJapan": {
      "upgradeHeaderTitle": "日本統合"
    },
    "googleAdsLibrary": {
      "noAdvertisers": "広告主が見つかりません",
      "title": "Google広告ライブラリ",
      "upgradeMessage": "Google広告キャンペーンを検索および分析します。成長プランおよびエンタープライズプランで利用可能です。",
      "subtitle": "Google広告透明性センターから広告を検索および分析",
      "searchByName": "広告主名で検索",
      "searchByDomain": "ドメインで検索",
      "domainPlaceholder": "ドメインを入力 (例: nike.com)",
      "namePlaceholder": "広告主名を入力 (例: Nike)",
      "search": "検索",
      "creditsPerSearch": "検索あたりのクレジット",
      "creditsPerAdsFetch": "広告取得あたりのクレジット",
      "advertisersFound": "見つかった広告主",
      "ads": "広告",
      "backToResults": "結果に戻る",
      "pushToKB": "ナレッジベースにプッシュ",
      "allFormats": "すべてのフォーマット",
      "allPlatforms": "すべてのプラットフォーム",
      "deselectAll": "すべて選択解除",
      "selectAll": "すべて選択",
      "loadMore": "もっと読み込む",
      "adDetails": "広告の詳細",
      "regions": "地域",
      "variations": "バリエーション",
      "pushTitle": "ナレッジベースにプッシュ",
      "pushDescription": "{{count}} 件の選択した広告をPDFドキュメントとしてナレッジベースにエクスポートします。",
      "pushButton": "ナレッジベースにプッシュ",
      "selectKB": "ナレッジベースを選択..."
    },
    "integrations": {
      "buttons": {
        "upgrade": "プランをアップグレード"
      },
      "upgrade": {
        "message": "統合クレジットは成長プラン以上で利用可能です"
      },
      "credits": {
        "insufficient": "クレジットが不足しています"
      }
    },
    "interviews": {
      "viewer": {
        "marketOpportunities": "市場機会",
        "riskAssessment": "リスク評価",
        "yatabaseAnalysis": "Yatabase分析",
        "analysisSubtitleWithLens": "理論に基づくインタビュー合成 · {{lens}}",
        "analysisSubtitle": "インタビュー応答の多段階合成",
        "atAGlance": "概要",
        "complexity": "複雑さ",
        "marketSize": "市場規模",
        "competitiveAdvantage": "競争優位性",
        "highRisks": "高リスク",
        "mediumRisks": "中リスク",
        "mitigationStrategies": "緩和戦略",
        "questionnaire": "質問票",
        "executiveSummaryTab": "エグゼクティブサマリー",
        "noExecutiveSummary": "エグゼクティブサマリーは利用できません",
        "executiveSummaryNotGenerated": "エグゼクティブサマリーはまだ生成されていません。",
        "yatabaseAnalysisTab": "Yatabase分析",
        "marketOpportunitiesCount": "市場機会",
        "risksIdentifiedCount": "特定されたリスク",
        "marketOpportunitiesTooltip": "特定された市場機会とその規模の推定、競争優位性",
        "riskAssessmentTooltip": "特定されたビジネスリスクと推奨される緩和戦略",
        "theoreticalLens": "理論的レンズ",
        "constructs": "構成要素",
        "inheritedFromRq": "研究質問カテゴリから継承",
        "viewRqSet": "セットを表示",
        "whatIsFramework": "{{name}}とは何ですか？",
        "whyThisFramework": "なぜこのフレームワークなのか",
        "constructGlossary": "構成要素の用語集"
      },
      "library": {
        "errorLoadingInterview": "インタビューの読み込みエラー",
        "untitled": "無題のインタビュー",
        "archiveInterview": "インタビューをアーカイブ",
        "tooltip": {
          "keyMessage": "主要な発見"
        },
        "labels": {
          "theoreticalLens": "理論的レンズ"
        }
      },
      "generator": {
        "filterInterviewOnly": "インタビューのみ",
        "filterAll": "すべて",
        "noQuestionsMatch": "選択したフィルターに一致する質問はありません。",
        "selectResearchQuestion": "インタビューに使用する研究質問を選択してください"
      }
    },
    "surveyChat": {
      "error": "申し訳ありませんが、何かがうまくいきませんでした。再試行してください。"
    },
    "modelDash": {
      "archiveKnowledgeBaseQuestion": "知識ベースをアーカイブしますか？",
      "archiveKBConfirmText": "この知識ベースをアーカイブしてもよろしいですか？元に戻すことはできません。",
      "archiveKBNote": "作成した知識ベースの総数にはカウントされますが、アクティブリストからは削除されます。",
      "archiveKnowledgeBaseTooltip": "ナレッジベースをアーカイブ",
      "linkCopied": "共有リンクがクリップボードにコピーされました"
    },
    "linkedInPage": {
      "messages": {
        "pushError": "知識ベースへのプッシュに失敗しました",
        "pushSuccess": "{{count}}件の投稿を知識ベースに正常にプッシュしました"
      }
    },
    "metaAdLibrary": {
      "noPages": "ページが見つかりません",
      "title": "Meta広告ライブラリ",
      "upgradeMessage": "FacebookおよびInstagramの広告キャンペーンを検索および分析します。成長およびエンタープライズプランで利用可能。",
      "subtitle": "FacebookおよびInstagramの広告キャンペーンを検索および分析",
      "searchByPage": "ページ/ブランドで",
      "searchByKeyword": "キーワードで",
      "keywordPlaceholder": "キーワードで広告を検索（例：ランニングシューズ）",
      "pagePlaceholder": "ページまたはブランド名を入力（例：Nike）",
      "search": "検索",
      "creditsPerSearch": "検索あたりのクレジット",
      "creditsPerAdsFetch": "広告取得あたりのクレジット",
      "pagesFound": "見つかったページ",
      "ads": "広告",
      "backToResults": "結果に戻る",
      "pushToKB": "知識ベースにプッシュ",
      "allMedia": "すべてのメディア",
      "activeOnly": "アクティブのみ",
      "inactiveOnly": "非アクティブのみ",
      "allStatus": "すべてのステータス",
      "deselectAll": "すべて選択解除",
      "selectAll": "すべて選択",
      "loadMore": "さらに読み込む",
      "pushTitle": "知識ベースにプッシュ",
      "pushGeneratingDetail": "{{count}}件の広告を画像付きでフォーマット中です。しばらくお待ちください。",
      "pushDescription": "{{count}}件の選択した広告をPDFドキュメントとして知識ベースにエクスポートします。",
      "pushButton": "ナレッジベースにプッシュ",
      "pushGenerating": "PDFドキュメントを生成中...",
      "selectKB": "ナレッジベースを選択..."
    },
    "noteComPage": {
      "search": {
        "errorEmpty": "検索キーワードを入力してください"
      },
      "creator": {
        "errorEmpty": "クリエイターのユーザー名を入力してください"
      },
      "hashtag": {
        "errorEmpty": "ハッシュタグを入力してください"
      }
    },
    "organization": {
      "members": {
        "confirmRemoveTitle": "メンバーを削除しますか？",
        "remove": "削除"
      }
    },
    "personas": {
      "wizard": {
        "generating": {
          "segmentsStage1": "ナレッジベースを読み込み中...",
          "segmentsStage2": "データ内の顧客セグメントを測定中..."
        },
        "framework": {
          "cardTitle": "理論的フレームワーク",
          "cardHint": "ペルソナを分析するレンズです — すべてのペルソナがこのフレームワークの構成概念でスコアリングされます。",
          "statusLocked": "ロック中",
          "statusInherited": "リサーチクエスチョンセットから継承",
          "statusManual": "手動で選択",
          "statusAuto": "質問に合わせて自動選択",
          "anchoredIn": "基盤論文",
          "changeShort": "変更",
          "constructs": "構成要素",
          "crowdedWarning": "レーダーが混雑しているように感じるかもしれません"
        },
        "segments": {
          "title": "コーパスセグメント",
          "hint": "ナレッジベース内の自然な顧客セグメントを発見します — セグメントごとに1つのペルソナを、コーパスに占める測定シェアで重み付けして生成します。",
          "discover": "セグメントを発見",
          "discovering": "コーパスを分析中…",
          "noCorpus": "このナレッジベースには、信頼できるセグメント化に十分な文書コンテンツがありません。",
          "discoverFailed": "セグメントの発見に失敗しました。通常どおりペルソナを生成できます。",
          "useSegments": "データから数を設定 — 発見されたセグメントごとに1つのペルソナ（推奨）",
          "countLocked": "セグメントにより設定",
          "basis": "{{docs}}件の文書（{{chunks}}件の抜粋）から測定",
          "rediscover": "再発見",
          "autoHint": "データが決めます：ナレッジベースで見つかった顧客セグメントごとに1つのペルソナを、測定されたシェアで重み付けします。",
          "autoChip": "自動",
          "autoOption": "自動 — データ内のセグメントごとに1つのペルソナ（推奨）",
          "manualOption": "手動 — 数を自分で選ぶ",
          "postSaveNote": "保存後、Yatabaseは自動的にこのパネルをナレッジベースでキャリブレーションし、実際の顧客の発言とのブラインドテストを行い、判定モデルが違いを見分けられなくなるまでボイスを調整します。コレクションページでライブで確認できます。"
        },
        "step1": {
          "setAttribute": "属性",
          "filterPersonaOnly": "ペルソナのみ",
          "filterAll": "すべて",
          "noQuestionsMatch": "選択したフィルターに一致する質問はありません。",
          "hoverCategoryPrompt": "カテゴリにカーソルを合わせて質問を表示",
          "selectResearchQuestion": "研究質問を選択"
        },
        "messages": {
          "personaGeneratedSuccessfully": "新しいペルソナが正常に生成されました！",
          "errorGeneratingPersona": "ペルソナの生成中にエラーが発生しました。再試行してください。",
          "derivedEmpty": "コンテキストと声の属性を生成できませんでした。エディタを開いて再生成をクリックしてください。",
          "waitForExtras": "AIによって生成された属性が生成を完了するのを待っています",
          "regenerateDerivedFailed": "派生属性の再生成に失敗しました",
          "regenerateExtrasFailed": "目標と痛点の再生成に失敗しました"
        },
        "buttons": {
          "generating": "生成中...",
          "addPersona": "ペルソナを追加",
          "addBlank": "空のペルソナを追加",
          "aiGenerate": "AIによるペルソナ生成"
        },
        "gen": {
          "substrate": "基盤",
          "substrateDesc": "人口統計 + フレームワーク構成要素、調整済み",
          "images": "ポートレート",
          "imagesDesc": "各ペルソナの性別、年齢、場所に合わせたヘッドショット",
          "extras": "目標と痛点",
          "extrasDesc": "各ペルソナが望むこと — そして何が痛いのか — 彼らの構成プロファイルに基づいて",
          "derived": "コンテキストと声",
          "derivedDesc": "日常生活、JTBDの仕事、反論、価値観、声のサンプル、旅の段階 — 一貫したパスで生成",
          "composing": "ペルソナを構成中",
          "composingBlurb": "各ステップは別々のAIコールであり、すべての詳細は検証された理論に基づいています — 孤立して発明されたものではありません。",
          "complete": "完了",
          "now": "今",
          "footnote": "他の生成が進行中でも、すでにペルソナを編集できます。すべてのステップが完了すると保存が解除されます。",
          "cardDescPending": "彼らの構成プロファイルからこのペルソナの説明を構成中…",
          "cardDescEmpty": "まだ説明はありません — エディタで再生成をクリックして生成してください。"
        },
        "step3": {
          "composingDescription": "構成プロファイルから説明を構成中…"
        },
        "navigation": {
          "waitingForFramework": "フレームワークを待っています…",
          "updatingCollection": "更新中...",
          "savingCollection": "保存中..."
        }
      },
      "library": {
        "badges": {
          "shared": "共有済み"
        },
        "labels": {
          "more": "もっと見る",
          "theoreticalLens": "理論的レンズ"
        }
      },
      "health": {
        "savedTitle": "コレクションを保存しました",
        "gapsTitle": "ひとつ確認を — 顧客が語る{{n}}件のトピックに、まだこのパネルの声がありません：",
        "allCovered": "測定されたすべてのトピックに声が揃いました — カバレッジ{{cov}}%。",
        "progress": "これまでに{{added}}人を追加 — カバレッジは現在{{cov}}%です。",
        "done": "完了 — コレクションを見る"
      },
      "realism": {
        "pending": "リアリズムチェック待機中 — コンテキストとボイスの生成が完了し次第、ブラインドAI判定モデルがこのパネルをナレッジベース内の実際の顧客の発言と照合します。",
        "tuning": "リアリズムを検証中 — ブラインドAI判定モデルがペルソナの発言とナレッジベース内の実際の顧客の発言を比較し、見分けられる場合はボイスを調整しています…",
        "passTitle": "ブラインド判定モデルは違いを見分けられませんでした。",
        "added": "顧客が語るトピックをカバーするため、{{n}}人のペルソナを追加しました：{{topics}}。",
        "designNote": "このパネルは、データで測定された全{{total}}トピックをカバーするよう設計されています。",
        "designNotePartial": "このパネルは、測定された{{total}}トピック中{{assigned}}トピックをカバーするよう設計されています。",
        "coverageMet": "測定されたトピック注目度：{{cov}}% — 公表ベンチマーク（75%以上）と同等です。",
        "coverageBelow": "測定されたトピック注目度：{{cov}}% — これはペルソナが実際の会話で何を強調するかをシミュレートしたもので、控えめに出やすく、実行ごとに±10ポイント変動します。",
        "missingLabel": "まだ声のないトピック：",
        "missingNext": "保存後、コレクションページでワンクリックの対応ができます — トピックごとにペルソナを追加、ナレッジベースを更新、またはそのままにする。",
        "passBody": "このパネルの発言は、実際の顧客の発言と統計的に区別できませんでした（判定精度{{acc}}%、50% = 完全な偶然）。",
        "adjusted": "そのためにボイスを{{n}}回自動調整しました。",
        "twoDraws": "2回の独立した抽出（計{{n}}件の発言）で検証済みです。",
        "partial": "{{n}}回のボイス調整後も、ブラインド判定モデルはペルソナの発言を見分けられました（精度{{acc}}%）。ナレッジベースが実際の顧客の発言ではなく混在コンテンツである場合に起こります — パネルはデータに基づいたままです。",
        "unavailable": "リアリズムチェックは現在利用できません — ペルソナはそのまま保存できます。",
        "stale": "検証後にパネルが変更されました — ブラインドテストの判定は無効になり、保存されません。",
        "reverify": "パネルを再検証",
        "chip": "ブラインド判定済み",
        "chipTip": "ブラインドAI判定モデルは、このパネルの発言とナレッジベース内の実際の顧客の発言を確実に見分けられませんでした（{{n}}件の発言で判定精度{{acc}}%、50% = 完全な偶然）。"
      },
      "calibration": {
        "bestFitDesigned": "このパネルは、ナレッジベース内の顧客の声に最適化されています。セグメントと重みは実データから測定され、すべてのトピックが設計段階で割り当てられています。上記の差異はコーパス自体の特性を反映したものであり、生成の誤りではありません。",
        "bestFitManual": "上記の差異は、リンクされたナレッジベース内の顧客の声と比較して測定されたものです。",
        "bestFitKbCta": "ナレッジベースに実際の顧客の声（レビュー、インタビュー、問い合わせ）を追加することが、今後のパネルの精度を高める最も確実な方法です。",
        "unchanged": "前回のキャリブレーション以降、変更はありません。既存の測定結果を表示しています。再測定するにはペルソナを編集するか、ナレッジベースを更新してください。",
        "tierGenerated": "生成された",
        "tierGrounded": "グラウンデッド",
        "tierCalibrated": "キャリブレーテッド",
        "tierGeneratedTip": "顧客データなしで生成されました — グラウンデッドにするためにナレッジベースをリンクしてください",
        "tierGroundedTip": "顧客データをコンテキストとして使用して生成されました — 一致を測定するためにキャリブレーションレポートを実行してください",
        "tierCalibratedTip": "リンクされたナレッジベースの実際の顧客の声コーパスに対して測定されました",
        "panelTitle": "キャリブレーション",
        "coverage": "トピックカバレッジ",
        "sentimentDelta": "感情ギャップ",
        "corpusMeta": "{{chunks}} の抜粋が {{docs}} のドキュメントから",
        "showDetails": "詳細を表示",
        "hideDetails": "詳細を非表示",
        "notYetRun": "このパネルをナレッジベースの実際の顧客の声に対して測定してください。",
        "run": "キャリブレーションレポートを実行",
        "rerun": "キャリブレーションを再実行",
        "running": "測定中…",
        "realSentiment": "実際の顧客",
        "panelSentiment": "ペルソナパネル",
        "missing": "パネルから欠落しています",
        "underweighted": "過小評価されています",
        "overweighted": "過大評価されています",
        "noCorpus": "リンクされたナレッジベースにはキャリブレーションに対してインデックスされたコンテンツが不足しています。",
        "failed": "キャリブレーションレポートが失敗しました。もう一度お試しください。",
        "addGapPersona": "+ ペルソナを追加",
        "gapRunning": "追加中…",
        "gapAdded": "ペルソナ \"{{name}}\" が追加されました — パネルを再計測しています…",
        "history": "実行におけるカバレッジ",
        "autoRunning": "自動キャリブレーション中 — このパネルをナレッジベースと照合しています…",
        "suggestTitle": "このパネルには{{n}}件のトピックを語るペルソナがいません：",
        "suggestAdd": "カバーするペルソナを{{n}}人追加",
        "suggestAdding": "追加・再測定中…",
        "suggestKb": "または、これらのトピックに関する文書を",
        "suggestKbLink": "ナレッジベース",
        "suggestKbTail": "に追加してください — 今後のすべてのパネルを改善する根本的な対策です。",
        "suggestDismiss": "このままにする — それでも問題ありません",
        "ofAttention": "の顧客注目度",
        "coverageTip": "実際の顧客が語るトピック（ナレッジベースで測定）のうち、パネル内の少なくとも1つのペルソナがカバーしている割合。",
        "sentimentDeltaTip": "実際の顧客のポジティブ／ネガティブ度合いとペルソナパネルのそれとの最大差。低いほど良好です。"
      },
      "validation": {
        "pill": "ブラインドテスト",
        "pillTip": "実際の声と合成の声を判別するブラインド判定精度：{{n}}件の発言で{{acc}}%（50%の偶然水準に対する p = {{p}}）。判定モデル：{{model}} — {{caveat}}。",
        "atChance": "偶然水準（{{acc}}%）",
        "aboveChance": "判別可能（{{acc}}%）",
        "holdout": "ホールドアウトカバレッジ",
        "run": "ブラインドテストを実行",
        "rerun": "ブラインドテストを再実行",
        "running": "テスト中…",
        "buttonTip": "ブラインドテスト：判定モデルはペルソナの発言と実際の顧客の声を見分けられるか？",
        "noCorpus": "ブラインドテストを実行するには、ナレッジベース内の実際の顧客の声が不足しています。",
        "failed": "検証テストに失敗しました。もう一度お試しください。",
        "detailTitle": "ブラインド検証",
        "detailAtChance": "ブラインド判定モデルは、このパネルの発言と実際の顧客の声を確実に見分けることができませんでした — {{n}}件の発言で{{acc}}%の精度は、50%の偶然水準と統計的に区別できません（p = {{p}}）。",
        "detailAboveChance": "ブラインド判定モデルは、このパネルの発言を実際の顧客の声から{{acc}}%の精度で見分けました（{{n}}件、p = {{p}}）— パネルの声は依然として実際の顧客と測定可能な差があります。",
        "detailHoldout": "アウトオブサンプル検証：パネルは、グラウンディングに使用されていない{{docs}}件のホールドアウト文書のトピックの{{cov}}%をカバーしました。",
        "detailCaveat": "注意：判定モデルと生成モデルは同じモデルファミリーです（{{model}}）。",
        "refine": "ボイスを調整",
        "refining": "調整中…",
        "refineTip": "各ペルソナのボイスを実際の顧客の語り口に近づけ、ブラインド再テストを繰り返します（最大3ラウンド）。",
        "refineDone": "ボイスを調整しました — 判定モデルはパネルと実際の顧客を確実に見分けられなくなりました。",
        "refineStopped": "{{rounds}}ラウンド調整しました — 近づきましたが、まだ判別可能です。再実行できます。",
        "refineHistory": "ボイス調整：",
        "refineRounds": "{{n}}ラウンド、各ラウンドで新しい発言を抽出",
        "autoRunning": "ブラインドテスト実行中…",
        "pooledPill": "識別不能スコア",
        "pooledTip": "{{runs}}回の独立したブラインドテスト（{{n}}件の発言）を統合：判定精度{{acc}}%（50%の偶然水準に対する p = {{p}}）。100 = 完全にコイントス。",
        "pooledDetailTitle": "統合スコア",
        "pooledDetail": "{{runs}}回の独立したブラインドテスト（{{n}}件、毎回新しい抽出）で、判定モデルの平均は{{acc}}% — 識別不能スコア{{score}}/100（p = {{p}}）。",
        "stepSegments": "セグメント",
        "stepCalibration": "キャリブレーション",
        "stepBlindTest": "ブラインドテスト",
        "stepRefine": "ボイス調整",
        "stepRefined": "ボイス調整済み（{{n}}回）",
        "stepNotNeeded": "不要",
        "stepPooled": "プール検証",
        "holdoutTip": "アウトオブサンプル検証：パネルのグラウンディングに使われていないホールドアウト文書（{{docs}}件）のみに対するトピックカバレッジ — 暗記ではなく汎化の証拠です。",
        "pooledTipOne": "現時点で1回のブラインドテストのみ（{{n}}件の発言）：判定精度{{acc}}%（50%の偶然水準に対する p = {{p}}）。100 = 完全にコイントス。ブラインドテストを再実行して独立した抽出を増やすと、スコアの精度が上がります。",
        "pooledDetailOne": "現時点で1回のブラインドテスト（{{n}}件）：判定精度{{acc}}% — 識別不能スコア{{score}}/100（p = {{p}}）。再実行で独立した抽出が増えるほどスコアは安定します。"
      },
      "optimize": {
        "button": "自動改善",
        "buttonTip": "すべて自動で実行します：パネルを測定し、トピックの不足を補い、ブラインドテストとボイス調整を最大3ラウンド行い、全指標グリーンを目指します。",
        "running": "改善中… ラウンド{{n}}",
        "doneNote": "自動改善が完了しました — すべての指標が目標に達しました。",
        "partialNote": "自動改善はこのコーパスで可能な限界まで到達しました：{{unmet}}は目標に届きませんでした。小規模または内容が混在したナレッジベースでは上限があります — 実際の顧客の声が豊富なほど上限は上がります。",
        "errorNote": "自動改善でエラーが発生しました — 再試行できます。",
        "startFailed": "自動改善を開始できませんでした。もう一度お試しください。",
        "stop": "停止",
        "stopping": "停止中…",
        "stoppedNote": "自動改善は停止されました。指標はこれまでの進捗を反映しています — いつでも再実行できます。",
        "greenTitle": "すべてグリーン — その意味",
        "greenCoverage": "ペルソナは実際の顧客と同じテーマについて語っています — データ内の主要なトピックがすべて代表されています。",
        "greenSentiment": "顧客と同じ感じ方をしています — パネルのポジティブ／ネガティブのバランスが実データと一致しています。",
        "greenBlind": "顧客と同じ声で語っています — 実際の発言とペルソナの発言を混ぜて提示されたAI判定モデルが、確実に見分けることができませんでした。",
        "greenSummary": "このパネルで行うリサーチは、一般的なAIの推測ではなく、実際の顧客 — そのトピック、感情、声 — を反映します。",
        "partialTitle": "自動改善はこのデータで可能な限界まで到達しました",
        "whyCoverage": "トピックカバレッジ（{{value}}% / 目標{{target}}%）：顧客はこのパネルが代弁できる以上のトピックを語っています。ナレッジベースの内容が多岐にわたるか、パネルが10人の上限に達していることが主な原因です。不足トピック（詳細に一覧）に関する文書を追加するか、パネルを2つに分けると改善します。",
        "whySentiment": "センチメントギャップ（{{value}}% / 目標≤{{target}}%）：パネル全体の感情がまだ顧客と異なります。不足トピックには不足している感情が伴うことが多いため、トピックのギャップが埋まると通常は解消します。",
        "whyBlind": "ブラインドテスト（{{value}}/100）：判定モデルはまだペルソナの発言を見分けられます。最も多い原因は、ナレッジベースが実際の顧客の発言ではなく混在コンテンツ（スクレイピングした投稿、マーケティング文、記事）であることです — 模倣すべき単一の「顧客の声」が存在しません。実際のレビュー、フィードバック、インタビュー記録があれば通常は合格します。",
        "partialSummary": "グリーンになった指標は実測の結果です — ナレッジベースを充実させて再実行すれば、残りも改善できます。"
      }
    },
    "common": {
      "unknown": "不明",
      "adding": "追加中..."
    },
    "personaChat": {
      "responseStyleNote": "ペルソナの特性は常にこれらの設定より優先されます。",
      "focusGroup": {
        "textRegenFailed": "属性は保存されましたが、テキストの再生成に失敗しました"
      }
    },
    "projects": {
      "teamAssigned": "チームがプロジェクトに正常に割り当てられました",
      "addTeamToProject": "プロジェクトにチームを追加",
      "totalProjects": "総プロジェクト数",
      "deleteProject": "削除",
      "deleteProjectTitle": "プロジェクトを削除しますか？"
    },
    "library": {
      "contextBasedResearch": "コンテキストベースの研究",
      "researchQuestion": "研究質問",
      "suggestedQuestions": "研究質問",
      "more": "もっと見る",
      "theoreticalLens": "理論的レンズ",
      "archiveFastResearch": "ファストリサーチをアーカイブ",
      "restoreQuickStart": "復元",
      "status": {
        "processingInterview": "インタビュー応答を生成中",
        "extractingThemes": "インタビューのテーマを分析中"
      }
    },
    "quickstart": {
      "modal": {
        "step1": {
          "contextRequired": "少なくとも1つのコンテキストソースを提供してください：ウェブサイトのURL、ナレッジベース、または追加のコンテキスト。",
          "projectRequired": "プロジェクトを選択してください",
          "researchFirst": "まず、URLの隣にある検索ボタンをクリックしてウェブリサーチを完了してください。",
          "project": "プロジェクト",
          "selectProject": "プロジェクトを選択",
          "insufficientQuota": "クォータ不足",
          "noQuotaRemaining": "合成応答が残っていません。ファストリサーチを使用するにはプランをアップグレードしてください。",
          "lowQuotaRemaining": "残りの合成応答は{{count}}件です。意味のある分析には最低10件が必要です。",
          "upgradePlan": "プランをアップグレード"
        },
        "step2": {
          "questionRequired": "研究質問を選択または入力してください",
          "contextSummary": "コンテキストの概要",
          "uxAnalysis": "UXおよびビューポート分析",
          "loadingRQSets": "読み込み中...",
          "noRQSets": "保存された研究質問セットはありません",
          "questions": "質問"
        }
      },
      "results": {
        "fastResearch": "ファストリサーチ",
        "contextBased": "コンテキストベースのリサーチ",
        "previewReportDesc": "リサーチの概要",
        "theoreticalLens": "理論的レンズ — このファストリサーチのすべてのアーティファクトを支える",
        "constructs": "構成要素",
        "whatIsFramework": "{{name}}とは何ですか？",
        "constructGlossary": "構成要素の用語集"
      },
      "tabs": {
        "convergence": "収束"
      },
      "convergence": {
        "confirmed": "確認済み",
        "nuanced": "微妙な",
        "ruledOut": "除外された",
        "tensionFlagged": "緊張が示された",
        "singleSource": "単一ソース",
        "loading": "収束レポートを読み込み中…",
        "unavailableTitle": "この実行の収束レポートは利用できません",
        "unavailableIntro": "このファストリサーチは理論的基盤が有効化される前に作成されました。そのペルソナとインタビューは共有された理論的枠組みなしで生成されたため、三角測量するための構成レベルの証拠はありません。",
        "unavailableAction": "新しいファストリサーチを開始して完全な収束レポートを取得してください — フレームワークは自動的に選択され、生成されるすべてのアーティファクトにスレッドされます。",
        "startNew": "新しいファストリサーチを開始",
        "title": "方法論的収束レポート",
        "intro": "このファストリサーチにおける三つの独立した生成にわたる方法論的三角測量 — ペルソナプロファイル、質的インタビュー、定量的調査応答 — すべて同じ理論的枠組みに基づいています。すべての方法が収束する構成要素は、最も信頼性の高い発見を形成します。収束しない構成要素はエラーではなく、シミュレーションされた仮定、物語の証拠、測定された信号が異なる方向に引っ張る最も興味深い発見です。",
        "framework": "対比される",
        "generating": "生成中…",
        "generate": "収束レポートを生成",
        "regenerating": "再生成中…",
        "regenerate": "再生成",
        "headerConfirmed": "すべての利用可能な方法で確認された構成要素。",
        "synthesis": "クロス構成要素合成",
        "personaSide": "ペルソナの証拠",
        "corePersonas": "重要なペルソナ",
        "avgImportance": "平均的重要性",
        "personaNotScored": "これらのペルソナはこの構成要素でスコア付けされていないため、ペルソナ側の比較はありません。",
        "interviewSide": "インタビューの証拠",
        "evidenceStrength": "証拠の強さ",
        "convergentQuote": "収束した引用",
        "divergentQuote": "分岐した引用",
        "noInterviewData": "この構成要素のインタビュー分析は完了していません。",
        "surveySide": "調査の証拠",
        "meanAgreement": "平均合意",
        "highAgreement": "強く同意した",
        "surveyItem": "項目",
        "surveyItems": "項目",
        "surveyResponses": "回答",
        "themeValidationTitle": "新たなテーマの検証",
        "themeValidationIntro": "インタビューでフレームワーク外のテーマが浮かび上がりました。その後、調査でこれらのテーマがスケールで成立するかどうかをテストしました — 逐次的探索的混合手法デザインの実践です。",
        "themeValidated": "検証済み",
        "themePartial": "部分的",
        "themeNotValidated": "未検証",
        "themeNoSurvey": "定性的のみ",
        "themeFromInterview": "インタビューから",
        "themeFromSurvey": "調査から",
        "footer": "Yatabaseの収束パイプラインによって生成されました。逐次的探索的混合手法デザイン（Creswell & Plano Clark）を使用し、ペルソナプロファイル、インタビュー、および調査回答に基づく構成ごとの証拠を集約し、"
      },
      "status": {
        "retry": "再試行"
      }
    },
    "researchQuestions": {
      "errors": {
        "loadFailed": "研究質問セットの読み込みに失敗しました。",
        "saveFailed": "質問が生成されましたが、ライブラリに保存できませんでした。",
        "generateFailed": "エラーが発生しました。もう一度お試しください。"
      }
    },
    "rqLibrary": {
      "loadingMore": "読み込み中...",
      "moreCategories": "さらにカテゴリ"
    },
    "syntheticData": {
      "contextFromSurvey": "YataForm設定から自動入力されたコンテキスト",
      "webResearchPlaceholder": "URLまたは検索トピック...",
      "webResearchHint": "オプション: より現実的な回答のためにウェブコンテキストを追加",
      "additionalContext": "追加コンテキスト（オプション）",
      "additionalContextPlaceholder": "ビジネスコンテキスト、ターゲットオーディエンスの詳細、回答生成のための具体的な指示...",
      "additionalContextHint": "合成回答生成を導くためのコンテキストや指示を提供"
    },
    "teams": {
      "memberCount_label": "メンバー",
      "projectCount_label": "プロジェクト",
      "buttons": {
        "editName": "名前を編集",
        "addMember": "メンバーを追加",
        "deleteTeam": "削除",
        "removeMember": "削除",
        "leaveTeam": "離れる"
      },
      "modals": {
        "editName": {
          "title": "チーム名を編集",
          "placeholder": "チーム名"
        }
      },
      "confirmations": {
        "removeMemberTitle": "メンバーを削除しますか？",
        "leaveTeamTitle": "チームを離れますか？",
        "deleteTeamTitle": "チームを削除しますか？"
      }
    },
    "theoryBridge": {
      "library": {
        "details": {
          "narrative": "ナラティブ"
        },
        "tooltips": {
          "archiveAnalysis": "アーカイブ",
          "totalAnalyses": "分析の総数",
          "totalMappings": "すべての分析でマッピングされた理論の総数",
          "totalGaps": "すべての分析で特定されたギャップの総数"
        },
        "labels": {
          "analyses": "分析",
          "mappings": "マッピング",
          "gaps": "ギャップ"
        },
        "searchPlaceholder": "分析を検索...",
        "loading": "分析を読み込み中...",
        "noResults": "一致する分析はありません",
        "noResultsDesc": "別の検索語を試してください",
        "archiveAnalysis": "分析をアーカイブしますか？",
        "confirmArchive": "これにより、Theory Bridgeの分析がアーカイブされ、アクティブリストから削除されます。これは元に戻せません。",
        "cancel": "キャンセル",
        "yesArchive": "はい、アーカイブ"
      }
    },
    "urlInput": {
      "step0": {
        "filterYataformOnly": "YataFormのみ",
        "filterAll": "すべて",
        "noQuestionsMatch": "選択したフィルターに一致する質問はありません。",
        "hoverCategoryPrompt": "カテゴリにカーソルを合わせて質問を表示",
        "selectQuestionPrompt": "研究質問を選択"
      },
      "wizardTitle": "YataFormを作成"
    },
    "contextSources": {
      "preFilledFromSet": "リサーチ質問セットから自動入力されたコンテキスト"
    },
    "yataFormLibrary": {
      "status": {
        "active": "アクティブ",
        "closed": "クローズ",
        "draft": "ドラフト"
      },
      "types": {
        "aiYataForm": "AI YataForm",
        "documentAnalysis": "ドキュメント分析",
        "excelAnalysis": "Excel分析",
        "webhookKB": "Webhook KB",
        "customForm": "カスタムフォーム"
      },
      "loading": "YataFormsを読み込み中...",
      "title": "YataFormライブラリ",
      "subtitle": "データ収集と分析のためのAI駆動のフォームと調査を作成および管理する",
      "tooltips": {
        "totalActive": "ライブラリ内のアクティブなYataFormsの総数",
        "unassigned": "プロジェクトに割り当てられていないYataForms",
        "open": "現在回答を受け付けているYataForms",
        "closed": "もはや回答を受け付けていないYataForms",
        "responses": "少なくとも1つの回答を収集したYataForms",
        "viewDashboard": "ダッシュボードを表示",
        "editName": "名前を編集"
      },
      "labels": {
        "active": "アクティブ",
        "unassigned": "未割り当て",
        "inProject": "プロジェクト内",
        "open": "オープン",
        "closed": "クローズ",
        "responses": "回答",
        "untitled": "無題",
        "shared": "共有"
      },
      "searchPlaceholder": "YataFormsを検索...",
      "filters": {
        "allProjects": "すべてのプロジェクト",
        "unassigned": "未割り当て"
      },
      "buttons": {
        "newYataForm": "新しいYataForm",
        "createYataForm": "YataFormを作成",
        "saveChanges": "変更を保存",
        "cancel": "キャンセル",
        "assigning": "割り当て中...",
        "assignToProject": "プロジェクトに割り当て"
      },
      "menu": {
        "aiYataForm": "AI YataForm",
        "aiYataFormDesc": "AIを使用してインテリジェントなフォームを作成",
        "curatedTemplates": "キュレーションされたテンプレート（エンタープライズ）",
        "curatedTemplatesDesc": "エンタープライズチーム向けの専門家設計のテンプレート"
      },
      "emptyState": {
        "title": "YataFormsが見つかりません",
        "adjustFilters": "検索またはフィルター条件を調整してみてください",
        "createFirst": "最初のYataFormを作成してデータの収集と分析を開始しましょう"
      },
      "modals": {
        "editName": {
          "title": "YataForm名を編集"
        },
        "projectAssign": {
          "changeTitle": "YataFormのプロジェクト割り当てを変更",
          "assignTitle": "YataFormをプロジェクトに割り当て",
          "yataForm": "YataForm",
          "selectProject": "プロジェクトを選択",
          "chooseProject": "プロジェクトを選択...",
          "currentProject": "現在のプロジェクト"
        }
      },
      "details": {
        "responses": "回答",
        "created": "作成日",
        "accessCode": "アクセスコード",
        "questions": "質問"
      },
      "descriptions": {
        "aiGenerated": "インテリジェントな分析と洞察を持つAI生成のYataForm",
        "documentAnalysis": "非構造化データ処理のためのドキュメント分析調査",
        "excelAnalysis": "AI駆動の洞察とパターンを持つExcelデータ分析",
        "customSurvey": "データ収集と分析を待っているカスタム調査"
      }
    }
  },
  "fr": {
    "globalSettings": {
      "mcpConnectionsTitle": "Assistants connectés",
      "mcpConnectionsEmpty": "Aucun assistant IA n'est encore connecté à votre compte.",
      "mcpConnected": "Connecté",
      "mcpLastActive": "Dernière activité",
      "mcpRevoke": "Révoquer",
      "mcpPlanBadge": "Inclus dans votre forfait (fonctionnalité Growth et Enterprise).",
      "mcpPlanUpgrade": "Le connecteur d'assistants IA est disponible avec les forfaits Growth et Enterprise. Passez à un forfait supérieur pour l'activer pour votre organisation.",
      "mcpTitle": "Connecter des assistants IA (Claude et ChatGPT)",
      "mcpIntro": "Donnez à Claude ou ChatGPT un accès sécurisé en lecture seule aux recherches de votre organisation — rapports Fast Research, personas, entretiens et bases de connaissances. Vous vous connectez avec votre compte Yatabase lors de la configuration ; l'accès peut être révoqué à tout moment en supprimant le connecteur.",
      "mcpUrlLabel": "URL du connecteur",
      "mcpCopy": "Copier",
      "mcpCopied": "Copié",
      "mcpClaudeStep1": "Dans claude.ai, ouvrez Paramètres → Connecteurs et choisissez « Ajouter un connecteur personnalisé ».",
      "mcpClaudeStep2": "Collez l'URL du connecteur ci-dessus et confirmez.",
      "mcpClaudeStep3": "Cliquez sur le bouton de connexion du connecteur, connectez-vous avec votre compte Yatabase et appuyez sur « Autoriser ».",
      "mcpChatgptStep1": "Dans ChatGPT, ouvrez Paramètres → Connecteurs (nécessite le mode développeur ou un forfait Business/Enterprise).",
      "mcpChatgptStep2": "Ajoutez un nouveau connecteur avec l'URL ci-dessus.",
      "mcpChatgptStep3": "Connectez-vous avec votre compte Yatabase lorsque vous y êtes invité et appuyez sur « Autoriser ».",
      "mcpNote": "Le connecteur lit exactement les recherches que votre compte peut voir dans Yatabase — rien ne peut être modifié ou supprimé par son intermédiaire. Vos questions sont traitées par l'assistant IA connecté."
    },
    "autobase": {
      "schedule": {
        "once": "Exécuter une fois",
        "dailyAt": "Quotidiennement à {{time}}",
        "weeklyAt": "Hebdomadairement le {{day}} à {{time}}",
        "monthlyAt": "Mensuellement le {{day}} à {{time}}"
      }
    },
    "dashboard": {
      "pageTitle": "Tableau de bord",
      "subtitle": "Voici les problèmes que vous résolvez"
    },
    "documentAnalyzer": {
      "errors": {
        "downloadFailed": "Échec de l'obtention de l'URL de téléchargement. Veuillez réessayer.",
        "downloadError": "Une erreur est survenue lors du téléchargement du document."
      },
      "documents": "Documents",
      "downloadDocument": "Télécharger le document",
      "viewSummary": "Voir le résumé"
    },
    "documentLibrary": {
      "summary": {
        "knowledgeBases": "Bases de connaissances",
        "archived": "Archivé"
      },
      "intelligence": {
        "insights": "Intelligence",
        "dataPoints": "points de données analysés"
      },
      "manageTeams": "Gérer les équipes"
    },
    "documentSummary": {
      "overview": "Aperçu",
      "section": "section",
      "sections": "sections",
      "collapseAll": "Réduire tout",
      "expandAll": "Développer tout"
    },
    "estatJapan": {
      "upgradeHeaderTitle": "Intégration Japon"
    },
    "googleAdsLibrary": {
      "noAdvertisers": "Aucun annonceur trouvé",
      "title": "Bibliothèque des annonces Google",
      "upgradeMessage": "Recherchez et analysez les campagnes publicitaires Google. Disponible sur les plans Growth et Enterprise.",
      "subtitle": "Recherchez et analysez les annonces du Centre de transparence des annonces Google",
      "searchByName": "Par nom d'annonceur",
      "searchByDomain": "Par domaine",
      "domainPlaceholder": "Entrez le domaine (ex. : nike.com)",
      "namePlaceholder": "Entrez le nom de l'annonceur (ex. : Nike)",
      "search": "Rechercher",
      "creditsPerSearch": "crédits par recherche",
      "creditsPerAdsFetch": "crédits par récupération d'annonces",
      "advertisersFound": "Annonceurs trouvés",
      "ads": "annonces",
      "backToResults": "Retour",
      "pushToKB": "Pousser vers la base de connaissances",
      "allFormats": "Tous les formats",
      "allPlatforms": "Toutes les plateformes",
      "deselectAll": "Désélectionner tout",
      "selectAll": "Sélectionner tout",
      "loadMore": "Charger plus",
      "adDetails": "Détails de l'annonce",
      "regions": "Régions",
      "variations": "Variations",
      "pushTitle": "Pousser vers la base de connaissances",
      "pushDescription": "Exporter {{count}} annonces sélectionnées en tant que document PDF dans votre base de connaissances.",
      "pushButton": "Pousser vers la base de connaissances",
      "selectKB": "Sélectionner une base de connaissances..."
    },
    "integrations": {
      "buttons": {
        "upgrade": "Mettre à niveau le plan"
      },
      "upgrade": {
        "message": "Les crédits d'intégration sont disponibles sur le plan Growth et supérieur"
      },
      "credits": {
        "insufficient": "Crédits insuffisants"
      }
    },
    "interviews": {
      "viewer": {
        "marketOpportunities": "Opportunités de marché",
        "riskAssessment": "Évaluation des risques",
        "yatabaseAnalysis": "Analyse Yatabase",
        "analysisSubtitleWithLens": "Synthèse d'entretien basée sur la théorie · {{lens}}",
        "analysisSubtitle": "Synthèse multi-pass des réponses aux entretiens",
        "atAGlance": "En un coup d'œil",
        "complexity": "Complexité",
        "marketSize": "Taille du marché",
        "competitiveAdvantage": "Avantage Concurrentiel",
        "highRisks": "Risques Élevés",
        "mediumRisks": "Risques Moyens",
        "mitigationStrategies": "Stratégies d'Atténuation",
        "questionnaire": "Questionnaire",
        "executiveSummaryTab": "Résumé Exécutif",
        "noExecutiveSummary": "Aucun Résumé Exécutif Disponible",
        "executiveSummaryNotGenerated": "Le résumé exécutif n'a pas encore été généré.",
        "yatabaseAnalysisTab": "Analyse Yatabase",
        "marketOpportunitiesCount": "Opportunités de Marché",
        "risksIdentifiedCount": "Risques Identifiés",
        "marketOpportunitiesTooltip": "Opportunités de marché identifiées avec des estimations de taille et des avantages concurrentiels",
        "riskAssessmentTooltip": "Risques commerciaux identifiés et stratégies d'atténuation recommandées",
        "theoreticalLens": "Cadre Théorique",
        "constructs": "constructs",
        "inheritedFromRq": "Hérité de la catégorie de question de recherche",
        "viewRqSet": "voir l'ensemble",
        "whatIsFramework": "Qu'est-ce que {{name}} ?",
        "whyThisFramework": "Pourquoi ce cadre",
        "constructGlossary": "Glossaire des Constructs"
      },
      "library": {
        "errorLoadingInterview": "Erreur de chargement de l'entretien",
        "untitled": "Entretien Sans Titre",
        "archiveInterview": "Archiver l'Entretien",
        "tooltip": {
          "keyMessage": "Résultat Clé"
        },
        "labels": {
          "theoreticalLens": "Cadre Théorique"
        }
      },
      "generator": {
        "filterInterviewOnly": "Entretien Seulement",
        "filterAll": "Tout",
        "noQuestionsMatch": "Aucune question ne correspond au filtre sélectionné.",
        "selectResearchQuestion": "Sélectionnez une question de recherche à utiliser pour vos entretiens"
      }
    },
    "surveyChat": {
      "error": "Désolé, quelque chose a mal tourné. Veuillez réessayer."
    },
    "modelDash": {
      "archiveKnowledgeBaseQuestion": "Archiver la Base de Connaissances ?",
      "archiveKBConfirmText": "Êtes-vous sûr de vouloir archiver cette base de connaissances ? Cela ne peut pas être inversé.",
      "archiveKBNote": "Cela comptera toujours dans le nombre total de bases de connaissances que vous avez créées, mais elle sera retirée de la liste active.",
      "archiveKnowledgeBaseTooltip": "Archiver la base de connaissances",
      "linkCopied": "Lien partagé copié dans le presse-papiers"
    },
    "linkedInPage": {
      "messages": {
        "pushError": "Échec de l'envoi à la Base de Connaissances",
        "pushSuccess": "Envoi de {{count}} publications à la Base de Connaissances réussi"
      }
    },
    "metaAdLibrary": {
      "noPages": "Aucune page trouvée",
      "title": "Bibliothèque d'Annonces Meta",
      "upgradeMessage": "Recherchez et analysez les campagnes publicitaires sur Facebook et Instagram. Disponible sur les plans Croissance et Entreprise.",
      "subtitle": "Recherchez et analysez les campagnes publicitaires sur Facebook et Instagram",
      "searchByPage": "Par Page/Marque",
      "searchByKeyword": "Par Mot-Clé",
      "keywordPlaceholder": "Rechercher des annonces par mot-clé (ex. : chaussures de course)",
      "pagePlaceholder": "Entrez le nom de la page ou de la marque (ex. : Nike)",
      "search": "Rechercher",
      "creditsPerSearch": "crédits par recherche",
      "creditsPerAdsFetch": "crédits par récupération d'annonces",
      "pagesFound": "Pages Trouvées",
      "ads": "annonces",
      "backToResults": "Retour",
      "pushToKB": "Envoyer à la Base de Connaissances",
      "allMedia": "Tous les Médias",
      "activeOnly": "Actif Seulement",
      "inactiveOnly": "Inactif Seulement",
      "allStatus": "Tous les Statuts",
      "deselectAll": "Désélectionner Tout",
      "selectAll": "Sélectionner Tout",
      "loadMore": "Charger Plus",
      "pushTitle": "Envoyer à la Base de Connaissances",
      "pushGeneratingDetail": "Mise en forme de {{count}} annonces avec images. Cela peut prendre un moment.",
      "pushDescription": "Exporter {{count}} annonces sélectionnées en tant que document PDF dans votre Base de Connaissances.",
      "pushButton": "Ajouter à la base de connaissances",
      "pushGenerating": "Génération du document PDF...",
      "selectKB": "Sélectionner une base de connaissances..."
    },
    "noteComPage": {
      "search": {
        "errorEmpty": "Veuillez entrer un mot-clé de recherche"
      },
      "creator": {
        "errorEmpty": "Veuillez entrer un nom d'utilisateur de créateur"
      },
      "hashtag": {
        "errorEmpty": "Veuillez entrer un hashtag"
      }
    },
    "organization": {
      "members": {
        "confirmRemoveTitle": "Supprimer le membre ?",
        "remove": "Supprimer"
      }
    },
    "personas": {
      "wizard": {
        "generating": {
          "segmentsStage1": "Lecture de votre base de connaissances...",
          "segmentsStage2": "Mesure des segments clients dans vos données..."
        },
        "framework": {
          "cardTitle": "Cadre théorique",
          "cardHint": "La lentille à travers laquelle vos personas sont profilés — chaque persona est noté sur ses construits.",
          "statusLocked": "Verrouillé",
          "statusInherited": "Hérité de votre jeu de questions de recherche",
          "statusManual": "Votre choix",
          "statusAuto": "Choisi automatiquement pour votre question",
          "anchoredIn": "Ancré dans",
          "changeShort": "Changer",
          "constructs": "constructions",
          "crowdedWarning": "le radar peut sembler encombré"
        },
        "segments": {
          "title": "Segments du corpus",
          "hint": "Découvrez les segments clients naturels de votre base de connaissances — un persona par segment, pondéré par sa part mesurée du corpus.",
          "discover": "Découvrir les segments",
          "discovering": "Analyse du corpus…",
          "noCorpus": "Contenu documentaire insuffisant dans cette base de connaissances pour une segmentation fiable.",
          "discoverFailed": "La découverte des segments a échoué. Vous pouvez toujours générer des personas normalement.",
          "useSegments": "Définir le nombre à partir de mes données — un persona par segment découvert (recommandé)",
          "countLocked": "Défini par les segments",
          "basis": "Mesuré à partir de {{docs}} documents ({{chunks}} extraits)",
          "rediscover": "Redécouvrir",
          "autoHint": "Vos données décident : un persona par segment client trouvé dans la base de connaissances, pondéré par sa part mesurée.",
          "autoChip": "Auto",
          "autoOption": "Auto — un persona par segment de mes données (recommandé)",
          "manualOption": "Manuel — je choisis le nombre",
          "postSaveNote": "Après l’enregistrement, Yatabase calibre automatiquement ce panel sur votre base de connaissances, le teste à l’aveugle contre de vrais énoncés clients et affine sa voix jusqu’à ce qu’un modèle juge ne fasse plus la différence. Suivez-le en direct sur la page de la collection."
        },
        "step1": {
          "setAttribute": "Attribut",
          "filterPersonaOnly": "Persona uniquement",
          "filterAll": "Tout",
          "noQuestionsMatch": "Aucune question ne correspond au filtre sélectionné.",
          "hoverCategoryPrompt": "Survolez une catégorie pour voir les questions",
          "selectResearchQuestion": "Sélectionner une question de recherche"
        },
        "messages": {
          "personaGeneratedSuccessfully": "Nouvelle persona générée avec succès !",
          "errorGeneratingPersona": "Erreur lors de la génération de la persona. Veuillez réessayer.",
          "derivedEmpty": "Impossible de générer les attributs de contexte et de voix. Ouvrez l'éditeur et cliquez sur Régénérer.",
          "waitForExtras": "Attendez que les attributs dérivés par l'IA terminent leur génération",
          "regenerateDerivedFailed": "Impossible de régénérer les attributs dérivés",
          "regenerateExtrasFailed": "Impossible de régénérer les objectifs et points de douleur"
        },
        "buttons": {
          "generating": "Génération...",
          "addPersona": "Ajouter une persona",
          "addBlank": "Ajouter une persona vierge",
          "aiGenerate": "Générer une persona avec l'IA"
        },
        "gen": {
          "substrate": "Substrat",
          "substrateDesc": "Démographiques + constructions de cadre, calibrées",
          "images": "Portraits",
          "imagesDesc": "Photos de tête correspondant au genre, à l'âge et à la localisation de chaque persona",
          "extras": "Objectifs et points de douleur",
          "extrasDesc": "Ce que chaque persona veut — et ce qui fait mal — ancré à leur profil de construction",
          "derived": "Contexte et voix",
          "derivedDesc": "Journée type, emploi JTBD, objections, valeurs, échantillons de voix et étape du parcours — générés en un seul passage cohérent",
          "composing": "Composition de vos personas",
          "composingBlurb": "Chaque étape est un appel IA séparé, donc chaque détail se rapporte à une théorie validée — pas inventée en isolation.",
          "complete": "complet",
          "now": "maintenant",
          "footnote": "Vous pouvez déjà éditer n'importe quelle persona pendant que le reste se génère. L'enregistrement est débloqué une fois toutes les étapes complètes.",
          "cardDescPending": "Composition de la description de cette persona à partir de son profil de construction…",
          "cardDescEmpty": "Pas encore de description — cliquez sur Régénérer dans l'éditeur pour en générer une."
        },
        "step3": {
          "composingDescription": "Composition de la description à partir du profil de construction…"
        },
        "navigation": {
          "waitingForFramework": "En attente du cadre…",
          "updatingCollection": "Mise à jour...",
          "savingCollection": "Enregistrement..."
        }
      },
      "library": {
        "badges": {
          "shared": "PARTAGÉ"
        },
        "labels": {
          "more": "plus",
          "theoreticalLens": "Lentille théorique"
        }
      },
      "health": {
        "savedTitle": "Collection enregistrée",
        "gapsTitle": "Un point à vérifier — {{n}} sujet(s) dont parlent vos clients n’ont pas encore de voix dans ce panel :",
        "allCovered": "Tous les sujets mesurés ont désormais une voix — couverture {{cov}} %.",
        "progress": "{{added}} persona(s) ajouté(s) — couverture désormais à {{cov}} %.",
        "done": "Terminé — voir la collection"
      },
      "realism": {
        "pending": "Vérification du réalisme en attente — un juge IA en aveugle testera ce panel contre de vrais énoncés clients de votre base de connaissances dès que le contexte et la voix seront générés.",
        "tuning": "Vérification du réalisme — un juge IA en aveugle compare les énoncés de vos personas à de vrais énoncés clients de votre base de connaissances, et ajuste leur voix s’il parvient à les distinguer…",
        "passTitle": "Le juge en aveugle n’a pas fait la différence.",
        "added": "{{n}} persona(s) ajouté(s) pour couvrir des sujets dont parlent vos clients : {{topics}}.",
        "designNote": "Panel conçu pour couvrir les {{total}} sujets mesurés dans vos données.",
        "designNotePartial": "Panel conçu pour couvrir {{assigned}} des {{total}} sujets mesurés.",
        "coverageMet": "Attention thématique mesurée : {{cov}} % — au niveau du benchmark publié (75 %+).",
        "coverageBelow": "Attention thématique mesurée : {{cov}} % — cette mesure simule ce que les personas mettraient réellement en avant en conversation, tend à sous-estimer, et varie de ±10 points entre les exécutions.",
        "missingLabel": "Sans voix pour l’instant :",
        "missingNext": "Après l’enregistrement, la page de la collection propose des correctifs en un clic — ajouter un persona par sujet, mettre à jour la base de connaissances, ou laisser tel quel.",
        "passBody": "Les énoncés de ce panel étaient statistiquement indiscernables de ceux de vos vrais clients ({{acc}} % de précision du juge, 50 % = pur hasard).",
        "adjusted": "Voix auto-ajustée {{n}}× pour y parvenir.",
        "twoDraws": "Vérifié sur 2 tirages indépendants ({{n}} énoncés).",
        "partial": "Un juge en aveugle distinguait encore les énoncés des personas ({{acc}} % de précision) après {{n}} ajustements de voix. Cela signifie généralement que la base de connaissances contient des contenus hétérogènes plutôt que de vrais énoncés clients — le panel reste fondé sur vos données.",
        "unavailable": "Vérification du réalisme indisponible pour l’instant — vos personas peuvent être enregistrés malgré tout.",
        "stale": "Le panel a changé après la vérification — le verdict du test à l’aveugle ne s’applique plus et ne sera pas enregistré.",
        "reverify": "Re-vérifier le panel",
        "chip": "Vérifié en aveugle",
        "chipTip": "Un juge IA en aveugle n’a pas pu distinguer de manière fiable les énoncés de ce panel des vrais énoncés clients de votre base de connaissances ({{acc}} % de précision sur {{n}} énoncés, 50 % = pur hasard)."
      },
      "calibration": {
        "bestFitDesigned": "Ce panel est le meilleur ajustement possible à la voix client de votre base de connaissances : les segments et les pondérations en sont mesurés, et chaque sujet a été attribué dès la conception. Les écarts listés ci-dessus reflètent le corpus lui-même, pas une erreur de génération.",
        "bestFitManual": "Les écarts listés ci-dessus sont mesurés par rapport à la voix client de votre base de connaissances liée.",
        "bestFitKbCta": "Ajouter davantage de voix client réelle (avis, entretiens, tickets) à la base de connaissances est le moyen le plus fiable d'affiner les prochains panels.",
        "unchanged": "Rien n'a changé depuis le dernier calibrage — la mesure existante est affichée. Modifiez les personas ou mettez à jour la base de connaissances pour re-mesurer.",
        "tierGenerated": "Généré",
        "tierGrounded": "Ancré",
        "tierCalibrated": "Calibré",
        "tierGeneratedTip": "Généré sans données clients — liez une base de connaissances pour l'ancrer",
        "tierGroundedTip": "Généré en utilisant vos données clients comme contexte — exécutez un rapport de calibration pour mesurer la correspondance",
        "tierCalibratedTip": "Mesuré par rapport au véritable corpus de voix client dans la base de connaissances liée",
        "panelTitle": "Calibration",
        "coverage": "Couverture des sujets",
        "sentimentDelta": "Écart de sentiment",
        "corpusMeta": "{{chunks}} extraits de {{docs}} documents",
        "showDetails": "Afficher les détails",
        "hideDetails": "Masquer les détails",
        "notYetRun": "Mesurez ce panneau par rapport à la véritable voix client dans sa base de connaissances.",
        "run": "Exécuter le rapport de calibration",
        "rerun": "Réexécuter la calibration",
        "running": "Mesure en cours…",
        "realSentiment": "Vrais clients",
        "panelSentiment": "Panneau de persona",
        "missing": "Manquant du panneau",
        "underweighted": "Sous-évalué",
        "overweighted": "Surchargé",
        "noCorpus": "La base de connaissances liée a trop peu de contenu indexé pour calibrer.",
        "failed": "Le rapport de calibration a échoué. Veuillez réessayer.",
        "addGapPersona": "+ Ajouter persona",
        "gapRunning": "Ajout en cours…",
        "gapAdded": "Persona \"{{name}}\" ajoutée — nouvelle mesure du panel en cours…",
        "history": "Couverture sur les exécutions",
        "autoRunning": "Calibration automatique — mesure de ce panel par rapport à votre base de connaissances…",
        "suggestTitle": "{{n}} sujet(s) n’ont aucune voix dans ce panel :",
        "suggestAdd": "Ajouter {{n}} persona(s) pour les couvrir",
        "suggestAdding": "Ajout et re-mesure…",
        "suggestKb": "Ou ajoutez des documents sur ces sujets à votre",
        "suggestKbLink": "base de connaissances",
        "suggestKbTail": "— le correctif en amont qui améliore chaque futur panel.",
        "suggestDismiss": "Laisser tel quel — c’est un choix valable",
        "ofAttention": "de l’attention client",
        "coverageTip": "Part des sujets abordés par les vrais clients (mesurés dans la base de connaissances) couverts par au moins un persona du panel.",
        "sentimentDeltaTip": "Écart maximal entre la tonalité positive/négative des vrais clients et celle du panel de personas. Plus c’est bas, mieux c’est."
      },
      "validation": {
        "pill": "Test à l’aveugle",
        "pillTip": "Précision du juge en aveugle pour distinguer les verbatims réels des synthétiques : {{acc}} % sur {{n}} énoncés (p = {{p}} par rapport au seuil de hasard de 50 %). Juge : {{model}} — {{caveat}}.",
        "atChance": "au niveau du hasard ({{acc}} %)",
        "aboveChance": "distinguable ({{acc}} %)",
        "holdout": "Couverture hors échantillon",
        "run": "Lancer le test à l’aveugle",
        "rerun": "Relancer le test à l’aveugle",
        "running": "Test en cours…",
        "buttonTip": "Test à l’aveugle : un modèle juge peut-il distinguer les énoncés de vos personas des verbatims clients réels ?",
        "noCorpus": "Pas assez de verbatims clients réels dans la base de connaissances pour lancer le test à l’aveugle.",
        "failed": "Le test de validation a échoué. Veuillez réessayer.",
        "detailTitle": "Validation à l’aveugle",
        "detailAtChance": "Un modèle juge en aveugle n’a pas pu distinguer de manière fiable les énoncés de ce panel des verbatims clients réels — une précision de {{acc}} % sur {{n}} énoncés est statistiquement indiscernable du seuil de hasard de 50 % (p = {{p}}).",
        "detailAboveChance": "Un modèle juge en aveugle a distingué les énoncés de ce panel des verbatims clients réels avec une précision de {{acc}} % sur {{n}} énoncés (p = {{p}}) — la voix du panel diffère encore de façon mesurable des clients réels.",
        "detailHoldout": "Contrôle hors échantillon : le panel a couvert {{cov}} % des sujets de {{docs}} documents mis de côté sur lesquels il n’a jamais été fondé.",
        "detailCaveat": "Réserve : le juge et le générateur partagent la même famille de modèles ({{model}}).",
        "refine": "Affiner la voix",
        "refining": "Affinage…",
        "refineTip": "Révise la voix de chaque persona vers le registre des vrais clients, re-teste à l’aveugle, répète (max 3 tours).",
        "refineDone": "Voix affinée — le juge ne peut plus distinguer de manière fiable le panel des vrais clients.",
        "refineStopped": "Affiné pendant {{rounds}} tours — le panel se rapproche, mais reste distinguable. Vous pouvez relancer.",
        "refineHistory": "Affinage de la voix :",
        "refineRounds": "{{n}} tour(s), nouveau tirage de verbatims à chaque tour",
        "autoRunning": "Test à l’aveugle en cours…",
        "pooledPill": "Indistinguabilité",
        "pooledTip": "Cumulé sur {{runs}} tests à l’aveugle indépendants ({{n}} énoncés) : précision du juge {{acc}} % contre le seuil de hasard de 50 % (p = {{p}}). 100 = pile ou face exact.",
        "pooledDetailTitle": "Score cumulé",
        "pooledDetail": "Sur {{runs}} tests à l’aveugle indépendants ({{n}} énoncés, tirage neuf à chaque fois), le juge a obtenu en moyenne {{acc}} % — indistinguabilité {{score}}/100 (p = {{p}}).",
        "stepSegments": "Segments",
        "stepCalibration": "Calibration",
        "stepBlindTest": "Test à l’aveugle",
        "stepRefine": "Affinage de la voix",
        "stepRefined": "Voix affinée ({{n}}×)",
        "stepNotNeeded": "Non nécessaire",
        "stepPooled": "Vérifié par cumul",
        "holdoutTip": "Contrôle hors échantillon : couverture des sujets mesurée uniquement sur les documents mis de côté ({{docs}}) sur lesquels le panel n’a jamais été fondé — généralisation, pas de la récitation.",
        "pooledTipOne": "Basé sur un seul test à l’aveugle pour l’instant ({{n}} énoncés) : précision du juge {{acc}} % contre le seuil de hasard de 50 % (p = {{p}}). 100 = pile ou face exact. Relancez le test pour ajouter des tirages indépendants et affiner ce score.",
        "pooledDetailOne": "Un seul test à l’aveugle pour l’instant ({{n}} énoncés) : précision du juge {{acc}} % — indistinguabilité {{score}}/100 (p = {{p}}). Relancez le test pour ajouter des tirages indépendants ; le score se consolide au fil des tests."
      },
      "optimize": {
        "button": "Amélioration auto",
        "buttonTip": "Fait tout pour vous : mesure le panel, comble les sujets manquants, teste à l’aveugle et affine la voix — jusqu’à 3 tours — pour viser des indicateurs tout verts.",
        "running": "Amélioration… tour {{n}}",
        "doneNote": "Amélioration terminée — tous les indicateurs ont atteint leur cible.",
        "partialNote": "L’amélioration est allée aussi loin que ce corpus le permet : {{unmet}} n’a pas atteint la cible. Les bases de connaissances petites ou hétérogènes limitent ces indicateurs — des verbatims clients plus riches relèvent le plafond.",
        "errorNote": "L’amélioration a rencontré une erreur — vous pouvez réessayer.",
        "startFailed": "Impossible de lancer l’amélioration automatique. Veuillez réessayer.",
        "stop": "Arrêter",
        "stopping": "Arrêt…",
        "stoppedNote": "L’amélioration automatique a été arrêtée. Les indicateurs reflètent les progrès réalisés — relancez-la quand vous voulez.",
        "greenTitle": "Tout est vert — ce que cela signifie",
        "greenCoverage": "Vos personas parlent des mêmes sujets que vos vrais clients — aucun sujet majeur de vos données n’est laissé de côté.",
        "greenSentiment": "Ils ressentent ce que ressentent vos clients — l’équilibre positif/négatif du panel correspond aux données réelles.",
        "greenBlind": "Ils parlent comme vos clients — un juge IA, face à un mélange d’énoncés réels et de personas, n’a pas pu les distinguer de manière fiable.",
        "greenSummary": "Les recherches menées sur ce panel reflètent vos clients réels — leurs sujets, leur humeur, leur voix — et non une supposition générique de l’IA.",
        "partialTitle": "L’amélioration automatique est allée aussi loin que ces données le permettent",
        "whyCoverage": "Couverture des sujets ({{value}} % sur {{target}} %) : vos clients abordent plus de sujets que ce panel ne peut en porter. En général, la base de connaissances est très hétérogène, ou le panel a atteint sa limite de 10 personas. Ajouter des documents sur les sujets manquants (listés dans les détails) — ou créer deux panels ciblés — améliore ce score.",
        "whySentiment": "Écart de sentiment ({{value}} % vs ≤{{target}} %) : l’humeur globale du panel diffère encore de celle de vos clients. Cela se résorbe généralement avec les lacunes thématiques, car les sujets manquants portent souvent les émotions manquantes.",
        "whyBlind": "Test à l’aveugle ({{value}}/100) : un juge peut encore repérer les énoncés des personas. La cause la plus fréquente : une base de connaissances au contenu hétérogène (publications récupérées, textes marketing, articles) plutôt que de véritables énoncés clients — il n’y a pas de « voix client » unique à imiter. Avec de vrais avis, retours ou transcriptions, ce test passe généralement.",
        "partialSummary": "Les indicateurs déjà verts sont des résultats réellement mesurés — et relancer l’amélioration après avoir enrichi la base de connaissances peut faire progresser le reste."
      }
    },
    "common": {
      "unknown": "Inconnu",
      "adding": "Ajout en cours..."
    },
    "personaChat": {
      "responseStyleNote": "Les traits de la persona ont toujours la priorité sur ces paramètres.",
      "focusGroup": {
        "textRegenFailed": "Attributs enregistrés mais la régénération du texte a échoué"
      }
    },
    "projects": {
      "teamAssigned": "Équipe assignée avec succès au projet",
      "addTeamToProject": "Ajouter une équipe au projet",
      "totalProjects": "Total des projets",
      "deleteProject": "Supprimer",
      "deleteProjectTitle": "Supprimer le projet ?"
    },
    "library": {
      "contextBasedResearch": "Recherche basée sur le contexte",
      "researchQuestion": "Question de recherche",
      "suggestedQuestions": "Questions de recherche",
      "more": "plus",
      "theoreticalLens": "Cadre théorique",
      "archiveFastResearch": "Archiver la recherche rapide",
      "restoreQuickStart": "Restaurer",
      "status": {
        "processingInterview": "Génération des réponses d'entretien",
        "extractingThemes": "Analyse des thèmes d'entretien"
      }
    },
    "quickstart": {
      "modal": {
        "step1": {
          "contextRequired": "Veuillez fournir au moins une source de contexte : une URL de site web, une base de connaissances ou un contexte supplémentaire.",
          "projectRequired": "Veuillez sélectionner un projet",
          "researchFirst": "Veuillez d'abord compléter la recherche web en cliquant sur le bouton de recherche à côté de l'URL.",
          "project": "Projet",
          "selectProject": "Sélectionner un projet",
          "insufficientQuota": "Quota insuffisant",
          "noQuotaRemaining": "Vous n'avez plus de réponses synthétiques restantes. Veuillez mettre à niveau votre plan pour utiliser la recherche rapide.",
          "lowQuotaRemaining": "Il vous reste seulement {{count}} réponses synthétiques. Un minimum de 10 est nécessaire pour une analyse significative.",
          "upgradePlan": "Mettre à niveau le plan"
        },
        "step2": {
          "questionRequired": "Veuillez sélectionner ou entrer une question de recherche",
          "contextSummary": "Résumé du contexte",
          "uxAnalysis": "Analyse UX et de la fenêtre d'affichage",
          "loadingRQSets": "Chargement...",
          "noRQSets": "Aucun ensemble de questions de recherche enregistré",
          "questions": "questions"
        }
      },
      "results": {
        "fastResearch": "Recherche rapide",
        "contextBased": "Recherche basée sur le contexte",
        "previewReportDesc": "Aperçu résumé de votre recherche",
        "theoreticalLens": "Cadre théorique — ancre chaque artefact dans cette recherche rapide",
        "constructs": "constructs",
        "whatIsFramework": "Qu'est-ce que {{name}} ?",
        "constructGlossary": "Glossaire des constructs"
      },
      "tabs": {
        "convergence": "Convergence"
      },
      "convergence": {
        "confirmed": "Confirmé",
        "nuanced": "Nuancé",
        "ruledOut": "Écarté",
        "tensionFlagged": "Tension signalée",
        "singleSource": "Source unique",
        "loading": "Chargement du rapport de convergence…",
        "unavailableTitle": "Rapport de convergence non disponible pour cette exécution",
        "unavailableIntro": "Cette recherche rapide a été créée avant que le cadre théorique ne soit activé. Ses personas et entretiens ont été générés sans un cadre théorique partagé, donc il n'y a pas de preuves au niveau des constructs à trianguler.",
        "unavailableAction": "Démarrez une nouvelle recherche rapide pour obtenir le rapport de convergence complet — le cadre est sélectionné automatiquement et traverse chaque artefact qu'il génère.",
        "startNew": "Démarrer une nouvelle recherche rapide",
        "title": "Rapport de convergence méthodologique",
        "intro": "Triangulation méthodologique à travers les trois générations indépendantes dans cette recherche rapide — profils de personas, entretiens qualitatifs et réponses d'enquête quantitatives — tous ancrés dans le même cadre théorique. Les constructs où toutes les méthodes convergent forment vos résultats les plus fiables. Les constructs où elles divergent ne sont pas des erreurs — ce sont les résultats les plus intéressants, mettant en lumière où les hypothèses simulées, les preuves narratives et le signal mesuré tirent dans des directions différentes.",
        "framework": "Ancré contre",
        "generating": "Génération…",
        "generate": "Générer le rapport de convergence",
        "regenerating": "Régénération…",
        "regenerate": "Régénérer",
        "headerConfirmed": "constructs confirmés à travers toutes les méthodes disponibles.",
        "synthesis": "Synthèse croisée des constructs",
        "personaSide": "Preuves des personas",
        "corePersonas": "personas d'importance centrale",
        "avgImportance": "Importance moyenne",
        "personaNotScored": "Ces personas n'ont pas été notés sur ce construct, donc il n'y a pas de comparaison du côté des personas.",
        "interviewSide": "Preuves des entretiens",
        "evidenceStrength": "Force de la preuve",
        "convergentQuote": "Citation convergente",
        "divergentQuote": "Citation divergente",
        "noInterviewData": "L'analyse des entretiens n'a pas été complétée pour ce construct.",
        "surveySide": "Preuves de l'enquête",
        "meanAgreement": "Accord moyen",
        "highAgreement": "fortement d'accord",
        "surveyItem": "élément",
        "surveyItems": "éléments",
        "surveyResponses": "réponses",
        "themeValidationTitle": "Validation des thèmes émergents",
        "themeValidationIntro": "Les entretiens ont fait émerger des thèmes en dehors du cadre. L'enquête a ensuite testé si ces thèmes se maintiennent à grande échelle — la conception séquentielle exploratoire des méthodes mixtes en pratique.",
        "themeValidated": "VALIDÉ",
        "themePartial": "PARTIEL",
        "themeNotValidated": "NON VALIDÉ",
        "themeNoSurvey": "QUALITATIF UNIQUEMENT",
        "themeFromInterview": "Des entretiens",
        "themeFromSurvey": "D'une enquête",
        "footer": "Généré par le pipeline de convergence Yatabase utilisant une conception séquentielle exploratoire des méthodes mixtes (Creswell & Plano Clark) : agrégation des preuves par construction à travers les profils de persona, les entretiens et les réponses aux enquêtes, ancrées à"
      },
      "status": {
        "retry": "Réessayer"
      }
    },
    "researchQuestions": {
      "errors": {
        "loadFailed": "Échec du chargement de l'ensemble de questions de recherche.",
        "saveFailed": "Questions générées mais échec de l'enregistrement dans la bibliothèque.",
        "generateFailed": "Une erreur s'est produite. Veuillez réessayer."
      }
    },
    "rqLibrary": {
      "loadingMore": "Chargement...",
      "moreCategories": "plus de catégories"
    },
    "syntheticData": {
      "contextFromSurvey": "Contexte auto-rempli à partir de vos paramètres YataForm",
      "webResearchPlaceholder": "URL ou sujet de recherche...",
      "webResearchHint": "Optionnel : Ajoutez un contexte web pour des réponses plus réalistes",
      "additionalContext": "Contexte supplémentaire (Optionnel)",
      "additionalContextPlaceholder": "Contexte commercial, détails sur le public cible, instructions spécifiques pour la génération de réponses...",
      "additionalContextHint": "Fournissez un contexte ou des instructions pour guider la génération de réponses synthétiques"
    },
    "teams": {
      "memberCount_label": "membres",
      "projectCount_label": "projets",
      "buttons": {
        "editName": "Modifier le nom",
        "addMember": "Ajouter un membre",
        "deleteTeam": "Supprimer",
        "removeMember": "Supprimer",
        "leaveTeam": "Quitter"
      },
      "modals": {
        "editName": {
          "title": "Modifier le nom de l'équipe",
          "placeholder": "Nom de l'équipe"
        }
      },
      "confirmations": {
        "removeMemberTitle": "Supprimer le membre ?",
        "leaveTeamTitle": "Quitter l'équipe ?",
        "deleteTeamTitle": "Supprimer l'équipe ?"
      }
    },
    "theoryBridge": {
      "library": {
        "details": {
          "narrative": "Narratif"
        },
        "tooltips": {
          "archiveAnalysis": "Archiver",
          "totalAnalyses": "Nombre total d'analyses",
          "totalMappings": "Total des théories cartographiées à travers toutes les analyses",
          "totalGaps": "Total des lacunes identifiées à travers toutes les analyses"
        },
        "labels": {
          "analyses": "Analyses",
          "mappings": "Cartographies",
          "gaps": "Lacunes"
        },
        "searchPlaceholder": "Rechercher des analyses...",
        "loading": "Chargement des analyses...",
        "noResults": "Aucune analyse correspondante",
        "noResultsDesc": "Essayez un terme de recherche différent",
        "archiveAnalysis": "Archiver l'analyse ?",
        "confirmArchive": "Cela archivera l'analyse Theory Bridge et l'enlèvera de votre liste active. Cela ne peut pas être annulé.",
        "cancel": "Annuler",
        "yesArchive": "Oui, archiver"
      }
    },
    "urlInput": {
      "step0": {
        "filterYataformOnly": "YataForm uniquement",
        "filterAll": "Tous",
        "noQuestionsMatch": "Aucune question ne correspond aux filtres sélectionnés.",
        "hoverCategoryPrompt": "Survolez une catégorie pour voir les questions",
        "selectQuestionPrompt": "Sélectionnez une question de recherche"
      },
      "wizardTitle": "Créer YataForm"
    },
    "contextSources": {
      "preFilledFromSet": "Contexte pré-rempli à partir de votre ensemble de questions de recherche"
    },
    "yataFormLibrary": {
      "status": {
        "active": "Actif",
        "closed": "Fermé",
        "draft": "Brouillon"
      },
      "types": {
        "aiYataForm": "AI YataForm",
        "documentAnalysis": "Analyse de Document",
        "excelAnalysis": "Analyse Excel",
        "webhookKB": "Webhook KB",
        "customForm": "Formulaire Personnalisé"
      },
      "loading": "Chargement des YataForms...",
      "title": "Bibliothèque YataForm",
      "subtitle": "Créez et gérez des formulaires et enquêtes alimentés par l'IA pour la collecte et l'analyse de données",
      "tooltips": {
        "totalActive": "Nombre total de YataForms actifs dans votre bibliothèque",
        "unassigned": "YataForms non assignés à un projet",
        "open": "YataForms acceptant actuellement des réponses",
        "closed": "YataForms n'acceptant plus de réponses",
        "responses": "YataForms ayant collecté au moins une réponse",
        "viewDashboard": "Voir le Tableau de Bord",
        "editName": "Modifier le Nom"
      },
      "labels": {
        "active": "Actif",
        "unassigned": "Non assigné",
        "inProject": "Dans le Projet",
        "open": "Ouvert",
        "closed": "Fermé",
        "responses": "Réponses",
        "untitled": "Sans Titre",
        "shared": "Partagé"
      },
      "searchPlaceholder": "Rechercher des YataForms...",
      "filters": {
        "allProjects": "Tous les Projets",
        "unassigned": "Non assigné"
      },
      "buttons": {
        "newYataForm": "Nouveau YataForm",
        "createYataForm": "Créer YataForm",
        "saveChanges": "Enregistrer les Modifications",
        "cancel": "Annuler",
        "assigning": "Attribution...",
        "assignToProject": "Attribuer au Projet"
      },
      "menu": {
        "aiYataForm": "AI YataForm",
        "aiYataFormDesc": "Créer des formulaires intelligents avec l'IA",
        "curatedTemplates": "Modèles Curés (Entreprise)",
        "curatedTemplatesDesc": "Modèles conçus par des experts pour les équipes d'entreprise"
      },
      "emptyState": {
        "title": "Aucun YataForm trouvé",
        "adjustFilters": "Essayez d'ajuster votre recherche ou vos critères de filtrage",
        "createFirst": "Créez votre premier YataForm pour commencer à collecter et analyser des données"
      },
      "modals": {
        "editName": {
          "title": "Modifier le Nom du YataForm"
        },
        "projectAssign": {
          "changeTitle": "Changer l'Attribution du Projet YataForm",
          "assignTitle": "Attribuer YataForm au Projet",
          "yataForm": "YataForm",
          "selectProject": "Sélectionner le Projet",
          "chooseProject": "Choisissez un projet...",
          "currentProject": "Projet Actuel"
        }
      },
      "details": {
        "responses": "réponses",
        "created": "Créé",
        "accessCode": "Code d'Accès",
        "questions": "Questions"
      },
      "descriptions": {
        "aiGenerated": "YataForm généré par l'IA avec analyse et insights intelligents",
        "documentAnalysis": "Enquête d'analyse de document pour le traitement de données non structurées",
        "excelAnalysis": "Analyse de données Excel avec insights et modèles alimentés par l'IA",
        "customSurvey": "Enquête personnalisée en attente de collecte et d'analyse de données"
      }
    }
  },
  "de": {
    "globalSettings": {
      "mcpConnectionsTitle": "Verbundene Assistenten",
      "mcpConnectionsEmpty": "Mit Ihrem Konto sind noch keine KI-Assistenten verbunden.",
      "mcpConnected": "Verbunden",
      "mcpLastActive": "Zuletzt aktiv",
      "mcpRevoke": "Widerrufen",
      "mcpPlanBadge": "In Ihrem Plan enthalten (Growth- & Enterprise-Funktion).",
      "mcpPlanUpgrade": "Der KI-Assistenten-Connector ist in den Growth- und Enterprise-Plänen verfügbar. Führen Sie ein Upgrade durch, um ihn für Ihre Organisation zu aktivieren.",
      "mcpTitle": "KI-Assistenten verbinden (Claude & ChatGPT)",
      "mcpIntro": "Geben Sie Claude oder ChatGPT sicheren, schreibgeschützten Zugriff auf die Forschung Ihrer Organisation — Fast-Research-Berichte, Personas, Interviews und Wissensdatenbanken. Bei der Einrichtung melden Sie sich mit Ihrem Yatabase-Konto an; der Zugriff kann jederzeit durch Entfernen des Connectors widerrufen werden.",
      "mcpUrlLabel": "Connector-URL",
      "mcpCopy": "Kopieren",
      "mcpCopied": "Kopiert",
      "mcpClaudeStep1": "Öffnen Sie in claude.ai Einstellungen → Connectors und wählen Sie „Benutzerdefinierten Connector hinzufügen\".",
      "mcpClaudeStep2": "Fügen Sie die obige Connector-URL ein und bestätigen Sie.",
      "mcpClaudeStep3": "Klicken Sie auf die Anmelde-Schaltfläche des Connectors, melden Sie sich mit Ihrem Yatabase-Konto an und drücken Sie „Erlauben\".",
      "mcpChatgptStep1": "Öffnen Sie in ChatGPT Einstellungen → Connectors (erfordert den Entwicklermodus oder einen Business-/Enterprise-Plan).",
      "mcpChatgptStep2": "Fügen Sie einen neuen Connector mit der obigen URL hinzu.",
      "mcpChatgptStep3": "Melden Sie sich bei Aufforderung mit Ihrem Yatabase-Konto an und drücken Sie „Erlauben\".",
      "mcpNote": "Der Connector liest genau die Forschung, die Ihr Konto in Yatabase sehen kann — nichts kann darüber geändert oder gelöscht werden. Ihre Fragen werden vom verbundenen KI-Assistenten verarbeitet."
    },
    "autobase": {
      "schedule": {
        "once": "Einmal ausführen",
        "dailyAt": "Täglich um {{time}}",
        "weeklyAt": "Wöchentlich am {{day}} um {{time}}",
        "monthlyAt": "Monatlich am {{day}} um {{time}}"
      }
    },
    "dashboard": {
      "pageTitle": "Dashboard",
      "subtitle": "Das sind die Probleme, die Sie lösen"
    },
    "documentAnalyzer": {
      "errors": {
        "downloadFailed": "Fehler beim Abrufen der Download-URL. Bitte versuchen Sie es erneut.",
        "downloadError": "Beim Herunterladen des Dokuments ist ein Fehler aufgetreten."
      },
      "documents": "Dokumente",
      "downloadDocument": "Dokument herunterladen",
      "viewSummary": "Zusammenfassung anzeigen"
    },
    "documentLibrary": {
      "summary": {
        "knowledgeBases": "Wissensdatenbanken",
        "archived": "Archiviert"
      },
      "intelligence": {
        "insights": "Intelligenz",
        "dataPoints": "analysierte Datenpunkte"
      },
      "manageTeams": "Teams verwalten"
    },
    "documentSummary": {
      "overview": "Überblick",
      "section": "Abschnitt",
      "sections": "Abschnitte",
      "collapseAll": "Alle einklappen",
      "expandAll": "Alle aufklappen"
    },
    "estatJapan": {
      "upgradeHeaderTitle": "Japan-Integration"
    },
    "googleAdsLibrary": {
      "noAdvertisers": "Keine Werbetreibenden gefunden",
      "title": "Google Ads-Bibliothek",
      "upgradeMessage": "Durchsuchen und analysieren Sie Google-Werbekampagnen. Verfügbar in den Wachstums- und Unternehmensplänen.",
      "subtitle": "Durchsuchen und analysieren Sie Anzeigen aus dem Google Ads Transparenzzentrum",
      "searchByName": "Nach Werbetreibendem",
      "searchByDomain": "Nach Domain",
      "domainPlaceholder": "Domain eingeben (z.B. nike.com)",
      "namePlaceholder": "Werbetreibendenamen eingeben (z.B. Nike)",
      "search": "Suche",
      "creditsPerSearch": "Credits pro Suche",
      "creditsPerAdsFetch": "Credits pro Anzeigenabruf",
      "advertisersFound": "Gefundene Werbetreibende",
      "ads": "Anzeigen",
      "backToResults": "Zurück",
      "pushToKB": "In KB übertragen",
      "allFormats": "Alle Formate",
      "allPlatforms": "Alle Plattformen",
      "deselectAll": "Alle abwählen",
      "selectAll": "Alle auswählen",
      "loadMore": "Mehr laden",
      "adDetails": "Anzeigen-Details",
      "regions": "Regionen",
      "variations": "Variationen",
      "pushTitle": "In Wissensdatenbank übertragen",
      "pushDescription": "Exportieren Sie {{count}} ausgewählte Anzeigen als PDF-Dokument in Ihre Wissensdatenbank.",
      "pushButton": "In Wissensdatenbank übertragen",
      "selectKB": "Wählen Sie eine Wissensdatenbank..."
    },
    "integrations": {
      "buttons": {
        "upgrade": "Plan upgraden"
      },
      "upgrade": {
        "message": "Integrationscredits sind im Wachstumsplan und höher verfügbar"
      },
      "credits": {
        "insufficient": "Unzureichende Credits"
      }
    },
    "interviews": {
      "viewer": {
        "marketOpportunities": "Marktchancen",
        "riskAssessment": "Risikobewertung",
        "yatabaseAnalysis": "Yatabase-Analyse",
        "analysisSubtitleWithLens": "Theoriegestützte Synthese von Interviews · {{lens}}",
        "analysisSubtitle": "Mehrfachsynthese der Interviewantworten",
        "atAGlance": "Auf einen Blick",
        "complexity": "Komplexität",
        "marketSize": "Marktgröße",
        "competitiveAdvantage": "Wettbewerbsvorteil",
        "highRisks": "Hohe Risiken",
        "mediumRisks": "Mittlere Risiken",
        "mitigationStrategies": "Minderungsstrategien",
        "questionnaire": "Fragebogen",
        "executiveSummaryTab": "Zusammenfassung",
        "noExecutiveSummary": "Keine Zusammenfassung verfügbar",
        "executiveSummaryNotGenerated": "Zusammenfassung wurde noch nicht erstellt.",
        "yatabaseAnalysisTab": "Yatabase-Analyse",
        "marketOpportunitiesCount": "Marktchancen",
        "risksIdentifiedCount": "Identifizierte Risiken",
        "marketOpportunitiesTooltip": "Identifizierte Marktchancen mit Größenabschätzungen und Wettbewerbsvorteilen",
        "riskAssessmentTooltip": "Identifizierte Geschäftsrisiken und empfohlene Minderungsstrategien",
        "theoreticalLens": "Theoretischer Rahmen",
        "constructs": "Konstrukte",
        "inheritedFromRq": "Von der Forschungsfrage-Kategorie übernommen",
        "viewRqSet": "Set anzeigen",
        "whatIsFramework": "Was ist {{name}}?",
        "whyThisFramework": "Warum dieser Rahmen",
        "constructGlossary": "Konstrukt-Glossar"
      },
      "library": {
        "errorLoadingInterview": "Fehler beim Laden des Interviews",
        "untitled": "Unbetiteltes Interview",
        "archiveInterview": "Interview archivieren",
        "tooltip": {
          "keyMessage": "Wichtiges Ergebnis"
        },
        "labels": {
          "theoreticalLens": "Theoretischer Rahmen"
        }
      },
      "generator": {
        "filterInterviewOnly": "Nur Interview",
        "filterAll": "Alle",
        "noQuestionsMatch": "Keine Fragen entsprechen dem ausgewählten Filter.",
        "selectResearchQuestion": "Wählen Sie eine Forschungsfrage für Ihre Interviews aus"
      }
    },
    "surveyChat": {
      "error": "Entschuldigung, etwas ist schiefgelaufen. Bitte versuchen Sie es erneut."
    },
    "modelDash": {
      "archiveKnowledgeBaseQuestion": "Wissenbasis archivieren?",
      "archiveKBConfirmText": "Sind Sie sicher, dass Sie diese Wissenbasis archivieren möchten? Dies kann nicht rückgängig gemacht werden.",
      "archiveKBNote": "Es wird weiterhin zur Gesamtzahl der von Ihnen erstellten Wissenbasen zählen, aber es wird von der aktiven Liste entfernt.",
      "archiveKnowledgeBaseTooltip": "Wissensdatenbank archivieren",
      "linkCopied": "Freigabelink in die Zwischenablage kopiert"
    },
    "linkedInPage": {
      "messages": {
        "pushError": "Fehler beim Übertragen in die Wissenbasis",
        "pushSuccess": "Erfolgreich {{count}} Beiträge in die Wissenbasis übertragen"
      }
    },
    "metaAdLibrary": {
      "noPages": "Keine Seiten gefunden",
      "title": "Meta Ad Bibliothek",
      "upgradeMessage": "Durchsuchen und analysieren Sie Facebook- und Instagram-Werbekampagnen. Verfügbar in den Wachstums- und Unternehmensplänen.",
      "subtitle": "Durchsuchen und analysieren Sie Facebook- und Instagram-Werbekampagnen",
      "searchByPage": "Nach Seite/Marke",
      "searchByKeyword": "Nach Schlüsselwort",
      "keywordPlaceholder": "Werbung nach Schlüsselwort durchsuchen (z. B. Laufschuhe)",
      "pagePlaceholder": "Seiten- oder Markenname eingeben (z. B. Nike)",
      "search": "Suchen",
      "creditsPerSearch": "Credits pro Suche",
      "creditsPerAdsFetch": "Credits pro Anzeigenabruf",
      "pagesFound": "Gefundene Seiten",
      "ads": "Anzeigen",
      "backToResults": "Zurück",
      "pushToKB": "In Wissenbasis übertragen",
      "allMedia": "Alle Medien",
      "activeOnly": "Nur aktiv",
      "inactiveOnly": "Nur inaktiv",
      "allStatus": "Alle Status",
      "deselectAll": "Alle abwählen",
      "selectAll": "Alle auswählen",
      "loadMore": "Mehr laden",
      "pushTitle": "In Wissenbasis übertragen",
      "pushGeneratingDetail": "Formatiere {{count}} Anzeigen mit Bildern. Dies kann einen Moment dauern.",
      "pushDescription": "{{count}} ausgewählte Anzeigen als PDF-Dokument in Ihre Wissenbasis exportieren.",
      "pushButton": "In Wissensdatenbank übertragen",
      "pushGenerating": "PDF-Dokument wird erstellt...",
      "selectKB": "Wählen Sie eine Wissensdatenbank..."
    },
    "noteComPage": {
      "search": {
        "errorEmpty": "Bitte geben Sie ein Suchwort ein"
      },
      "creator": {
        "errorEmpty": "Bitte geben Sie einen Benutzernamen des Erstellers ein"
      },
      "hashtag": {
        "errorEmpty": "Bitte geben Sie einen Hashtag ein"
      }
    },
    "organization": {
      "members": {
        "confirmRemoveTitle": "Mitglied entfernen?",
        "remove": "Entfernen"
      }
    },
    "personas": {
      "wizard": {
        "generating": {
          "segmentsStage1": "Wissensdatenbank wird gelesen...",
          "segmentsStage2": "Kundensegmente in Ihren Daten werden gemessen..."
        },
        "framework": {
          "cardTitle": "Theoretisches Framework",
          "cardHint": "Die Linse, durch die Ihre Personas profiliert werden — jede Persona wird auf dessen Konstrukten bewertet.",
          "statusLocked": "Gesperrt",
          "statusInherited": "Aus Ihrem Forschungsfragen-Set",
          "statusManual": "Ihre Wahl",
          "statusAuto": "Automatisch für Ihre Frage gewählt",
          "anchoredIn": "Verankert in",
          "changeShort": "Ändern",
          "constructs": "Konstrukte",
          "crowdedWarning": "Radar könnte überfüllt erscheinen"
        },
        "segments": {
          "title": "Korpus-Segmente",
          "hint": "Finden Sie die natürlichen Kundensegmente in Ihrer Wissensdatenbank — eine Persona pro Segment, gewichtet nach ihrem gemessenen Anteil am Korpus.",
          "discover": "Segmente entdecken",
          "discovering": "Korpus wird analysiert…",
          "noCorpus": "Nicht genügend Dokumentinhalte in dieser Wissensdatenbank für eine zuverlässige Segmentierung.",
          "discoverFailed": "Segmententdeckung fehlgeschlagen. Sie können Personas weiterhin normal generieren.",
          "useSegments": "Anzahl aus meinen Daten ableiten — eine Persona pro entdecktem Segment (empfohlen)",
          "countLocked": "Durch Segmente festgelegt",
          "basis": "Gemessen aus {{docs}} Dokumenten ({{chunks}} Auszüge)",
          "rediscover": "Neu entdecken",
          "autoHint": "Ihre Daten entscheiden: eine Persona pro Kundensegment in der Wissensdatenbank, gewichtet nach dem gemessenen Anteil.",
          "autoChip": "Auto",
          "autoOption": "Auto — eine Persona pro Segment in meinen Daten (empfohlen)",
          "manualOption": "Manuell — ich wähle die Anzahl",
          "postSaveNote": "Nach dem Speichern kalibriert Yatabase dieses Panel automatisch an Ihrer Wissensdatenbank, testet es blind gegen echte Kundenaussagen und verfeinert seine Stimme, bis ein Prüfmodell keinen Unterschied mehr erkennt. Live auf der Sammlungsseite zu verfolgen."
        },
        "step1": {
          "setAttribute": "Attribut",
          "filterPersonaOnly": "Nur Persona",
          "filterAll": "Alle",
          "noQuestionsMatch": "Keine Fragen entsprechen dem ausgewählten Filter.",
          "hoverCategoryPrompt": "Fahren Sie mit der Maus über eine Kategorie, um Fragen zu sehen",
          "selectResearchQuestion": "Wählen Sie eine Forschungsfrage"
        },
        "messages": {
          "personaGeneratedSuccessfully": "Neue Persona erfolgreich generiert!",
          "errorGeneratingPersona": "Fehler bei der Generierung der Persona. Bitte versuchen Sie es erneut.",
          "derivedEmpty": "Kontext- und Sprachattribute konnten nicht generiert werden. Öffnen Sie den Editor und klicken Sie auf Regenerieren.",
          "waitForExtras": "Warten Sie, bis die AI-abgeleiteten Attribute fertiggestellt sind",
          "regenerateDerivedFailed": "Konnte abgeleitete Attribute nicht regenerieren",
          "regenerateExtrasFailed": "Konnte Ziele & Schmerzpunkte nicht regenerieren"
        },
        "buttons": {
          "generating": "Wird generiert...",
          "addPersona": "Persona hinzufügen",
          "addBlank": "Leere Persona hinzufügen",
          "aiGenerate": "AI Persona generieren"
        },
        "gen": {
          "substrate": "Substrat",
          "substrateDesc": "Demografie + Rahmenkonstrukte, kalibriert",
          "images": "Porträts",
          "imagesDesc": "Portraits, die dem Geschlecht, Alter und Standort jeder Persona entsprechen",
          "extras": "Ziele & Schmerzpunkte",
          "extrasDesc": "Was jede Persona will — und was schmerzt — verankert in ihrem Konstruktprofil",
          "derived": "Kontext & Stimme",
          "derivedDesc": "Tag im Leben, JTBD-Job, Einwände, Werte, Sprachproben und Reisephase — in einem kohärenten Durchgang generiert",
          "composing": "Ihre Personas werden erstellt",
          "composingBlurb": "Jeder Schritt ist ein separater AI-Aufruf, sodass jedes Detail auf validierter Theorie basiert — nicht isoliert erfunden.",
          "complete": "vollständig",
          "now": "jetzt",
          "footnote": "Sie können bereits jede Persona bearbeiten, während der Rest generiert wird. Speichern ist freigeschaltet, sobald alle Schritte abgeschlossen sind.",
          "cardDescPending": "Beschreibung dieser Persona aus ihrem Konstruktprofil wird erstellt…",
          "cardDescEmpty": "Noch keine Beschreibung — klicken Sie im Editor auf Regenerieren, um eine zu generieren."
        },
        "step3": {
          "composingDescription": "Beschreibung aus dem Konstruktprofil wird erstellt…"
        },
        "navigation": {
          "waitingForFramework": "Warten auf Rahmenwerk…",
          "updatingCollection": "Aktualisieren...",
          "savingCollection": "Speichern..."
        }
      },
      "library": {
        "badges": {
          "shared": "GETEILT"
        },
        "labels": {
          "more": "mehr",
          "theoreticalLens": "Theoretische Linse"
        }
      },
      "health": {
        "savedTitle": "Sammlung gespeichert",
        "gapsTitle": "Ein Punkt lohnt den Blick — {{n}} Thema/Themen Ihrer Kunden haben in diesem Panel noch keine Stimme:",
        "allCovered": "Alle gemessenen Themen haben jetzt eine Stimme — Abdeckung {{cov}} %.",
        "progress": "Bisher {{added}} Persona(s) ergänzt — Abdeckung jetzt {{cov}} %.",
        "done": "Fertig — Sammlung ansehen"
      },
      "realism": {
        "pending": "Realismusprüfung wartet — ein verblindetes KI-Prüfmodell testet dieses Panel gegen echte Kundenaussagen aus Ihrer Wissensdatenbank, sobald Kontext und Stimme generiert sind.",
        "tuning": "Realismus wird geprüft — ein verblindetes KI-Prüfmodell vergleicht die Aussagen Ihrer Personas mit echten Kundenaussagen aus Ihrer Wissensdatenbank und passt ihre Stimme an, wenn es sie unterscheiden kann…",
        "passTitle": "Das verblindete Prüfmodell konnte keinen Unterschied erkennen.",
        "added": "{{n}} Persona(s) ergänzt, um Themen abzudecken, über die Ihre Kunden sprechen: {{topics}}.",
        "designNote": "Panel so entworfen, dass es alle {{total}} in Ihren Daten gemessenen Themen abdeckt.",
        "designNotePartial": "Panel so entworfen, dass es {{assigned}} von {{total}} gemessenen Themen abdeckt.",
        "coverageMet": "Gemessene Themenaufmerksamkeit: {{cov}} % — auf dem Niveau des publizierten Benchmarks (75 %+).",
        "coverageBelow": "Gemessene Themenaufmerksamkeit: {{cov}} % — diese Messung simuliert, was die Personas im Gespräch tatsächlich betonen würden, fällt konservativ aus und schwankt ±10 Punkte zwischen Läufen.",
        "missingLabel": "Noch ohne Stimme:",
        "missingNext": "Nach dem Speichern bietet die Sammlungsseite Ein-Klick-Korrekturen — pro Thema eine Persona ergänzen, die Wissensdatenbank aktualisieren oder es so belassen.",
        "passBody": "Die Aussagen dieses Panels waren statistisch nicht von denen Ihrer echten Kunden zu unterscheiden ({{acc}} % Prüfgenauigkeit, 50 % = reiner Zufall).",
        "adjusted": "Stimme dafür {{n}}× automatisch angepasst.",
        "twoDraws": "Über 2 unabhängige Ziehungen verifiziert ({{n}} Aussagen).",
        "partial": "Ein verblindetes Prüfmodell konnte Persona-Aussagen nach {{n}} Stimmanpassungen noch unterscheiden ({{acc}} % Genauigkeit). Das bedeutet meist, dass die Wissensdatenbank gemischte Inhalte statt echter Kundenaussagen enthält — das Panel bleibt in Ihren Daten fundiert.",
        "unavailable": "Realismusprüfung derzeit nicht verfügbar — Ihre Personas können trotzdem gespeichert werden.",
        "stale": "Das Panel wurde nach der Verifizierung geändert — das Blindtest-Urteil gilt nicht mehr und wird nicht gespeichert.",
        "reverify": "Panel erneut verifizieren",
        "chip": "Blind verifiziert",
        "chipTip": "Ein verblindetes KI-Prüfmodell konnte die Aussagen dieses Panels nicht zuverlässig von echten Kundenaussagen aus Ihrer Wissensdatenbank unterscheiden ({{acc}} % Genauigkeit über {{n}} Aussagen, 50 % = reiner Zufall)."
      },
      "calibration": {
        "bestFitDesigned": "Dieses Panel ist eine bestmögliche Anpassung an die Kundenstimmen in Ihrer Wissensdatenbank: Segmente und Gewichte wurden daraus gemessen, und jedes Thema wurde gezielt zugewiesen. Die oben aufgeführten Abweichungen spiegeln den Korpus selbst wider, keinen Erstellungsfehler.",
        "bestFitManual": "Die oben aufgeführten Abweichungen wurden gegen die Kundenstimmen in Ihrer verknüpften Wissensdatenbank gemessen.",
        "bestFitKbCta": "Mehr echte Kundenstimmen (Bewertungen, Interviews, Tickets) in der Wissensdatenbank sind der zuverlässigste Weg zu präziseren zukünftigen Panels.",
        "unchanged": "Seit der letzten Kalibrierung hat sich nichts geändert — die bestehende Messung wird angezeigt. Bearbeiten Sie Personas oder aktualisieren Sie die Wissensdatenbank, um neu zu messen.",
        "tierGenerated": "Generiert",
        "tierGrounded": "Fundiert",
        "tierCalibrated": "Kalibriert",
        "tierGeneratedTip": "Generiert ohne Kundendaten — verlinken Sie eine Wissensdatenbank, um es zu fundieren",
        "tierGroundedTip": "Generiert unter Verwendung Ihrer Kundendaten als Kontext — führen Sie einen Kalibrierungsbericht durch, um die Übereinstimmung zu messen",
        "tierCalibratedTip": "Gemessen gegen das echte Kundenstimmen-Korpus in der verlinkten Wissensdatenbank",
        "panelTitle": "Kalibrierung",
        "coverage": "Themenabdeckung",
        "sentimentDelta": "Sentiment-Differenz",
        "corpusMeta": "{{chunks}} Auszüge aus {{docs}} Dokumenten",
        "showDetails": "Details anzeigen",
        "hideDetails": "Details ausblenden",
        "notYetRun": "Messen Sie dieses Panel gegen die echte Kundenstimme in seiner Wissensdatenbank.",
        "run": "Kalibrierungsbericht ausführen",
        "rerun": "Kalibrierung erneut ausführen",
        "running": "Messen…",
        "realSentiment": "Echte Kunden",
        "panelSentiment": "Persona-Panel",
        "missing": "Fehlt im Panel",
        "underweighted": "Untergewichtet",
        "overweighted": "Übergewichtet",
        "noCorpus": "Die verlinkte Wissensdatenbank hat zu wenig indizierten Inhalt, um dagegen zu kalibrieren.",
        "failed": "Kalibrierungsbericht fehlgeschlagen. Bitte versuchen Sie es erneut.",
        "addGapPersona": "+ Persona hinzufügen",
        "gapRunning": "Wird hinzugefügt…",
        "gapAdded": "Persona \"{{name}}\" hinzugefügt — Panel wird neu gemessen…",
        "history": "Abdeckung über Durchläufe",
        "autoRunning": "Automatische Kalibrierung — dieses Panel wird an Ihrer Wissensdatenbank gemessen…",
        "suggestTitle": "{{n}} Thema/Themen haben keine Stimme in diesem Panel:",
        "suggestAdd": "{{n}} Persona(s) zur Abdeckung ergänzen",
        "suggestAdding": "Wird ergänzt & neu gemessen…",
        "suggestKb": "Oder ergänzen Sie Dokumente zu diesen Themen in Ihrer",
        "suggestKbLink": "Wissensdatenbank",
        "suggestKbTail": "— die Ursachenbehebung, die jedes künftige Panel verbessert.",
        "suggestDismiss": "So belassen — auch das ist in Ordnung",
        "ofAttention": "der Kundenaufmerksamkeit",
        "coverageTip": "Anteil der Themen echter Kunden (gemessen in der Wissensdatenbank), die mindestens eine Persona dieses Panels abdeckt.",
        "sentimentDeltaTip": "Größte Differenz zwischen der Positiv-/Negativ-Stimmung echter Kunden und der des Persona-Panels. Niedriger ist besser."
      },
      "validation": {
        "pill": "Blindtest",
        "pillTip": "Trefferquote des verblindeten Prüfmodells bei der Unterscheidung echter von synthetischen Aussagen: {{acc}} % bei {{n}} Aussagen (p = {{p}} gegenüber der 50-%-Zufallsgrenze). Prüfmodell: {{model}} — {{caveat}}.",
        "atChance": "auf Zufallsniveau ({{acc}} %)",
        "aboveChance": "unterscheidbar ({{acc}} %)",
        "holdout": "Holdout-Abdeckung",
        "run": "Blindtest starten",
        "rerun": "Blindtest wiederholen",
        "running": "Test läuft…",
        "buttonTip": "Blindtest: Kann ein Prüfmodell die Aussagen Ihrer Personas von echten Kundenaussagen unterscheiden?",
        "noCorpus": "Nicht genügend echte Kundenaussagen in der Wissensdatenbank, um den Blindtest durchzuführen.",
        "failed": "Der Validierungstest ist fehlgeschlagen. Bitte versuchen Sie es erneut.",
        "detailTitle": "Blindvalidierung",
        "detailAtChance": "Ein verblindetes Prüfmodell konnte die Aussagen dieses Panels nicht zuverlässig von echten Kundenaussagen unterscheiden — {{acc}} % Genauigkeit bei {{n}} Aussagen ist statistisch nicht von der 50-%-Zufallsgrenze zu unterscheiden (p = {{p}}).",
        "detailAboveChance": "Ein verblindetes Prüfmodell unterschied die Aussagen dieses Panels mit {{acc}} % Genauigkeit bei {{n}} Aussagen von echten Kundenaussagen (p = {{p}}) — die Stimme des Panels unterscheidet sich weiterhin messbar von echten Kunden.",
        "detailHoldout": "Out-of-Sample-Prüfung: Das Panel deckte {{cov}} % der Themen in {{docs}} zurückgehaltenen Dokumenten ab, auf denen es nie fundiert wurde.",
        "detailCaveat": "Einschränkung: Prüf- und Generierungsmodell stammen aus derselben Modellfamilie ({{model}}).",
        "refine": "Stimme verfeinern",
        "refining": "Verfeinern…",
        "refineTip": "Überarbeitet die Stimme jeder Persona in Richtung des echten Kundenregisters, testet blind erneut, wiederholt (max. 3 Runden).",
        "refineDone": "Stimme verfeinert — das Prüfmodell kann das Panel nicht mehr zuverlässig von echten Kunden unterscheiden.",
        "refineStopped": "{{rounds}} Runden verfeinert — das Panel ist näher dran, aber noch unterscheidbar. Sie können erneut starten.",
        "refineHistory": "Stimmverfeinerung:",
        "refineRounds": "{{n}} Runde(n), pro Runde frisch gezogene Aussagen",
        "autoRunning": "Blindtest läuft…",
        "pooledPill": "Ununterscheidbarkeit",
        "pooledTip": "Gepoolt über {{runs}} unabhängige Blindtests ({{n}} Aussagen): Prüfgenauigkeit {{acc}} % gegenüber der 50-%-Zufallsgrenze (p = {{p}}). 100 = exakt Münzwurf.",
        "pooledDetailTitle": "Gepoolter Wert",
        "pooledDetail": "Über {{runs}} unabhängige Blindtests ({{n}} Aussagen, jeweils frische Ziehung) erreichte das Prüfmodell im Schnitt {{acc}} % — Ununterscheidbarkeit {{score}}/100 (p = {{p}}).",
        "stepSegments": "Segmente",
        "stepCalibration": "Kalibrierung",
        "stepBlindTest": "Blindtest",
        "stepRefine": "Stimmverfeinerung",
        "stepRefined": "Stimme verfeinert ({{n}}×)",
        "stepNotNeeded": "Nicht nötig",
        "stepPooled": "Pool-verifiziert",
        "holdoutTip": "Out-of-Sample-Prüfung: Themenabdeckung nur gegen zurückgehaltene Dokumente ({{docs}}), auf denen das Panel nie fundiert wurde — Generalisierung statt Nachplappern.",
        "pooledTipOne": "Bisher nur ein Blindtest ({{n}} Aussagen): Prüfgenauigkeit {{acc}} % gegenüber der 50-%-Zufallsgrenze (p = {{p}}). 100 = exakt Münzwurf. Führen Sie den Blindtest erneut aus, um unabhängige Ziehungen zu ergänzen und den Wert zu festigen.",
        "pooledDetailOne": "Bisher ein Blindtest ({{n}} Aussagen): Prüfgenauigkeit {{acc}} % — Ununterscheidbarkeit {{score}}/100 (p = {{p}}). Weitere Durchläufe fügen unabhängige Ziehungen hinzu; der Wert festigt sich mit jedem Test."
      },
      "optimize": {
        "button": "Auto-Verbesserung",
        "buttonTip": "Erledigt alles für Sie: misst das Panel, schließt Themenlücken, führt Blindtests durch und verfeinert die Stimme — bis zu 3 Runden — mit dem Ziel durchgehend grüner Kennzahlen.",
        "running": "Verbessern… Runde {{n}}",
        "doneNote": "Auto-Verbesserung abgeschlossen — alle Kennzahlen haben ihre Ziele erreicht.",
        "partialNote": "Die Auto-Verbesserung ging so weit, wie dieser Korpus es zulässt: {{unmet}} erreichte das Ziel nicht. Kleine oder gemischte Wissensdatenbanken begrenzen diese Werte — reichhaltigere echte Kundenaussagen heben die Obergrenze.",
        "errorNote": "Bei der Auto-Verbesserung ist ein Fehler aufgetreten — Sie können es erneut versuchen.",
        "startFailed": "Auto-Verbesserung konnte nicht gestartet werden. Bitte erneut versuchen.",
        "stop": "Stopp",
        "stopping": "Wird gestoppt…",
        "stoppedNote": "Die Auto-Verbesserung wurde gestoppt. Die Kennzahlen zeigen den bisherigen Fortschritt — jederzeit erneut startbar.",
        "greenTitle": "Alles grün — was das bedeutet",
        "greenCoverage": "Ihre Personas sprechen über dieselben Themen wie Ihre echten Kunden — kein wichtiges Thema Ihrer Daten bleibt unvertreten.",
        "greenSentiment": "Sie empfinden wie Ihre Kunden — die Positiv-/Negativ-Balance des Panels entspricht den echten Daten.",
        "greenBlind": "Sie klingen wie Ihre Kunden — ein KI-Prüfmodell konnte gemischte echte und Persona-Aussagen nicht zuverlässig auseinanderhalten.",
        "greenSummary": "Forschung mit diesem Panel spiegelt Ihre echten Kunden wider — ihre Themen, ihre Stimmung, ihre Stimme — statt einer generischen KI-Vermutung.",
        "partialTitle": "Die Auto-Verbesserung ging so weit, wie diese Daten es zulassen",
        "whyCoverage": "Themenabdeckung ({{value}} % von {{target}} %): Ihre Kunden besprechen mehr Themen, als dieses Panel vertreten kann. Meist ist die Wissensdatenbank sehr gemischt, oder das Panel hat sein 10-Personas-Limit erreicht. Dokumente zu den fehlenden Themen (in den Details gelistet) — oder zwei fokussierte Panels — verbessern den Wert.",
        "whySentiment": "Stimmungslücke ({{value}} % vs. ≤{{target}} %): Die Gesamtstimmung des Panels weicht noch von Ihren Kunden ab. Das schließt sich meist mit den Themenlücken, denn fehlende Themen tragen oft die fehlenden Emotionen.",
        "whyBlind": "Blindtest ({{value}}/100): Ein Prüfmodell erkennt Persona-Aussagen noch. Häufigste Ursache: eine Wissensdatenbank mit gemischten Inhalten (gesammelte Posts, Marketingtexte, Artikel) statt echter Kundenaussagen — es gibt keine einheitliche „Kundenstimme“ zum Nachbilden. Mit echten Bewertungen, Feedback oder Transkripten besteht dieser Test in der Regel.",
        "partialSummary": "Die bereits grünen Kennzahlen sind real gemessene Ergebnisse — und ein erneuter Lauf nach Anreicherung der Wissensdatenbank kann den Rest anheben."
      }
    },
    "common": {
      "unknown": "Unbekannt",
      "adding": "Wird hinzugefügt..."
    },
    "personaChat": {
      "responseStyleNote": "Die Eigenschaften der Persona haben immer Vorrang vor diesen Einstellungen.",
      "focusGroup": {
        "textRegenFailed": "Attribute gespeichert, aber die Textregeneration ist fehlgeschlagen"
      }
    },
    "projects": {
      "teamAssigned": "Team erfolgreich dem Projekt zugewiesen",
      "addTeamToProject": "Team zum Projekt hinzufügen",
      "totalProjects": "Gesamtprojekte",
      "deleteProject": "Löschen",
      "deleteProjectTitle": "Projekt löschen?"
    },
    "library": {
      "contextBasedResearch": "Kontextbasierte Forschung",
      "researchQuestion": "Forschungsfrage",
      "suggestedQuestions": "Forschungsfragen",
      "more": "mehr",
      "theoreticalLens": "Theoretische Linse",
      "archiveFastResearch": "Schnellforschung archivieren",
      "restoreQuickStart": "Wiederherstellen",
      "status": {
        "processingInterview": "Generiere Interviewantworten",
        "extractingThemes": "Analysiere Interviewthemen"
      }
    },
    "quickstart": {
      "modal": {
        "step1": {
          "contextRequired": "Bitte geben Sie mindestens eine Kontextquelle an: eine Website-URL, Wissensdatenbank oder zusätzlichen Kontext.",
          "projectRequired": "Bitte wählen Sie ein Projekt aus",
          "researchFirst": "Bitte führen Sie zuerst die Webforschung durch, indem Sie auf die Suchschaltfläche neben der URL klicken.",
          "project": "Projekt",
          "selectProject": "Projekt auswählen",
          "insufficientQuota": "Unzureichendes Kontingent",
          "noQuotaRemaining": "Sie haben keine synthetischen Antworten mehr. Bitte aktualisieren Sie Ihren Plan, um die Schnellforschung zu nutzen.",
          "lowQuotaRemaining": "Sie haben nur noch {{count}} synthetische Antworten. Ein Minimum von 10 ist für eine sinnvolle Analyse erforderlich.",
          "upgradePlan": "Plan upgraden"
        },
        "step2": {
          "questionRequired": "Bitte wählen oder geben Sie eine Forschungsfrage ein",
          "contextSummary": "Kontextzusammenfassung",
          "uxAnalysis": "UX- und Viewport-Analyse",
          "loadingRQSets": "Lade...",
          "noRQSets": "Keine gespeicherten Forschungsfragen-Sets",
          "questions": "Fragen"
        }
      },
      "results": {
        "fastResearch": "Schnellforschung",
        "contextBased": "Kontextbasierte Forschung",
        "previewReportDesc": "Zusammenfassende Übersicht Ihrer Forschung",
        "theoreticalLens": "Theoretische Linse — verankert jedes Artefakt in dieser Schnellforschung",
        "constructs": "Konstrukte",
        "whatIsFramework": "Was ist {{name}}?",
        "constructGlossary": "Konstrukt-Glossar"
      },
      "tabs": {
        "convergence": "Konvergenz"
      },
      "convergence": {
        "confirmed": "Bestätigt",
        "nuanced": "Nuanciert",
        "ruledOut": "Ausgeschlossen",
        "tensionFlagged": "Spannung markiert",
        "singleSource": "Einzelquelle",
        "loading": "Lade Konvergenzbericht…",
        "unavailableTitle": "Konvergenzbericht für diesen Durchlauf nicht verfügbar",
        "unavailableIntro": "Diese Schnellforschung wurde erstellt, bevor die theoretische Verankerung aktiviert wurde. Ihre Personas und Interviews wurden ohne ein gemeinsames theoretisches Rahmenwerk generiert, sodass es keine Beweise auf Konstrukt-Ebene gibt, um zu triangulieren.",
        "unavailableAction": "Starten Sie eine neue Schnellforschung, um den vollständigen Konvergenzbericht zu erhalten — das Rahmenwerk wird automatisch ausgewählt und zieht sich durch jedes Artefakt, das es generiert.",
        "startNew": "Neue Schnellforschung starten",
        "title": "Methodologischer Konvergenzbericht",
        "intro": "Methodologische Triangulation über die drei unabhängigen Generationen in dieser Schnellforschung — Persona-Profile, qualitative Interviews und quantitative Umfrageantworten — alle verankert im selben theoretischen Rahmenwerk. Konstrukte, bei denen alle Methoden konvergieren, bilden Ihre Ergebnisse mit der höchsten Zuversicht. Konstrukte, bei denen sie divergieren, sind keine Fehler — sie sind die interessantesten Ergebnisse, die dort auftauchen, wo simulierte Annahmen, narrative Beweise und gemessene Signale in unterschiedliche Richtungen ziehen.",
        "framework": "Verankert gegen",
        "generating": "Generiere…",
        "generate": "Konvergenzbericht generieren",
        "regenerating": "Regeneriere…",
        "regenerate": "Regenerieren",
        "headerConfirmed": "Konstrukte, die über alle verfügbaren Methoden bestätigt wurden.",
        "synthesis": "Kreuz-Konstrukt-Synthese",
        "personaSide": "Persona-Beweise",
        "corePersonas": "Personas von zentraler Bedeutung",
        "avgImportance": "Durchschnittliche Bedeutung",
        "personaNotScored": "Diese Personas wurden für dieses Konstrukt nicht bewertet, daher gibt es keinen Vergleich auf der Persona-Seite.",
        "interviewSide": "Interviewbeweise",
        "evidenceStrength": "Beweiskraft",
        "convergentQuote": "Konvergentes Zitat",
        "divergentQuote": "Divergentes Zitat",
        "noInterviewData": "Die Interviewanalyse wurde für dieses Konstrukt nicht abgeschlossen.",
        "surveySide": "Umfragebeweise",
        "meanAgreement": "Durchschnittliche Zustimmung",
        "highAgreement": "stark zugestimmt",
        "surveyItem": "Element",
        "surveyItems": "Elemente",
        "surveyResponses": "Antworten",
        "themeValidationTitle": "Validierung emergenter Themen",
        "themeValidationIntro": "Interviews haben Themen außerhalb des Rahmens hervorgebracht. Die Umfrage testete dann, ob diese Themen im größeren Maßstab Bestand haben – das sequenzielle explorative Mixed-Methods-Design in der Praxis.",
        "themeValidated": "VALIDIERT",
        "themePartial": "TEILWEISE",
        "themeNotValidated": "NICHT VALIDIERT",
        "themeNoSurvey": "NUR QUALITATIV",
        "themeFromInterview": "Aus Interviews",
        "themeFromSurvey": "Aus Umfragen",
        "footer": "Generiert durch die Yatabase-Konvergenz-Pipeline unter Verwendung eines sequenziellen explorativen Mixed-Methods-Designs (Creswell & Plano Clark): evidenzbasierte Aggregation pro Konstrukt über Persona-Profile, Interviews und Umfrageantworten, verankert an"
      },
      "status": {
        "retry": "Erneut versuchen"
      }
    },
    "researchQuestions": {
      "errors": {
        "loadFailed": "Fehler beim Laden des Forschungsfragen-Sets.",
        "saveFailed": "Fragen wurden generiert, konnten aber nicht in die Bibliothek gespeichert werden.",
        "generateFailed": "Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut."
      }
    },
    "rqLibrary": {
      "loadingMore": "Laden...",
      "moreCategories": "weitere Kategorien"
    },
    "syntheticData": {
      "contextFromSurvey": "Kontext automatisch ausgefüllt aus Ihren YataForm-Einstellungen",
      "webResearchPlaceholder": "URL oder Suchthema...",
      "webResearchHint": "Optional: Fügen Sie Webkontext für realistischere Antworten hinzu",
      "additionalContext": "Zusätzlicher Kontext (Optional)",
      "additionalContextPlaceholder": "Geschäftskontext, Details zur Zielgruppe, spezifische Anweisungen zur Antwortgenerierung...",
      "additionalContextHint": "Geben Sie Kontext oder Anweisungen zur Anleitung der synthetischen Antwortgenerierung an"
    },
    "teams": {
      "memberCount_label": "Mitglieder",
      "projectCount_label": "Projekte",
      "buttons": {
        "editName": "Namen bearbeiten",
        "addMember": "Mitglied hinzufügen",
        "deleteTeam": "Löschen",
        "removeMember": "Entfernen",
        "leaveTeam": "Verlassen"
      },
      "modals": {
        "editName": {
          "title": "Teamname bearbeiten",
          "placeholder": "Teamname"
        }
      },
      "confirmations": {
        "removeMemberTitle": "Mitglied entfernen?",
        "leaveTeamTitle": "Team verlassen?",
        "deleteTeamTitle": "Team löschen?"
      }
    },
    "theoryBridge": {
      "library": {
        "details": {
          "narrative": "Erzählung"
        },
        "tooltips": {
          "archiveAnalysis": "Archivieren",
          "totalAnalyses": "Gesamtanzahl der Analysen",
          "totalMappings": "Gesamtanzahl der abgebildeten Theorien über alle Analysen",
          "totalGaps": "Gesamtanzahl der identifizierten Lücken über alle Analysen"
        },
        "labels": {
          "analyses": "Analysen",
          "mappings": "Abbildungen",
          "gaps": "Lücken"
        },
        "searchPlaceholder": "Analysen durchsuchen...",
        "loading": "Analysen werden geladen...",
        "noResults": "Keine passenden Analysen",
        "noResultsDesc": "Versuchen Sie einen anderen Suchbegriff",
        "archiveAnalysis": "Analyse archivieren?",
        "confirmArchive": "Dies wird die Theory Bridge-Analyse archivieren und von Ihrer aktiven Liste entfernen. Dies kann nicht rückgängig gemacht werden.",
        "cancel": "Abbrechen",
        "yesArchive": "Ja, archivieren"
      }
    },
    "urlInput": {
      "step0": {
        "filterYataformOnly": "Nur YataForm",
        "filterAll": "Alle",
        "noQuestionsMatch": "Keine Fragen entsprechen den ausgewählten Filtern.",
        "hoverCategoryPrompt": "Fahren Sie mit der Maus über eine Kategorie, um Fragen zu sehen",
        "selectQuestionPrompt": "Wählen Sie eine Forschungsfrage"
      },
      "wizardTitle": "YataForm erstellen"
    },
    "contextSources": {
      "preFilledFromSet": "Kontext automatisch ausgefüllt aus Ihrem Fragenkatalog"
    },
    "yataFormLibrary": {
      "status": {
        "active": "Aktiv",
        "closed": "Geschlossen",
        "draft": "Entwurf"
      },
      "types": {
        "aiYataForm": "AI YataForm",
        "documentAnalysis": "Dokumentenanalysen",
        "excelAnalysis": "Excel-Analyse",
        "webhookKB": "Webhook KB",
        "customForm": "Benutzerdefiniertes Formular"
      },
      "loading": "YataForms werden geladen...",
      "title": "YataForm-Bibliothek",
      "subtitle": "Erstellen und verwalten Sie KI-gestützte Formulare und Umfragen zur Datensammlung und -analyse",
      "tooltips": {
        "totalActive": "Gesamtanzahl aktiver YataForms in Ihrer Bibliothek",
        "unassigned": "YataForms, die keinem Projekt zugewiesen sind",
        "open": "YataForms, die derzeit Antworten annehmen",
        "closed": "YataForms, die keine Antworten mehr annehmen",
        "responses": "YataForms, die mindestens eine Antwort gesammelt haben",
        "viewDashboard": "Dashboard anzeigen",
        "editName": "Namen bearbeiten"
      },
      "labels": {
        "active": "Aktiv",
        "unassigned": "Nicht zugewiesen",
        "inProject": "Im Projekt",
        "open": "Offen",
        "closed": "Geschlossen",
        "responses": "Antworten",
        "untitled": "Unbetitelt",
        "shared": "Geteilt"
      },
      "searchPlaceholder": "YataForms suchen...",
      "filters": {
        "allProjects": "Alle Projekte",
        "unassigned": "Nicht zugewiesen"
      },
      "buttons": {
        "newYataForm": "Neues YataForm",
        "createYataForm": "YataForm erstellen",
        "saveChanges": "Änderungen speichern",
        "cancel": "Abbrechen",
        "assigning": "Zuweisen...",
        "assignToProject": "Projekt zuweisen"
      },
      "menu": {
        "aiYataForm": "AI YataForm",
        "aiYataFormDesc": "Intelligente Formulare mit KI erstellen",
        "curatedTemplates": "Kuratierten Vorlagen (Enterprise)",
        "curatedTemplatesDesc": "Von Experten gestaltete Vorlagen für Unternehmens-Teams"
      },
      "emptyState": {
        "title": "Keine YataForms gefunden",
        "adjustFilters": "Versuchen Sie, Ihre Such- oder Filterkriterien anzupassen",
        "createFirst": "Erstellen Sie Ihr erstes YataForm, um mit der Datensammlung und -analyse zu beginnen"
      },
      "modals": {
        "editName": {
          "title": "YataForm-Namen bearbeiten"
        },
        "projectAssign": {
          "changeTitle": "YataForm-Projektzuweisung ändern",
          "assignTitle": "YataForm einem Projekt zuweisen",
          "yataForm": "YataForm",
          "selectProject": "Projekt auswählen",
          "chooseProject": "Wählen Sie ein Projekt...",
          "currentProject": "Aktuelles Projekt"
        }
      },
      "details": {
        "responses": "Antworten",
        "created": "Erstellt",
        "accessCode": "Zugangs-Code",
        "questions": "Fragen"
      },
      "descriptions": {
        "aiGenerated": "KI-generiertes YataForm mit intelligenter Analyse und Erkenntnissen",
        "documentAnalysis": "Umfrage zur Dokumentenanalyse für die Verarbeitung unstrukturierter Daten",
        "excelAnalysis": "Excel-Datenanalyse mit KI-gestützten Erkenntnissen und Mustern",
        "customSurvey": "Benutzerdefinierte Umfrage wartet auf Datensammlung und -analyse"
      }
    }
  },
  "id": {
    "globalSettings": {
      "mcpConnectionsTitle": "Asisten yang terhubung",
      "mcpConnectionsEmpty": "Belum ada asisten AI yang terhubung ke akun Anda.",
      "mcpConnected": "Terhubung",
      "mcpLastActive": "Terakhir aktif",
      "mcpRevoke": "Cabut",
      "mcpPlanBadge": "Termasuk dalam paket Anda (fitur Growth & Enterprise).",
      "mcpPlanUpgrade": "Konektor asisten AI tersedia pada paket Growth dan Enterprise. Tingkatkan paket untuk mengaktifkannya bagi organisasi Anda.",
      "mcpTitle": "Hubungkan asisten AI (Claude & ChatGPT)",
      "mcpIntro": "Berikan Claude atau ChatGPT akses aman hanya-baca ke riset organisasi Anda — laporan Fast Research, persona, wawancara, dan basis pengetahuan. Anda masuk dengan akun Yatabase saat penyiapan; akses dapat dicabut kapan saja dengan menghapus konektor.",
      "mcpUrlLabel": "URL konektor",
      "mcpCopy": "Salin",
      "mcpCopied": "Tersalin",
      "mcpClaudeStep1": "Di claude.ai, buka Settings → Connectors lalu pilih \"Add custom connector\".",
      "mcpClaudeStep2": "Tempel URL konektor di atas lalu konfirmasi.",
      "mcpClaudeStep3": "Klik tombol masuk pada konektor, masuk dengan akun Yatabase Anda, lalu tekan Allow.",
      "mcpChatgptStep1": "Di ChatGPT, buka Settings → Connectors (memerlukan mode developer atau paket Business/Enterprise).",
      "mcpChatgptStep2": "Tambahkan konektor baru dengan URL di atas.",
      "mcpChatgptStep3": "Masuk dengan akun Yatabase Anda saat diminta, lalu tekan Allow.",
      "mcpNote": "Konektor hanya membaca riset yang dapat dilihat akun Anda di Yatabase — tidak ada yang dapat diubah atau dihapus melaluinya. Pertanyaan Anda diproses oleh asisten AI yang terhubung."
    },
    "autobase": {
      "schedule": {
        "once": "Jalankan Sekali",
        "dailyAt": "Setiap hari pada {{time}}",
        "weeklyAt": "Setiap minggu pada {{day}} pada {{time}}",
        "monthlyAt": "Setiap bulan pada hari {{day}} pada {{time}}"
      }
    },
    "dashboard": {
      "pageTitle": "Dasbor",
      "subtitle": "Ini adalah masalah yang Anda selesaikan"
    },
    "documentAnalyzer": {
      "errors": {
        "downloadFailed": "Gagal mendapatkan URL unduhan. Silakan coba lagi.",
        "downloadError": "Terjadi kesalahan saat mengunduh dokumen."
      },
      "documents": "Dokumen",
      "downloadDocument": "Unduh dokumen",
      "viewSummary": "Lihat ringkasan"
    },
    "documentLibrary": {
      "summary": {
        "knowledgeBases": "Basis Pengetahuan",
        "archived": "Arsip"
      },
      "intelligence": {
        "insights": "Intelijen",
        "dataPoints": "titik data yang dianalisis"
      },
      "manageTeams": "Kelola Tim"
    },
    "documentSummary": {
      "overview": "Ikhtisar",
      "section": "bagian",
      "sections": "bagian",
      "collapseAll": "Liput semua",
      "expandAll": "Perluas semua"
    },
    "estatJapan": {
      "upgradeHeaderTitle": "Integrasi Jepang"
    },
    "googleAdsLibrary": {
      "noAdvertisers": "Tidak ada pengiklan yang ditemukan",
      "title": "Perpustakaan Iklan Google",
      "upgradeMessage": "Cari dan analisis kampanye iklan Google. Tersedia pada rencana Pertumbuhan dan Perusahaan.",
      "subtitle": "Cari dan analisis iklan dari Pusat Transparansi Iklan Google",
      "searchByName": "Berdasarkan Nama Pengiklan",
      "searchByDomain": "Berdasarkan Domain",
      "domainPlaceholder": "Masukkan domain (mis. nike.com)",
      "namePlaceholder": "Masukkan nama pengiklan (mis. Nike)",
      "search": "Cari",
      "creditsPerSearch": "kredit per pencarian",
      "creditsPerAdsFetch": "kredit per pengambilan iklan",
      "advertisersFound": "Pengiklan Ditemukan",
      "ads": "iklan",
      "backToResults": "Kembali",
      "pushToKB": "Dorong ke KB",
      "allFormats": "Semua Format",
      "allPlatforms": "Semua Platform",
      "deselectAll": "Batalkan Pilihan Semua",
      "selectAll": "Pilih Semua",
      "loadMore": "Muat Lebih Banyak",
      "adDetails": "Detail Iklan",
      "regions": "Wilayah",
      "variations": "Variasi",
      "pushTitle": "Dorong ke Basis Pengetahuan",
      "pushDescription": "Ekspor {{count}} iklan yang dipilih sebagai dokumen PDF ke dalam Basis Pengetahuan Anda.",
      "pushButton": "Dorong ke Basis Pengetahuan",
      "selectKB": "Pilih Basis Pengetahuan..."
    },
    "integrations": {
      "buttons": {
        "upgrade": "Tingkatkan Rencana"
      },
      "upgrade": {
        "message": "Kredit integrasi tersedia pada rencana Pertumbuhan dan di atasnya"
      },
      "credits": {
        "insufficient": "Kredit tidak mencukupi"
      }
    },
    "interviews": {
      "viewer": {
        "marketOpportunities": "Peluang Pasar",
        "riskAssessment": "Penilaian Risiko",
        "yatabaseAnalysis": "Analisis Yatabase",
        "analysisSubtitleWithLens": "Sintesis wawancara berbasis teori · {{lens}}",
        "analysisSubtitle": "Sintesis multi-langkah dari respons wawancara",
        "atAGlance": "Sekilas",
        "complexity": "Kompleksitas",
        "marketSize": "Ukuran Pasar",
        "competitiveAdvantage": "Keunggulan Kompetitif",
        "highRisks": "Risiko Tinggi",
        "mediumRisks": "Risiko Sedang",
        "mitigationStrategies": "Strategi Mitigasi",
        "questionnaire": "Kuesioner",
        "executiveSummaryTab": "Ringkasan Eksekutif",
        "noExecutiveSummary": "Tidak Ada Ringkasan Eksekutif Tersedia",
        "executiveSummaryNotGenerated": "Ringkasan eksekutif belum dihasilkan.",
        "yatabaseAnalysisTab": "Analisis Yatabase",
        "marketOpportunitiesCount": "Peluang Pasar",
        "risksIdentifiedCount": "Risiko yang Diidentifikasi",
        "marketOpportunitiesTooltip": "Peluang pasar yang diidentifikasi dengan estimasi ukuran dan keunggulan kompetitif",
        "riskAssessmentTooltip": "Risiko bisnis yang diidentifikasi dan strategi mitigasi yang direkomendasikan",
        "theoreticalLens": "Lensa Teoritis",
        "constructs": "konstruk",
        "inheritedFromRq": "Diperoleh dari kategori pertanyaan penelitian",
        "viewRqSet": "lihat set",
        "whatIsFramework": "Apa itu {{name}}?",
        "whyThisFramework": "Mengapa kerangka ini",
        "constructGlossary": "Glosarium konstruk"
      },
      "library": {
        "errorLoadingInterview": "Kesalahan memuat wawancara",
        "untitled": "Wawancara Tanpa Judul",
        "archiveInterview": "Arsipkan Wawancara",
        "tooltip": {
          "keyMessage": "Temuan Utama"
        },
        "labels": {
          "theoreticalLens": "Lensa Teoritis"
        }
      },
      "generator": {
        "filterInterviewOnly": "Wawancara Saja",
        "filterAll": "Semua",
        "noQuestionsMatch": "Tidak ada pertanyaan yang cocok dengan filter yang dipilih.",
        "selectResearchQuestion": "Pilih pertanyaan penelitian untuk digunakan dalam wawancara Anda"
      }
    },
    "surveyChat": {
      "error": "Maaf, terjadi kesalahan. Silakan coba lagi."
    },
    "modelDash": {
      "archiveKnowledgeBaseQuestion": "Arsipkan Basis Pengetahuan?",
      "archiveKBConfirmText": "Apakah Anda yakin ingin mengarsipkan basis pengetahuan ini? Ini tidak dapat dibatalkan.",
      "archiveKBNote": "Ini akan tetap dihitung dalam jumlah total basis pengetahuan yang telah Anda buat, tetapi akan dihapus dari daftar aktif.",
      "archiveKnowledgeBaseTooltip": "Arsipkan Basis Pengetahuan",
      "linkCopied": "Tautan dibagikan disalin ke clipboard"
    },
    "linkedInPage": {
      "messages": {
        "pushError": "Gagal mengirim ke Basis Pengetahuan",
        "pushSuccess": "Berhasil mengirim {{count}} pos ke Basis Pengetahuan"
      }
    },
    "metaAdLibrary": {
      "noPages": "Tidak ada halaman ditemukan",
      "title": "Perpustakaan Iklan Meta",
      "upgradeMessage": "Cari dan analisis kampanye iklan Facebook & Instagram. Tersedia di paket Growth dan Enterprise.",
      "subtitle": "Cari dan analisis kampanye iklan Facebook & Instagram",
      "searchByPage": "Berdasarkan Halaman/Merek",
      "searchByKeyword": "Berdasarkan Kata Kunci",
      "keywordPlaceholder": "Cari iklan berdasarkan kata kunci (mis. sepatu lari)",
      "pagePlaceholder": "Masukkan nama halaman atau merek (mis. Nike)",
      "search": "Cari",
      "creditsPerSearch": "kredit per pencarian",
      "creditsPerAdsFetch": "kredit per pengambilan iklan",
      "pagesFound": "Halaman Ditemukan",
      "ads": "iklan",
      "backToResults": "Kembali",
      "pushToKB": "Kirim ke KB",
      "allMedia": "Semua Media",
      "activeOnly": "Hanya Aktif",
      "inactiveOnly": "Hanya Tidak Aktif",
      "allStatus": "Semua Status",
      "deselectAll": "Batalkan Pilihan Semua",
      "selectAll": "Pilih Semua",
      "loadMore": "Muat Lebih Banyak",
      "pushTitle": "Kirim ke Basis Pengetahuan",
      "pushGeneratingDetail": "Memformat {{count}} iklan dengan gambar. Ini mungkin memerlukan waktu.",
      "pushDescription": "Ekspor {{count}} iklan yang dipilih sebagai dokumen PDF ke dalam Basis Pengetahuan Anda.",
      "pushButton": "Dorong ke Basis Pengetahuan",
      "pushGenerating": "Menghasilkan dokumen PDF...",
      "selectKB": "Pilih Basis Pengetahuan..."
    },
    "noteComPage": {
      "search": {
        "errorEmpty": "Silakan masukkan kata kunci pencarian"
      },
      "creator": {
        "errorEmpty": "Silakan masukkan nama pengguna pembuat"
      },
      "hashtag": {
        "errorEmpty": "Silakan masukkan hashtag"
      }
    },
    "organization": {
      "members": {
        "confirmRemoveTitle": "Hapus Anggota?",
        "remove": "Hapus"
      }
    },
    "personas": {
      "wizard": {
        "generating": {
          "segmentsStage1": "Membaca Basis Pengetahuan Anda...",
          "segmentsStage2": "Mengukur segmen pelanggan dalam data Anda..."
        },
        "framework": {
          "cardTitle": "Kerangka teoretis",
          "cardHint": "Lensa untuk memprofilkan persona Anda — setiap persona dinilai berdasarkan konstruknya.",
          "statusLocked": "Terkunci",
          "statusInherited": "Dari set pertanyaan riset Anda",
          "statusManual": "Pilihan Anda",
          "statusAuto": "Dipilih otomatis untuk pertanyaan Anda",
          "anchoredIn": "Berlandaskan pada",
          "changeShort": "Ubah",
          "constructs": "konstruksi",
          "crowdedWarning": "radar mungkin terasa ramai"
        },
        "segments": {
          "title": "Segmen korpus",
          "hint": "Temukan segmen pelanggan alami di Basis Pengetahuan Anda — satu persona per segmen, dibobot berdasarkan porsi terukurnya dalam korpus.",
          "discover": "Temukan segmen",
          "discovering": "Menganalisis korpus…",
          "noCorpus": "Konten dokumen di Basis Pengetahuan ini tidak cukup untuk segmentasi yang andal.",
          "discoverFailed": "Penemuan segmen gagal. Anda tetap dapat membuat persona seperti biasa.",
          "useSegments": "Tetapkan jumlah dari data saya — satu persona per segmen yang ditemukan (disarankan)",
          "countLocked": "Ditentukan oleh segmen",
          "basis": "Diukur dari {{docs}} dokumen ({{chunks}} kutipan)",
          "rediscover": "Temukan ulang",
          "autoHint": "Data Anda yang menentukan: satu persona per segmen pelanggan yang ditemukan di Basis Pengetahuan, dibobot berdasarkan porsi terukurnya.",
          "autoChip": "Otomatis",
          "autoOption": "Otomatis — satu persona per segmen dalam data saya (disarankan)",
          "manualOption": "Manual — saya memilih jumlahnya",
          "postSaveNote": "Setelah disimpan, Yatabase secara otomatis mengkalibrasi panel ini terhadap Basis Pengetahuan Anda, mengujinya secara buta terhadap pernyataan pelanggan asli, dan memperhalus suaranya hingga model juri tidak lagi dapat membedakannya. Saksikan langsung di halaman koleksi."
        },
        "step1": {
          "setAttribute": "Atribut",
          "filterPersonaOnly": "Hanya Persona",
          "filterAll": "Semua",
          "noQuestionsMatch": "Tidak ada pertanyaan yang cocok dengan filter yang dipilih.",
          "hoverCategoryPrompt": "Arahkan mouse ke kategori untuk melihat pertanyaan",
          "selectResearchQuestion": "Pilih pertanyaan penelitian"
        },
        "messages": {
          "personaGeneratedSuccessfully": "Persona baru berhasil dihasilkan!",
          "errorGeneratingPersona": "Kesalahan saat menghasilkan persona. Silakan coba lagi.",
          "derivedEmpty": "Tidak dapat menghasilkan atribut konteks & suara. Buka editor dan klik Regenerate.",
          "waitForExtras": "Tunggu atribut yang dihasilkan AI selesai dihasilkan",
          "regenerateDerivedFailed": "Tidak dapat menghasilkan kembali atribut turunan",
          "regenerateExtrasFailed": "Tidak dapat menghasilkan kembali tujuan & titik sakit"
        },
        "buttons": {
          "generating": "Menghasilkan...",
          "addPersona": "Tambahkan Persona",
          "addBlank": "Tambahkan Persona Kosong",
          "aiGenerate": "AI Hasilkan Persona"
        },
        "gen": {
          "substrate": "Substrat",
          "substrateDesc": "Demografi + konstruksi kerangka, dikalibrasi",
          "images": "Potret",
          "imagesDesc": "Foto wajah yang disesuaikan dengan gender, usia, dan lokasi setiap persona",
          "extras": "Tujuan & titik sakit",
          "extrasDesc": "Apa yang diinginkan setiap persona — dan apa yang menyakitkan — terikat pada profil konstruksi mereka",
          "derived": "Konteks & suara",
          "derivedDesc": "Hari dalam kehidupan, pekerjaan JTBD, keberatan, nilai, sampel suara, dan tahap perjalanan — dihasilkan dalam satu proses yang koheren",
          "composing": "Menyusun persona Anda",
          "composingBlurb": "Setiap langkah adalah panggilan AI terpisah sehingga setiap detail kembali ke teori yang divalidasi — tidak diciptakan dalam isolasi.",
          "complete": "lengkap",
          "now": "sekarang",
          "footnote": "Anda sudah dapat mengedit persona mana pun sementara yang lainnya dihasilkan. Simpan dibuka setelah semua langkah selesai.",
          "cardDescPending": "Menyusun deskripsi persona ini dari profil konstruksi mereka…",
          "cardDescEmpty": "Belum ada deskripsi — klik Regenerate di editor untuk menghasilkan satu."
        },
        "step3": {
          "composingDescription": "Menyusun deskripsi dari profil konstruksi…"
        },
        "navigation": {
          "waitingForFramework": "Menunggu kerangka…",
          "updatingCollection": "Memperbarui...",
          "savingCollection": "Menyimpan..."
        }
      },
      "library": {
        "badges": {
          "shared": "DIBAGIKAN"
        },
        "labels": {
          "more": "lebih",
          "theoreticalLens": "Lensa Teoretis"
        }
      },
      "health": {
        "savedTitle": "Koleksi disimpan",
        "gapsTitle": "Satu hal yang perlu dilihat — {{n}} topik yang dibicarakan pelanggan Anda belum memiliki suara di panel ini:",
        "allCovered": "Semua topik terukur kini memiliki suara — cakupan {{cov}}%.",
        "progress": "{{added}} persona telah ditambahkan — cakupan kini {{cov}}%.",
        "done": "Selesai — lihat koleksi"
      },
      "realism": {
        "pending": "Pemeriksaan realisme dalam antrean — juri AI buta akan menguji panel ini terhadap pernyataan pelanggan asli dari Basis Pengetahuan Anda begitu konteks & suara selesai dibuat.",
        "tuning": "Memverifikasi realisme — juri AI buta membandingkan pernyataan persona Anda dengan pernyataan pelanggan asli dari Basis Pengetahuan, dan menyesuaikan suaranya jika masih dapat dibedakan…",
        "passTitle": "Juri buta tidak dapat membedakannya.",
        "added": "Menambahkan {{n}} persona untuk mencakup topik yang dibicarakan pelanggan Anda: {{topics}}.",
        "designNote": "Panel dirancang untuk mencakup semua {{total}} topik yang terukur dalam data Anda.",
        "designNotePartial": "Panel dirancang untuk mencakup {{assigned}} dari {{total}} topik terukur.",
        "coverageMet": "Perhatian topik terukur: {{cov}}% — setara dengan tolok ukur terpublikasi (75%+).",
        "coverageBelow": "Perhatian topik terukur: {{cov}}% — ini mensimulasikan apa yang benar-benar akan ditekankan persona dalam percakapan, cenderung konservatif, dan bervariasi ±10 poin antar-pengukuran.",
        "missingLabel": "Belum ada suaranya:",
        "missingNext": "Setelah disimpan, halaman koleksi menawarkan perbaikan sekali klik — tambahkan satu persona per topik, perbarui Basis Pengetahuan, atau biarkan saja.",
        "passBody": "Pernyataan panel ini secara statistik tidak dapat dibedakan dari pernyataan pelanggan asli Anda (akurasi juri {{acc}}%, 50% = murni kebetulan).",
        "adjusted": "Suara disesuaikan otomatis {{n}}× untuk mencapainya.",
        "twoDraws": "Terverifikasi melalui 2 pengambilan independen ({{n}} pernyataan).",
        "partial": "Juri buta masih dapat membedakan pernyataan persona (akurasi {{acc}}%) setelah {{n}} penyesuaian suara. Ini biasanya berarti Basis Pengetahuan berisi konten campuran, bukan pernyataan pelanggan asli — panel tetap berlandaskan data Anda.",
        "unavailable": "Pemeriksaan realisme tidak tersedia saat ini — persona Anda tetap siap disimpan.",
        "stale": "Panel berubah setelah verifikasi — vonis uji buta tidak lagi berlaku dan tidak akan disimpan.",
        "reverify": "Verifikasi ulang panel",
        "chip": "Terverifikasi juri buta",
        "chipTip": "Juri AI buta tidak dapat membedakan secara andal pernyataan panel ini dari pernyataan pelanggan asli di Basis Pengetahuan Anda (akurasi {{acc}}% dari {{n}} pernyataan, 50% = murni kebetulan)."
      },
      "calibration": {
        "bestFitDesigned": "Panel ini adalah kecocokan terbaik dengan suara pelanggan di Basis Pengetahuan Anda: segmen dan bobot diukur darinya, dan setiap topik ditetapkan sejak perancangan. Perbedaan yang tercantum di atas mencerminkan korpus itu sendiri, bukan kesalahan pembuatan.",
        "bestFitManual": "Perbedaan yang tercantum di atas diukur terhadap suara pelanggan di Basis Pengetahuan yang tertaut.",
        "bestFitKbCta": "Menambahkan lebih banyak suara pelanggan nyata (ulasan, wawancara, tiket) ke Basis Pengetahuan adalah cara paling andal untuk mempertajam panel berikutnya.",
        "unchanged": "Tidak ada perubahan sejak kalibrasi terakhir — menampilkan pengukuran yang ada. Edit persona atau perbarui Basis Pengetahuan untuk mengukur ulang.",
        "tierGenerated": "Dihasilkan",
        "tierGrounded": "Terhubung",
        "tierCalibrated": "Terkalibrasi",
        "tierGeneratedTip": "Dihasilkan tanpa data pelanggan — tautkan Basis Pengetahuan untuk menghubungkannya",
        "tierGroundedTip": "Dihasilkan menggunakan data pelanggan Anda sebagai konteks — jalankan laporan kalibrasi untuk mengukur kesesuaian",
        "tierCalibratedTip": "Diukur terhadap korpus suara pelanggan nyata dalam Basis Pengetahuan yang terhubung",
        "panelTitle": "Kalibrasi",
        "coverage": "Cakupan topik",
        "sentimentDelta": "Kesenjangan sentimen",
        "corpusMeta": "{{chunks}} kutipan dari {{docs}} dokumen",
        "showDetails": "Tampilkan detail",
        "hideDetails": "Sembunyikan detail",
        "notYetRun": "Ukur panel ini terhadap suara pelanggan nyata dalam Basis Pengetahuan-nya.",
        "run": "Jalankan laporan kalibrasi",
        "rerun": "Jalankan ulang kalibrasi",
        "running": "Mengukur…",
        "realSentiment": "Pelanggan nyata",
        "panelSentiment": "Panel persona",
        "missing": "Hilang dari panel",
        "underweighted": "Terbobot rendah",
        "overweighted": "Terbobot tinggi",
        "noCorpus": "Basis Pengetahuan yang terhubung memiliki terlalu sedikit konten yang diindeks untuk dikalibrasi.",
        "failed": "Laporan kalibrasi gagal. Silakan coba lagi.",
        "addGapPersona": "+ Tambah persona",
        "gapRunning": "Menambahkan…",
        "gapAdded": "Persona \"{{name}}\" ditambahkan — mengukur ulang panel…",
        "history": "Cakupan selama pengukuran",
        "autoRunning": "Kalibrasi otomatis — mengukur panel ini terhadap Basis Pengetahuan Anda…",
        "suggestTitle": "{{n}} topik tidak memiliki suara di panel ini:",
        "suggestAdd": "Tambahkan {{n}} persona untuk mencakupnya",
        "suggestAdding": "Menambahkan & mengukur ulang…",
        "suggestKb": "Atau tambahkan dokumen tentang topik ini ke",
        "suggestKbLink": "Basis Pengetahuan",
        "suggestKbTail": "Anda — perbaikan hulu yang meningkatkan setiap panel berikutnya.",
        "suggestDismiss": "Biarkan saja — itu juga tidak masalah",
        "ofAttention": "perhatian pelanggan",
        "coverageTip": "Porsi topik yang dibicarakan pelanggan asli (diukur di Basis Pengetahuan) yang dicakup oleh setidaknya satu persona dalam panel ini.",
        "sentimentDeltaTip": "Selisih terbesar antara tingkat positif/negatif pelanggan asli dan panel persona. Semakin rendah semakin baik."
      },
      "validation": {
        "pill": "Uji buta",
        "pillTip": "Akurasi juri buta dalam membedakan pernyataan asli dari sintetis: {{acc}}% dari {{n}} pernyataan (p = {{p}} terhadap ambang kebetulan 50%). Juri: {{model}} — {{caveat}}.",
        "atChance": "setara kebetulan ({{acc}}%)",
        "aboveChance": "dapat dibedakan ({{acc}}%)",
        "holdout": "Cakupan holdout",
        "run": "Jalankan uji buta",
        "rerun": "Ulangi uji buta",
        "running": "Menguji…",
        "buttonTip": "Uji buta: dapatkah model juri membedakan pernyataan persona Anda dari suara pelanggan asli?",
        "noCorpus": "Suara pelanggan asli di Basis Pengetahuan tidak cukup untuk menjalankan uji buta.",
        "failed": "Uji validasi gagal. Silakan coba lagi.",
        "detailTitle": "Validasi buta",
        "detailAtChance": "Model juri buta tidak dapat membedakan secara andal pernyataan panel ini dari suara pelanggan asli — akurasi {{acc}}% dari {{n}} pernyataan secara statistik tidak dapat dibedakan dari ambang kebetulan 50% (p = {{p}}).",
        "detailAboveChance": "Model juri buta membedakan pernyataan panel ini dari suara pelanggan asli dengan akurasi {{acc}}% dari {{n}} pernyataan (p = {{p}}) — suara panel masih berbeda secara terukur dari pelanggan asli.",
        "detailHoldout": "Pemeriksaan di luar sampel: panel mencakup {{cov}}% topik dalam {{docs}} dokumen holdout yang tidak pernah menjadi dasarnya.",
        "detailCaveat": "Catatan: juri dan generator berasal dari keluarga model yang sama ({{model}}).",
        "refine": "Perhalus suara",
        "refining": "Memperhalus…",
        "refineTip": "Merevisi suara setiap persona menuju gaya pelanggan asli, menguji buta ulang, mengulang (maks 3 putaran).",
        "refineDone": "Suara diperhalus — juri tidak lagi dapat membedakan panel dari pelanggan asli secara andal.",
        "refineStopped": "Diperhalus selama {{rounds}} putaran — panel semakin dekat, tetapi masih dapat dibedakan. Anda dapat menjalankannya lagi.",
        "refineHistory": "Penghalusan suara:",
        "refineRounds": "{{n}} putaran, pengambilan pernyataan baru setiap putaran",
        "autoRunning": "Uji buta sedang berjalan…",
        "pooledPill": "Ketakterbedaan",
        "pooledTip": "Digabung dari {{runs}} uji buta independen ({{n}} pernyataan): akurasi juri {{acc}}% terhadap ambang kebetulan 50% (p = {{p}}). 100 = persis lempar koin.",
        "pooledDetailTitle": "Skor gabungan",
        "pooledDetail": "Dari {{runs}} uji buta independen ({{n}} pernyataan, pengambilan baru setiap kali), rata-rata juri {{acc}}% — ketakterbedaan {{score}}/100 (p = {{p}}).",
        "stepSegments": "Segmen",
        "stepCalibration": "Kalibrasi",
        "stepBlindTest": "Uji buta",
        "stepRefine": "Penghalusan suara",
        "stepRefined": "Suara diperhalus ({{n}}×)",
        "stepNotNeeded": "Tidak diperlukan",
        "stepPooled": "Terverifikasi gabungan",
        "holdoutTip": "Pemeriksaan di luar sampel: cakupan topik diukur hanya terhadap dokumen holdout ({{docs}}) yang tidak pernah menjadi dasar panel — bukti generalisasi, bukan sekadar meniru.",
        "pooledTipOne": "Sejauh ini baru satu uji buta ({{n}} pernyataan): akurasi juri {{acc}}% terhadap ambang kebetulan 50% (p = {{p}}). 100 = persis lempar koin. Jalankan ulang uji buta untuk menambah pengambilan independen dan memantapkan skor ini.",
        "pooledDetailOne": "Baru satu uji buta ({{n}} pernyataan): akurasi juri {{acc}}% — ketakterbedaan {{score}}/100 (p = {{p}}). Jalankan ulang untuk menambah pengambilan independen; skor semakin mantap seiring bertambahnya uji."
      },
      "optimize": {
        "button": "Perbaikan otomatis",
        "buttonTip": "Menjalankan semuanya untuk Anda: mengukur panel, mengisi kekosongan topik, menguji buta, dan memperhalus suara — hingga 3 putaran — menuju semua metrik hijau.",
        "running": "Memperbaiki… putaran {{n}}",
        "doneNote": "Perbaikan otomatis selesai — semua metrik mencapai target.",
        "partialNote": "Perbaikan otomatis sudah mencapai batas kemampuan korpus ini: {{unmet}} belum mencapai target. Basis Pengetahuan yang kecil atau campuran membatasi metrik ini — suara pelanggan asli yang lebih kaya menaikkan batasnya.",
        "errorNote": "Perbaikan otomatis mengalami kesalahan — Anda dapat mencoba lagi.",
        "startFailed": "Tidak dapat memulai perbaikan otomatis. Silakan coba lagi.",
        "stop": "Hentikan",
        "stopping": "Menghentikan…",
        "stoppedNote": "Perbaikan otomatis dihentikan. Metrik mencerminkan kemajuan sejauh ini — jalankan lagi kapan saja.",
        "greenTitle": "Semua hijau — apa artinya",
        "greenCoverage": "Persona Anda membicarakan hal yang sama dengan pelanggan asli Anda — tidak ada topik utama dalam data yang tak terwakili.",
        "greenSentiment": "Mereka merasakan apa yang dirasakan pelanggan Anda — keseimbangan positif/negatif panel cocok dengan data asli.",
        "greenBlind": "Mereka terdengar seperti pelanggan Anda — juri AI yang diberi campuran pernyataan asli dan persona tidak dapat membedakannya secara andal.",
        "greenSummary": "Riset yang Anda jalankan pada panel ini mencerminkan pelanggan Anda yang sebenarnya — topik, suasana hati, dan suara mereka — bukan tebakan AI generik.",
        "partialTitle": "Perbaikan otomatis sudah mencapai batas kemampuan data ini",
        "whyCoverage": "Cakupan topik ({{value}}% dari {{target}}%): pelanggan Anda membicarakan lebih banyak topik daripada yang dapat diwakili panel ini. Biasanya konten Basis Pengetahuan sangat beragam, atau panel sudah mencapai batas 10 persona. Menambahkan dokumen tentang topik yang kurang (tercantum di detail) — atau memecah menjadi dua panel terfokus — akan menaikkannya.",
        "whySentiment": "Kesenjangan sentimen ({{value}}% vs ≤{{target}}%): suasana hati panel secara keseluruhan masih berbeda dari pelanggan Anda. Ini biasanya membaik seiring tertutupnya kekosongan topik, karena topik yang hilang sering membawa emosi yang hilang.",
        "whyBlind": "Uji buta ({{value}}/100): juri masih dapat mengenali pernyataan persona. Penyebab paling umum adalah Basis Pengetahuan berisi konten campuran (unggahan hasil scraping, materi pemasaran, artikel) alih-alih pernyataan pelanggan asli — tidak ada satu \"suara pelanggan\" untuk ditiru. Dengan ulasan, umpan balik, atau transkrip asli, uji ini biasanya lulus.",
        "partialSummary": "Metrik yang SUDAH hijau adalah hasil pengukuran nyata — dan menjalankan Perbaikan otomatis lagi setelah memperkaya Basis Pengetahuan dapat mengangkat sisanya."
      }
    },
    "common": {
      "unknown": "Tidak Diketahui",
      "adding": "Menambahkan..."
    },
    "personaChat": {
      "responseStyleNote": "Sifat persona selalu lebih diutamakan daripada pengaturan ini.",
      "focusGroup": {
        "textRegenFailed": "Atribut disimpan tetapi regenerasi teks gagal"
      }
    },
    "projects": {
      "teamAssigned": "Tim berhasil ditugaskan ke proyek",
      "addTeamToProject": "Tambahkan Tim ke Proyek",
      "totalProjects": "Total proyek",
      "deleteProject": "Hapus",
      "deleteProjectTitle": "Hapus Proyek?"
    },
    "library": {
      "contextBasedResearch": "Penelitian berbasis konteks",
      "researchQuestion": "Pertanyaan Penelitian",
      "suggestedQuestions": "Pertanyaan Penelitian",
      "more": "lebih",
      "theoreticalLens": "Lensa Teoretis",
      "archiveFastResearch": "Arsip Penelitian Cepat",
      "restoreQuickStart": "Pulihkan",
      "status": {
        "processingInterview": "Menghasilkan respons wawancara",
        "extractingThemes": "Menganalisis tema wawancara"
      }
    },
    "quickstart": {
      "modal": {
        "step1": {
          "contextRequired": "Silakan berikan setidaknya satu sumber konteks: URL situs web, basis pengetahuan, atau konteks tambahan.",
          "projectRequired": "Silakan pilih proyek",
          "researchFirst": "Silakan selesaikan penelitian web terlebih dahulu dengan mengklik tombol pencarian di samping URL.",
          "project": "Proyek",
          "selectProject": "Pilih proyek",
          "insufficientQuota": "Kuota Tidak Cukup",
          "noQuotaRemaining": "Anda tidak memiliki respons sintetis yang tersisa. Silakan tingkatkan rencana Anda untuk menggunakan Penelitian Cepat.",
          "lowQuotaRemaining": "Anda hanya memiliki {{count}} respons sintetis yang tersisa. Minimum 10 diperlukan untuk analisis yang bermakna.",
          "upgradePlan": "Tingkatkan Rencana"
        },
        "step2": {
          "questionRequired": "Silakan pilih atau masukkan pertanyaan penelitian",
          "contextSummary": "Ringkasan Konteks",
          "uxAnalysis": "Analisis UX & Viewport",
          "loadingRQSets": "Memuat...",
          "noRQSets": "Tidak ada set pertanyaan penelitian yang disimpan",
          "questions": "pertanyaan"
        }
      },
      "results": {
        "fastResearch": "Penelitian Cepat",
        "contextBased": "Penelitian berbasis konteks",
        "previewReportDesc": "Ikhtisar ringkasan penelitian Anda",
        "theoreticalLens": "Lensa Teoretis — mengaitkan setiap artefak dalam Penelitian Cepat ini",
        "constructs": "konstruksi",
        "whatIsFramework": "Apa itu {{name}}?",
        "constructGlossary": "Glosarium Konstruksi"
      },
      "tabs": {
        "convergence": "Konvergensi"
      },
      "convergence": {
        "confirmed": "Dikonfirmasi",
        "nuanced": "Nuansa",
        "ruledOut": "Dikesampingkan",
        "tensionFlagged": "Ketegangan ditandai",
        "singleSource": "Sumber tunggal",
        "loading": "Memuat laporan konvergensi…",
        "unavailableTitle": "Laporan konvergensi tidak tersedia untuk sesi ini",
        "unavailableIntro": "Penelitian Cepat ini dibuat sebelum dasar teori diaktifkan. Persona dan wawancara dihasilkan tanpa kerangka teoretis yang sama, sehingga tidak ada bukti tingkat konstruksi untuk triangulasi.",
        "unavailableAction": "Mulai Penelitian Cepat baru untuk mendapatkan laporan konvergensi lengkap — kerangka dipilih secara otomatis dan mengalir melalui setiap artefak yang dihasilkannya.",
        "startNew": "Mulai Penelitian Cepat baru",
        "title": "Laporan Konvergensi Metodologis",
        "intro": "Triangulasi metodologis di seluruh tiga generasi independen dalam Penelitian Cepat ini — profil persona, wawancara kualitatif, dan respons survei kuantitatif — semuanya berakar pada kerangka teoretis yang sama. Konstruksi di mana semua metode berkonvergensi membentuk temuan dengan kepercayaan tertinggi Anda. Konstruksi di mana mereka menyimpang bukanlah kesalahan — mereka adalah temuan yang paling menarik, muncul di mana asumsi yang disimulasikan, bukti naratif, dan sinyal yang diukur menarik ke arah yang berbeda.",
        "framework": "Berakar pada",
        "generating": "Menghasilkan…",
        "generate": "Hasilkan Laporan Konvergensi",
        "regenerating": "Menghasilkan kembali…",
        "regenerate": "Hasilkan kembali",
        "headerConfirmed": "konstruksi yang dikonfirmasi di semua metode yang tersedia.",
        "synthesis": "Sintesis Lintas-Konstruksi",
        "personaSide": "Bukti persona",
        "corePersonas": "persona dengan kepentingan inti",
        "avgImportance": "Pentingnya rata-rata",
        "personaNotScored": "Persona ini tidak dinilai pada konstruksi ini, sehingga tidak ada perbandingan sisi persona.",
        "interviewSide": "Bukti wawancara",
        "evidenceStrength": "Kekuatan bukti",
        "convergentQuote": "Kutipan konvergen",
        "divergentQuote": "Kutipan divergen",
        "noInterviewData": "Analisis wawancara belum diselesaikan untuk konstruksi ini.",
        "surveySide": "Bukti survei",
        "meanAgreement": "Kesepakatan rata-rata",
        "highAgreement": "setuju kuat",
        "surveyItem": "item",
        "surveyItems": "item-item",
        "surveyResponses": "respon",
        "themeValidationTitle": "Validasi Tema Emergen",
        "themeValidationIntro": "Wawancara mengungkapkan tema di luar kerangka. Survei kemudian menguji apakah tema-tema ini bertahan pada skala — desain metode campuran eksploratori berurutan dalam praktik.",
        "themeValidated": "DIVALIDASI",
        "themePartial": "SEBAGIAN",
        "themeNotValidated": "TIDAK DIVALIDASI",
        "themeNoSurvey": "HANYA KUALITATIF",
        "themeFromInterview": "Dari wawancara",
        "themeFromSurvey": "Dari survei",
        "footer": "Dihasilkan oleh pipeline konvergensi Yatabase menggunakan desain metode campuran eksploratori berurutan (Creswell & Plano Clark): agregasi bukti per-konstruk di seluruh profil persona, wawancara, dan respon survei, terikat pada"
      },
      "status": {
        "retry": "Coba lagi"
      }
    },
    "researchQuestions": {
      "errors": {
        "loadFailed": "Gagal memuat set pertanyaan penelitian.",
        "saveFailed": "Pertanyaan dihasilkan tetapi gagal disimpan ke perpustakaan.",
        "generateFailed": "Terjadi kesalahan. Silakan coba lagi."
      }
    },
    "rqLibrary": {
      "loadingMore": "Memuat...",
      "moreCategories": "lebih banyak kategori"
    },
    "syntheticData": {
      "contextFromSurvey": "Konteks diisi otomatis dari pengaturan YataForm Anda",
      "webResearchPlaceholder": "URL atau topik pencarian...",
      "webResearchHint": "Opsional: Tambahkan konteks web untuk respon yang lebih realistis",
      "additionalContext": "Konteks Tambahan (Opsional)",
      "additionalContextPlaceholder": "Konteks bisnis, detail audiens target, instruksi spesifik untuk menghasilkan respon...",
      "additionalContextHint": "Berikan konteks atau instruksi untuk memandu penghasilan respon sintetis"
    },
    "teams": {
      "memberCount_label": "anggota",
      "projectCount_label": "proyek",
      "buttons": {
        "editName": "Edit Nama",
        "addMember": "Tambah Anggota",
        "deleteTeam": "Hapus",
        "removeMember": "Hapus",
        "leaveTeam": "Tinggalkan"
      },
      "modals": {
        "editName": {
          "title": "Edit Nama Tim",
          "placeholder": "Nama tim"
        }
      },
      "confirmations": {
        "removeMemberTitle": "Hapus Anggota?",
        "leaveTeamTitle": "Tinggalkan Tim?",
        "deleteTeamTitle": "Hapus Tim?"
      }
    },
    "theoryBridge": {
      "library": {
        "details": {
          "narrative": "Narasi"
        },
        "tooltips": {
          "archiveAnalysis": "Arsip",
          "totalAnalyses": "Total jumlah analisis",
          "totalMappings": "Total teori yang dipetakan di seluruh analisis",
          "totalGaps": "Total kesenjangan yang diidentifikasi di seluruh analisis"
        },
        "labels": {
          "analyses": "Analisis",
          "mappings": "Pemetaan",
          "gaps": "Kesenjangan"
        },
        "searchPlaceholder": "Cari analisis...",
        "loading": "Memuat analisis...",
        "noResults": "Tidak ada analisis yang cocok",
        "noResultsDesc": "Coba istilah pencarian yang berbeda",
        "archiveAnalysis": "Arsip Analisis?",
        "confirmArchive": "Ini akan mengarsipkan analisis Theory Bridge dan menghapusnya dari daftar aktif Anda. Ini tidak dapat dibatalkan.",
        "cancel": "Batal",
        "yesArchive": "Ya, Arsip"
      }
    },
    "urlInput": {
      "step0": {
        "filterYataformOnly": "Hanya YataForm",
        "filterAll": "Semua",
        "noQuestionsMatch": "Tidak ada pertanyaan yang cocok dengan filter yang dipilih.",
        "hoverCategoryPrompt": "Arahkan mouse ke kategori untuk melihat pertanyaan",
        "selectQuestionPrompt": "Pilih pertanyaan penelitian"
      },
      "wizardTitle": "Buat YataForm"
    },
    "contextSources": {
      "preFilledFromSet": "Konteks diisi otomatis dari set pertanyaan penelitian Anda"
    },
    "yataFormLibrary": {
      "status": {
        "active": "Aktif",
        "closed": "Tutup",
        "draft": "Draf"
      },
      "types": {
        "aiYataForm": "AI YataForm",
        "documentAnalysis": "Analisis Dokumen",
        "excelAnalysis": "Analisis Excel",
        "webhookKB": "Webhook KB",
        "customForm": "Form Kustom"
      },
      "loading": "Memuat YataForms...",
      "title": "Perpustakaan YataForm",
      "subtitle": "Buat dan kelola formulir dan survei bertenaga AI untuk pengumpulan dan analisis data",
      "tooltips": {
        "totalActive": "Jumlah total YataForms aktif di perpustakaan Anda",
        "unassigned": "YataForms yang tidak ditugaskan ke proyek manapun",
        "open": "YataForms yang saat ini menerima respons",
        "closed": "YataForms yang tidak lagi menerima respons",
        "responses": "YataForms yang telah mengumpulkan setidaknya satu respons",
        "viewDashboard": "Lihat Dasbor",
        "editName": "Edit Nama"
      },
      "labels": {
        "active": "Aktif",
        "unassigned": "Belum Ditugaskan",
        "inProject": "Dalam Proyek",
        "open": "Buka",
        "closed": "Tutup",
        "responses": "Respons",
        "untitled": "Tanpa Judul",
        "shared": "Dibagikan"
      },
      "searchPlaceholder": "Cari YataForms...",
      "filters": {
        "allProjects": "Semua Proyek",
        "unassigned": "Belum Ditugaskan"
      },
      "buttons": {
        "newYataForm": "YataForm Baru",
        "createYataForm": "Buat YataForm",
        "saveChanges": "Simpan Perubahan",
        "cancel": "Batal",
        "assigning": "Menugaskan...",
        "assignToProject": "Tugaskan ke Proyek"
      },
      "menu": {
        "aiYataForm": "AI YataForm",
        "aiYataFormDesc": "Buat formulir cerdas dengan AI",
        "curatedTemplates": "Template Terkurasi (Enterprise)",
        "curatedTemplatesDesc": "Template yang dirancang oleh ahli untuk tim enterprise"
      },
      "emptyState": {
        "title": "Tidak ada YataForms ditemukan",
        "adjustFilters": "Coba sesuaikan pencarian atau kriteria filter Anda",
        "createFirst": "Buat YataForm pertama Anda untuk mulai mengumpulkan dan menganalisis data"
      },
      "modals": {
        "editName": {
          "title": "Edit Nama YataForm"
        },
        "projectAssign": {
          "changeTitle": "Ubah Penugasan Proyek YataForm",
          "assignTitle": "Tugaskan YataForm ke Proyek",
          "yataForm": "YataForm",
          "selectProject": "Pilih Proyek",
          "chooseProject": "Pilih sebuah proyek...",
          "currentProject": "Proyek Saat Ini"
        }
      },
      "details": {
        "responses": "respons",
        "created": "Dibuat",
        "accessCode": "Kode Akses",
        "questions": "Pertanyaan"
      },
      "descriptions": {
        "aiGenerated": "YataForm yang dihasilkan AI dengan analisis dan wawasan cerdas",
        "documentAnalysis": "Survei analisis dokumen untuk pemrosesan data tidak terstruktur",
        "excelAnalysis": "Analisis data Excel dengan wawasan dan pola bertenaga AI",
        "customSurvey": "Survei kustom yang menunggu pengumpulan dan analisis data"
      }
    }
  }
};

export default uiLabelsAugment;
